/**
 * Mojito Returns & Exchange Portal
 * Phase 1: Order Lookup Screen
 * Phase 2: Chat Interface
 * Phase 4: Wired to Backend (real API calls, SSE streaming, returns creation)
 */
(function () {
  'use strict';

  /* ========== i18n ========== */

  var STRINGS = {
    en: {
      title: 'Returns & Exchanges',
      subtitle: 'Look up your order to get started.',
      identifierLabel: 'Find your order by',
      orderNumber: 'Order #',
      trackingNumber: 'Tracking #',
      verifierLabel: 'Verify your identity with',
      email: 'Email',
      zipCode: 'Zip Code',
      orderPlaceholder: 'e.g. 1001',
      trackingPlaceholder: 'e.g. 9400111899223',
      emailPlaceholder: 'e.g. name@email.com',
      zipPlaceholder: 'e.g. 90210',
      submit: 'Find My Order',
      submitting: 'Looking up...',
      policyLink: 'View our return policy',
      errorEmpty: 'Please fill in both fields.',
      errorNotFound: "We couldn't find that order. Please check your details and try again.",
      errorNetwork: 'Something went wrong. Please try again.',
      errorTimeout: 'Request timed out. Please try again.',
      errorRateLimit: 'Too many requests. Please wait a moment and try again.',
      // Chat
      concierge: 'Mojito Concierge',
      back: 'Back',
      orderLabel: 'Order',
      dateLabel: 'Date',
      totalLabel: 'Total',
      statusLabel: 'Status',
      statusDelivered: 'Delivered',
      statusInTransit: 'In Transit',
      statusProcessing: 'Processing',
      exchangeAction: 'Exchange',
      exchangeDesc: 'Swap for a different size or color',
      returnAction: 'Return',
      returnDesc: 'Get a refund or store credit',
      questionAction: 'Question',
      questionDesc: 'Ask about your order',
      selectItemFirst: 'Please select an item above to get started.',
      selectEligibleItem: 'The selected items are not eligible for return or exchange. Please contact customer.service@mojitocollection.com for assistance.',
      chatPlaceholder: 'Type a message...',
      send: 'Send',
      wrongSize: 'Wrong size',
      differentColor: 'Different color',
      differentStyle: 'Different style',
      otherReason: 'Something else',
      doesntFit: "Doesn't fit",
      looksDifferent: 'Looks different in person',
      damagedDefective: 'Damaged or defective',
      changedMind: 'Changed my mind',
      tooSmall: 'Too small \u2014 need a bigger size',
      tooLarge: 'Too big \u2014 need a smaller size',
      fitShape: 'Right size, but the fit is off',
      wrongItem: 'Wrong item sent',
      sizingFit: 'Sizing & fit',
      careInstructions: 'Care instructions',
      shippingTimelines: 'Shipping & timelines',
      returnPolicyTopic: 'Return policy',
      outsideWindow: 'This order is outside our 30-day return window.',
      finalSale: 'Final Sale',
      finalSaleHint: 'Exchange or store credit only',
      storeCreditBadge: 'Store Credit',
      finalSaleNote: 'Final-sale items are reimbursed as store credit — not a refund to your original payment method.',
      confirmStoreCredit: 'Confirm Store Credit',
      confirmReturn: 'Confirm Return',
      returnThisItem: 'Return This Item',
      returningBadge: 'Returning',
      notIncludedBadge: 'Not included',
      bonusWord: 'bonus',
      creditOfferHint: 'Prefer store credit? Choose it on any item below and get a 5% bonus on that item.',
      creditSwitchOff: 'Get store credit instead',
      creditSwitchOn: '✓ Store credit — tap to undo',
      storeCreditBonusBadge: 'Credit +5%',
      // Exchange price difference in the CUSTOMER's favor. The line states the
      // outcome as one sentence and flips with the toggle, so the customer is
      // never left guessing which form the money comes back in — the whole
      // point of this control. {amount} is substituted, never translated.
      xDiffRefundLine: '{amount} comes back to your original payment method.',
      xDiffCreditLine: '{amount} will be issued as store credit — includes your 5% bonus.',
      xDiffFinalSaleLine: '{amount} will be issued as store credit (final sale).',
      // Phase 2.7: the pay-more direction. The review row showed nothing here
      // while the count was always 1; now the count is the customer's to set
      // and stock can cut it, so this row has to carry the final figure.
      xDiffOwedLine: "You'll pay {amount} for this exchange.",
      // 2026-09-11: same-item price match — a pricier color/size of the SAME
      // product is billed at what they paid; the two price columns disagree
      // on purpose, so this line says why.
      xDiffMatchedLine: 'Same item — priced at what you originally paid, no difference.',
      // Multi-unit lines (Phase 2.6)
      partiallyReturned: '{n} already returned',
      qtyLabel: 'How many?',
      qtyOfMax: '{n} of {max}',
      qtyDecrease: 'One fewer',
      qtyIncrease: 'One more',
      // Phase 2.7 — the order card reuses qtyLabel: it sits directly under a
      // line that already reads "× 2", and a longer question would wrap the
      // controls onto a second line in the narrow mobile info column.
      // qtySelectedOf is the read-only echo on the return-confirm modal.
      qtySelectedOf: '{n} of {max} selected',
      // {max} is the replacement's stock, {n} the count being exchanged and
      // {want} what the customer asked for — three different numbers, so the
      // cut variant has to name them separately.
      qtyStockCap: 'Only {max} in stock',
      // Used only when the count ended up below what the customer picked — the
      // previous screen may have quoted them a price for {want} units, so the
      // reduction has to be stated, not just implied by a changed price line.
      qtyStockCut: 'Only {max} in stock — exchanging {n}, not {want}',
      // Phase 2.8 — the worker re-checks the replacement's stock at submit
      // and rejects before filing anything. Shown INSIDE the review modal,
      // where the picker that fixes it is.
      errStockShort: 'Only {available} of "{item}" in stock right now, but this exchange asks for {requested}. Lower the quantity on that item and submit again — nothing has been filed yet.',
      errStockSoldOut: '"{item}" just sold out. Use "Change" on that item to pick a different size or color — nothing has been filed yet.',
      // Several lines converging on one replacement: their pickers may all
      // already read 1, so "lower the quantity" would point at nothing.
      errStockShared: 'Only {available} of "{item}" in stock right now, but {rows} items in this request are being exchanged into it. Use "Change" on one of them to pick a different size or color — nothing has been filed yet.',
      // Phase 2.9 — changing a replacement from the review modal, and the
      // "which item?" chooser for a pick made after the walk-through ended.
      xChangeBtn: 'Change',
      xChangeAria: 'Choose a different replacement for {item}',
      xChangeQuestion: 'Let\'s pick a different replacement. What\'s the reason for exchanging this one?',
      xWhichItem: 'Which item is this replacing?',
      xWhichItemReturn: 'Which item would you like to return instead?',
      reopenChange: 'Change {item}',
      notShipped: 'Not yet shipped',
      alreadyReturned: 'Already returned',
      // 2026-10-13 — the cards render ABOVE this message (it used to say 'below').
      welcomeMessage: "Hi there! I'm your Mojito Concierge. Tick an item above, then tap Exchange or Return \u2014 or type a message to get started.",
      retryBtn: 'Try again',
      streamError: 'Connection lost. Please try again.',
      cardMismatchNote: "Just to be safe — here's what's actually in stock right now:",
      errAlreadyReturned: "This item already has a return in progress — there's nothing more to submit. Check your email for return instructions, or contact customer.service@mojitocollection.com if you need help.",
      partialSuccess: "Part of your request went through, but some items couldn't be processed. Please email customer.service@mojitocollection.com and we'll finish it for you.",
      submitGenericError: "We couldn't process that request. Please try again or contact customer.service@mojitocollection.com.",
      submitNetworkError: 'Something went wrong submitting. Please try again.',
      sessionExpired: 'Your session timed out for security. Please look up your order again to continue.',
      returnCreated: 'Your return has been initiated! You will receive an email with return instructions shortly.',
      exchangeCreated: 'Your exchange has been created! You will receive an email with details shortly.',
      successModalTitleExchange: 'Exchange initiated',
      successModalTitleReturn: 'Return initiated',
      successModalBody: 'We will send a return label to your email address when approved.',
      successModalBodyCredit: 'We will send a return label to your email address when approved. Your reimbursement will be issued as store credit.',
      successModalBodyCreditBonus: 'We will send a return label to your email address when approved. Your reimbursement will be issued as store credit, including your 5% bonus.',
      successModalBodyMixedBonus: 'We will send a return label to your email address when approved. The items you chose store credit for will be issued as credit with your 5% bonus; the rest goes back to your original payment method.',
      successModalBodyExchangeCredit: 'We will send a return label to your email address when approved. The price difference on your exchange will be issued as store credit, including your 5% bonus.',
      successModalBodyExchangeRefund: 'We will send a return label to your email address when approved. The price difference on your exchange goes back to your original payment method.',
      successModalClose: 'Done',
      // Product detail modal
      pdmSizeSuggestions: 'Explore different sizes or a new color option',
      pdmSizeSuggestionsBody: "Since you're open to exchanging, you can try either a different size or discover a fresh color that fits your style best.",
      pdmRecommended: 'Recommended',
      pdmSize: 'Size',
      pdmColor: 'Color',
      pdmOutOfStock: 'Out of stock',
      pdmInStock: 'In stock',
      pdmFreeExchange: 'Free exchange',
      pdmPreviouslyOrdered: "You've previously ordered this",
      pdmPrevious: 'Previous',
      pdmNoThanks: 'No thanks',
      pdmContinue: 'Continue',
      pdmNext: 'Next',
      pdmClose: 'Close',
      pdmLoading: 'Loading product…',
      // 2026-10-13 — mixed flow: one request, a choice per item. Welcome copy
      // for multi-line orders, and the hint under the action cards that names
      // the mixed case at the moments a split request is born.
      welcomeMessageMulti: "Hi there! I'm your Mojito Concierge. Tick the items you'd like to send back above, then tap Exchange or Return \u2014 you can exchange one and return another in the same request. Or just type a question.",
      hintMulti: "Exchanging one item and returning another? Tap either \u2014 you'll choose for each item next.",
      hintAddMore: 'Sending back another item too? Tick it now \u2014 one request covers both, even if you exchange one and return the other.',
      hintAppend: "Now tap Exchange or Return \u2014 this item joins the request you've already started.",
      hintLocked: 'Finish the item below first \u2014 you can still change anything in the review before you submit.',
      itemRemoved: 'Removed {item} from this request.',
      staleOption: "That option was for an item you've already decided \u2014 use the options for the current item below.",
      // The plan bubble: Exchange | Return (Store credit on final sale) per
      // item, one Continue. {items}/{parts} carry product titles — insert
      // them with a function replacement so a "$" can never expand.
      planTitle: 'What would you like to do with each item?',
      planSub: 'Everything goes in one request, with one prepaid return label.',
      intentCredit: 'Store credit',
      planToggleAria: 'What to do with {item}',
      planUnitsNote: 'Applies to all {n} units',
      planDecided: '\u2713 Already in your request',
      planContinue: 'Continue',
      countExchange: '{n} to exchange',
      countReturn: '{n} to return',
      countCredit: '{n} for store credit',
      planOpener: "I'd like to {parts}",
      planOpenerAlso: "I'd also like to {parts}",
      planPartExchange: 'exchange: {items}',
      planPartReturn: 'return: {items}',
      planPartCredit: 'get store credit for: {items}',
      // Walk-through step: "Item N of M" + intent pill, the per-intent
      // question, and the one-tap switch in both directions.
      itemOfTotal: 'Item {n} of {m}',
      stepQExchange: "What's the reason for exchanging this one?",
      stepQReturn: "What's the reason for returning this one?",
      stepQCredit: "What's the reason for sending this one back for store credit?",
      stepQInstead: "Let's find a replacement for this one.",
      xToExchangeQuestion: "Let's find a replacement. What's the reason for exchanging this one?",
      switchToReturnLink: 'Return this one instead',
      switchToCreditLink: 'Store credit for this one instead',
      switchToExchangeLink: 'Exchange this one instead',
      switchToReturnMsg: "Actually, I'd like to return this one instead.",
      switchToCreditMsg: "Actually, I'd like store credit for this one instead.",
      switchToExchangeMsg: "Actually, I'd like to exchange this one instead.",
      // Review modal (was hard-coded English) + per-row switches and the
      // "add another item" prompt.
      reviewTitleMixed: 'Review your {n} items',
      reviewTitleExchange: 'Review your exchange',
      reviewTitleReturn: 'Review your return',
      reviewSubMixed: "We'll submit everything below as one request \u2014 one return label covers all items you're sending back.",
      reviewSubExchange: "Confirm the swaps below. We'll create a single exchange order for everything at once.",
      reviewSubReturn: "Confirm the items you're returning. A single return request will be submitted.",
      reviewSectionExchanging: 'Exchanging ({n})',
      reviewSectionReturning: 'Returning ({n})',
      reviewReturnInstead: 'Return instead',
      reviewCreditInstead: 'Store credit instead',
      reviewExchangeInstead: 'Exchange instead',
      reviewReturnInsteadAria: 'Return {item} instead of exchanging it',
      reviewExchangeInsteadAria: 'Exchange {item} instead of returning it',
      reviewAddAnotherText: 'Sending back another item from this order?',
      reviewAddAnotherBtn: 'Add it to this request',
      addAnotherHint: 'Tick the other item on your order above, then tap Exchange or Return \u2014 it joins this same request and return label.',
      reviewSubmitAll: 'Submit All',
      reviewSubmitExchanges: 'Submit All Exchanges',
      reviewSubmitReturns: 'Submit All Returns',
      reviewCancel: 'Cancel',
      reviewProcessing: 'Processing\u2026',
      reopenSaved: 'Your selections are saved ({summary}). Ready when you are.',
      reopenBtn: 'Reopen Review',
      // Mixed success: one sentence per money leg, so no claim (a 5% bonus,
      // a refund) is ever made for a leg it does not apply to.
      successModalTitleMixed: 'Exchange & return submitted',
      successMixedLabel: "We'll email one return label for everything you're sending back once your request is approved.",
      successMixedExchange: 'Your exchange ships once your return is on its way.',
      successMixedRefund: "What you're returning is refunded to your original payment method.",
      successMixedCredit: "What you're returning comes back as store credit.",
      successMixedCreditBonus: "What you're returning comes back as store credit, including your 5% bonus.",
      successMixedSplit: 'Items going back as store credit are issued as credit (plus your 5% bonus where you chose it); the rest is refunded to your original payment method.',
      successMixedXCreditBonus: "Your exchange's price difference comes back as store credit, including your 5% bonus.",
      successMixedXCredit: "Your exchange's price difference comes back as store credit.",
      successMixedXRefund: "Your exchange's price difference goes back to your original payment method.",
      successModalBodyExchangeCreditFinal: 'We will send a return label to your email address when approved. The price difference on your exchange will be issued as store credit (final sale).',
      // 2026-10-13 — fix round 1: exchange rows whose money back is partly
      // credit, partly a refund. Fix round 2: the bonus is claimed only
      // "where you chose it" — a final-sale row's credit is policy, not a
      // choice — and 'SplitFinal' when no row chose credit at all.
      successModalBodyExchangeSplit: "We will send a return label to your email address when approved. Part of your exchange's price difference comes back as store credit (with your 5% bonus where you chose it); the rest goes back to your original payment method.",
      successMixedXSplit: "Part of your exchange's price difference comes back as store credit (with your 5% bonus where you chose it); the rest goes back to your original payment method.",
      successModalBodyExchangeSplitFinal: "We will send a return label to your email address when approved. Part of your exchange's price difference comes back as store credit (final sale); the rest goes back to your original payment method.",
      successMixedXSplitFinal: "Part of your exchange's price difference comes back as store credit (final sale); the rest goes back to your original payment method.",
      // 2026-10-13 — mixed flow (fix round 2): every row is credit, some by
      // the customer's choice (5% bonus) and some final sale (no bonus).
      successModalBodyExchangeCreditSome: 'We will send a return label to your email address when approved. The price difference on your exchange will be issued as store credit, plus your 5% bonus where you chose it.',
      successMixedXCreditSome: "Your exchange's price difference comes back as store credit, plus your 5% bonus where you chose it.",
      successModalBodyCreditSomeBonus: 'We will send a return label to your email address when approved. Your reimbursement will be issued as store credit, plus your 5% bonus on the items where you chose it.',
      successMixedCreditSomeBonus: "What you're returning comes back as store credit, plus your 5% bonus on the items where you chose it.",
      // Return-only: credit and refund rows mix, and a final-sale row is among
      // the credit ones (the bonus body below claimed it for every credit row).
      successModalBodySplit: 'We will send a return label to your email address when approved. Items going back as store credit are issued as credit (plus your 5% bonus where you chose it); the rest is refunded to your original payment method.'
    },
    es: {
      title: 'Devoluciones y Cambios',
      subtitle: 'Busca tu pedido para comenzar.',
      identifierLabel: 'Buscar pedido por',
      orderNumber: 'N\u00BA de Pedido',
      trackingNumber: 'N\u00BA de Seguimiento',
      verifierLabel: 'Verifica tu identidad con',
      email: 'Correo electr\u00F3nico',
      zipCode: 'C\u00F3digo Postal',
      orderPlaceholder: 'ej. 1001',
      trackingPlaceholder: 'ej. 9400111899223',
      emailPlaceholder: 'ej. nombre@correo.com',
      zipPlaceholder: 'ej. 90210',
      submit: 'Buscar Mi Pedido',
      submitting: 'Buscando...',
      policyLink: 'Ver nuestra pol\u00EDtica de devoluciones',
      errorEmpty: 'Por favor completa ambos campos.',
      errorNotFound: 'No encontramos ese pedido. Revisa tus datos e int\u00E9ntalo de nuevo.',
      errorNetwork: 'Algo sali\u00F3 mal. Por favor int\u00E9ntalo de nuevo.',
      errorTimeout: 'La solicitud expir\u00F3. Por favor int\u00E9ntalo de nuevo.',
      errorRateLimit: 'Demasiadas solicitudes. Espera un momento e int\u00E9ntalo de nuevo.',
      // Chat
      concierge: 'Mojito Concierge',
      back: 'Volver',
      orderLabel: 'Pedido',
      dateLabel: 'Fecha',
      totalLabel: 'Total',
      statusLabel: 'Estado',
      statusDelivered: 'Entregado',
      statusInTransit: 'En Tr\u00E1nsito',
      statusProcessing: 'Procesando',
      exchangeAction: 'Cambio',
      exchangeDesc: 'Cambia por otra talla o color',
      returnAction: 'Devoluci\u00F3n',
      returnDesc: 'Obt\u00E9n un reembolso o cr\u00E9dito',
      questionAction: 'Pregunta',
      questionDesc: 'Pregunta sobre tu pedido',
      selectItemFirst: 'Por favor selecciona un art\u00EDculo arriba para comenzar.',
      selectEligibleItem: 'Los art\u00EDculos seleccionados no son elegibles para devoluci\u00F3n o cambio. Contacta a customer.service@mojitocollection.com.',
      chatPlaceholder: 'Escribe un mensaje...',
      send: 'Enviar',
      wrongSize: 'Talla incorrecta',
      differentColor: 'Diferente color',
      differentStyle: 'Diferente estilo',
      otherReason: 'Otra raz\u00F3n',
      doesntFit: 'No me queda bien',
      looksDifferent: 'Se ve diferente en persona',
      damagedDefective: 'Da\u00F1ado o defectuoso',
      changedMind: 'Cambi\u00E9 de opini\u00F3n',
      tooSmall: 'Me queda peque\u00F1o \u2014 talla m\u00E1s grande',
      tooLarge: 'Me queda grande \u2014 talla m\u00E1s peque\u00F1a',
      fitShape: 'Es mi talla, pero no me queda bien',
      wrongItem: 'Me enviaron otro art\u00EDculo',
      sizingFit: 'Tallas y ajuste',
      careInstructions: 'Instrucciones de cuidado',
      shippingTimelines: 'Env\u00EDo y plazos',
      returnPolicyTopic: 'Pol\u00EDtica de devoluciones',
      outsideWindow: 'Este pedido est\u00E1 fuera de nuestra ventana de devoluci\u00F3n de 30 d\u00EDas.',
      finalSale: 'Venta Final',
      finalSaleHint: 'Solo cambio o cr\u00E9dito de tienda',
      storeCreditBadge: 'Cr\u00E9dito de Tienda',
      finalSaleNote: 'Los art\u00EDculos de venta final se reembolsan como cr\u00E9dito de tienda \u2014 no a tu m\u00E9todo de pago original.',
      confirmStoreCredit: 'Confirmar Cr\u00E9dito',
      confirmReturn: 'Confirmar Devoluci\u00F3n',
      returnThisItem: 'Devolver Este Art\u00EDculo',
      returningBadge: 'Devolviendo',
      notIncludedBadge: 'No incluido',
      bonusWord: 'de bono',
      creditOfferHint: '\u00BFPrefieres cr\u00E9dito de tienda? El\u00EDgelo en cualquier art\u00EDculo y recibe un 5% extra en ese art\u00EDculo.',
      creditSwitchOff: 'Prefiero cr\u00E9dito de tienda',
      creditSwitchOn: '\u2713 Cr\u00E9dito de tienda \u2014 toca para deshacer',
      storeCreditBonusBadge: 'Cr\u00E9dito +5%',
      xDiffRefundLine: '{amount} volver\u00E1 a tu m\u00E9todo de pago original.',
      xDiffCreditLine: '{amount} se emitir\u00E1 como cr\u00E9dito de tienda \u2014 incluye tu bono del 5%.',
      xDiffFinalSaleLine: '{amount} se emitir\u00E1 como cr\u00E9dito de tienda (venta final).',
      xDiffOwedLine: 'Pagar\u00E1s {amount} por este cambio.',
      xDiffMatchedLine: 'Mismo art\u00EDculo \u2014 al precio que pagaste originalmente, sin diferencia.',
      partiallyReturned: '{n} ya devuelto(s)',
      qtyLabel: '\u00BFCu\u00E1ntos?',
      qtyOfMax: '{n} de {max}',
      qtyDecrease: 'Uno menos',
      qtyIncrease: 'Uno m\u00E1s',
      qtySelectedOf: '{n} de {max} seleccionado(s)',
      qtyStockCap: 'Solo {max} en stock',
      qtyStockCut: 'Solo {max} en stock — cambiando {n}, no {want}',
      errStockShort: 'Solo hay {available} de "{item}" en stock ahora mismo, pero este cambio pide {requested}. Reduce la cantidad de ese artículo y vuelve a enviar — todavía no se ha registrado nada.',
      errStockSoldOut: '"{item}" se acaba de agotar. Usa "Cambiar" en ese artículo para elegir otra talla o color — todavía no se ha registrado nada.',
      errStockShared: 'Solo hay {available} de "{item}" en stock ahora mismo, pero {rows} artículos de esta solicitud se están cambiando por él. Usa "Cambiar" en uno de ellos para elegir otra talla o color — todavía no se ha registrado nada.',
      xChangeBtn: 'Cambiar',
      xChangeAria: 'Elegir otro reemplazo para {item}',
      xChangeQuestion: 'Elijamos otro reemplazo. \u00BFCu\u00E1l es el motivo del cambio de este art\u00EDculo?',
      xWhichItem: '\u00BFQu\u00E9 art\u00EDculo reemplaza esto?',
      xWhichItemReturn: '\u00BFQu\u00E9 art\u00EDculo prefieres devolver?',
      reopenChange: 'Cambiar {item}',
      notShipped: 'A\u00FAn no enviado',
      alreadyReturned: 'Ya devuelto',
      welcomeMessage: '\u00A1Hola! Soy tu Mojito Concierge. Marca un art\u00EDculo arriba y toca Cambio o Devoluci\u00F3n, o escribe un mensaje para comenzar.',
      retryBtn: 'Intentar de nuevo',
      streamError: 'Conexi\u00F3n perdida. Por favor int\u00E9ntalo de nuevo.',
      cardMismatchNote: 'Para confirmar \u2014 esto es lo que realmente est\u00E1 disponible ahora:',
      errAlreadyReturned: 'Este art\u00EDculo ya tiene una devoluci\u00F3n en curso; no hay nada m\u00E1s que enviar. Revisa tu correo para las instrucciones de devoluci\u00F3n, o contacta a customer.service@mojitocollection.com si necesitas ayuda.',
      partialSuccess: 'Parte de tu solicitud se proces\u00F3, pero algunos art\u00EDculos no pudieron completarse. Por favor escribe a customer.service@mojitocollection.com y lo finalizaremos por ti.',
      submitGenericError: 'No pudimos procesar esa solicitud. Int\u00E9ntalo de nuevo o contacta a customer.service@mojitocollection.com.',
      submitNetworkError: 'Algo sali\u00F3 mal al enviar. Por favor int\u00E9ntalo de nuevo.',
      sessionExpired: 'Tu sesi\u00F3n expir\u00F3 por seguridad. Por favor busca tu pedido de nuevo para continuar.',
      returnCreated: '\u00A1Tu devoluci\u00F3n ha sido iniciada! Recibir\u00E1s un correo con instrucciones.',
      exchangeCreated: '\u00A1Tu cambio ha sido creado! Recibir\u00E1s un correo con los detalles.',
      successModalTitleExchange: 'Cambio iniciado',
      successModalTitleReturn: 'Devoluci\u00F3n iniciada',
      successModalBody: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada.',
      successModalBodyCredit: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. Tu reembolso se emitir\u00E1 como cr\u00E9dito de tienda.',
      successModalBodyCreditBonus: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. Tu reembolso se emitir\u00E1 como cr\u00E9dito de tienda, incluyendo tu bono del 5%.',
      successModalBodyMixedBonus: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. Los art\u00EDculos para los que elegiste cr\u00E9dito de tienda se emitir\u00E1n como cr\u00E9dito con tu bono del 5%; el resto volver\u00E1 a tu m\u00E9todo de pago original.',
      successModalBodyExchangeCredit: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. La diferencia de precio de tu cambio se emitir\u00E1 como cr\u00E9dito de tienda, incluyendo tu bono del 5%.',
      successModalBodyExchangeRefund: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. La diferencia de precio de tu cambio volver\u00E1 a tu m\u00E9todo de pago original.',
      successModalClose: 'Listo',
      // Modal de detalle de producto
      pdmSizeSuggestions: 'Explora tallas diferentes o un nuevo color',
      pdmSizeSuggestionsBody: 'Ya que est\u00E1s abierto al cambio, puedes probar otra talla o descubrir un color fresco que se ajuste a tu estilo.',
      pdmRecommended: 'Recomendado',
      pdmSize: 'Talla',
      pdmColor: 'Color',
      pdmOutOfStock: 'Sin existencia',
      pdmInStock: 'Disponible',
      pdmFreeExchange: 'Cambio gratis',
      pdmPreviouslyOrdered: 'Lo has ordenado antes',
      pdmPrevious: 'Anterior',
      pdmNoThanks: 'No, gracias',
      pdmContinue: 'Continuar',
      pdmNext: 'Siguiente',
      pdmClose: 'Cerrar',
      pdmLoading: 'Cargando producto\u2026',
      welcomeMessageMulti: '\u00A1Hola! Soy tu Mojito Concierge. Marca arriba los art\u00EDculos que quieres enviar y toca Cambio o Devoluci\u00F3n; puedes cambiar uno y devolver otro en la misma solicitud. O escribe una pregunta.',
      hintMulti: '\u00BFCambias un art\u00EDculo y devuelves otro? Toca cualquiera: luego eliges para cada art\u00EDculo.',
      hintAddMore: '\u00BFTambi\u00E9n env\u00EDas otro art\u00EDculo? M\u00E1rcalo ahora: una sola solicitud cubre ambos, aunque cambies uno y devuelvas el otro.',
      hintAppend: 'Ahora toca Cambio o Devoluci\u00F3n: este art\u00EDculo se suma a la solicitud que ya empezaste.',
      hintLocked: 'Primero termina con el art\u00EDculo de abajo; a\u00FAn puedes cambiar todo en la revisi\u00F3n antes de enviar.',
      itemRemoved: 'Quitamos {item} de esta solicitud.',
      staleOption: 'Esa opci\u00F3n era para un art\u00EDculo que ya decidiste; usa las opciones del art\u00EDculo actual, abajo.',
      planTitle: '\u00BFQu\u00E9 quieres hacer con cada art\u00EDculo?',
      planSub: 'Todo va en una sola solicitud, con una etiqueta de devoluci\u00F3n prepagada.',
      intentCredit: 'Cr\u00E9dito de tienda',
      planToggleAria: 'Qu\u00E9 hacer con {item}',
      planUnitsNote: 'Aplica a las {n} unidades',
      planDecided: '\u2713 Ya est\u00E1 en tu solicitud',
      planContinue: 'Continuar',
      countExchange: '{n} para cambiar',
      countReturn: '{n} para devolver',
      countCredit: '{n} por cr\u00E9dito de tienda',
      planOpener: 'Quiero {parts}',
      planOpenerAlso: 'Tambi\u00E9n quiero {parts}',
      planPartExchange: 'cambiar: {items}',
      planPartReturn: 'devolver: {items}',
      planPartCredit: 'cr\u00E9dito de tienda por: {items}',
      itemOfTotal: 'Art\u00EDculo {n} de {m}',
      stepQExchange: '\u00BFPor qu\u00E9 quieres cambiar este art\u00EDculo?',
      stepQReturn: '\u00BFPor qu\u00E9 quieres devolver este art\u00EDculo?',
      stepQCredit: '\u00BFPor qu\u00E9 devuelves este art\u00EDculo por cr\u00E9dito de tienda?',
      stepQInstead: 'Busquemos un reemplazo para este art\u00EDculo.',
      xToExchangeQuestion: 'Busquemos un reemplazo. \u00BFPor qu\u00E9 quieres cambiar este art\u00EDculo?',
      switchToReturnLink: 'Mejor devolver este',
      switchToCreditLink: 'Mejor cr\u00E9dito de tienda por este',
      switchToExchangeLink: 'Mejor cambiar este',
      switchToReturnMsg: 'Mejor quiero devolver este.',
      switchToCreditMsg: 'Mejor quiero cr\u00E9dito de tienda por este.',
      switchToExchangeMsg: 'Mejor quiero cambiar este.',
      reviewTitleMixed: 'Revisa tus {n} art\u00EDculos',
      reviewTitleExchange: 'Revisa tu cambio',
      reviewTitleReturn: 'Revisa tu devoluci\u00F3n',
      reviewSubMixed: 'Enviaremos todo lo de abajo como una sola solicitud: una sola etiqueta de devoluci\u00F3n cubre todo lo que nos env\u00EDas.',
      reviewSubExchange: 'Confirma los cambios de abajo. Crearemos un solo pedido de cambio para todo.',
      reviewSubReturn: 'Confirma los art\u00EDculos que devuelves. Se enviar\u00E1 una sola solicitud de devoluci\u00F3n.',
      reviewSectionExchanging: 'Cambiando ({n})',
      reviewSectionReturning: 'Devolviendo ({n})',
      reviewReturnInstead: 'Mejor devolver',
      reviewCreditInstead: 'Mejor cr\u00E9dito de tienda',
      reviewExchangeInstead: 'Mejor cambiar',
      reviewReturnInsteadAria: 'Devolver {item} en lugar de cambiarlo',
      reviewExchangeInsteadAria: 'Cambiar {item} en lugar de devolverlo',
      reviewAddAnotherText: '\u00BFEnv\u00EDas otro art\u00EDculo de este pedido?',
      reviewAddAnotherBtn: 'Agregarlo a esta solicitud',
      addAnotherHint: 'Marca el otro art\u00EDculo en tu pedido de arriba y toca Cambio o Devoluci\u00F3n: se suma a esta misma solicitud y etiqueta.',
      reviewSubmitAll: 'Enviar todo',
      reviewSubmitExchanges: 'Enviar cambios',
      reviewSubmitReturns: 'Enviar devoluciones',
      reviewCancel: 'Cancelar',
      reviewProcessing: 'Procesando\u2026',
      reopenSaved: 'Tu selecci\u00F3n est\u00E1 guardada ({summary}). Cuando quieras.',
      reopenBtn: 'Abrir revisi\u00F3n',
      successModalTitleMixed: 'Cambio y devoluci\u00F3n enviados',
      successMixedLabel: 'Cuando se apruebe tu solicitud, te enviaremos por correo una sola etiqueta de devoluci\u00F3n para todo lo que nos env\u00EDas.',
      successMixedExchange: 'Tu cambio se enviar\u00E1 cuando tu devoluci\u00F3n est\u00E9 en camino.',
      successMixedRefund: 'Lo que devuelves se reembolsar\u00E1 a tu m\u00E9todo de pago original.',
      successMixedCredit: 'Lo que devuelves se reembolsar\u00E1 como cr\u00E9dito de tienda.',
      successMixedCreditBonus: 'Lo que devuelves se reembolsar\u00E1 como cr\u00E9dito de tienda, con tu bono del 5%.',
      successMixedSplit: 'Los art\u00EDculos con cr\u00E9dito de tienda se emitir\u00E1n como cr\u00E9dito (m\u00E1s tu bono del 5% donde lo elegiste); el resto se reembolsar\u00E1 a tu m\u00E9todo de pago original.',
      successMixedXCreditBonus: 'La diferencia de precio de tu cambio se emitir\u00E1 como cr\u00E9dito de tienda, con tu bono del 5%.',
      successMixedXCredit: 'La diferencia de precio de tu cambio se emitir\u00E1 como cr\u00E9dito de tienda.',
      successMixedXRefund: 'La diferencia de precio de tu cambio volver\u00E1 a tu m\u00E9todo de pago original.',
      successModalBodyExchangeCreditFinal: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. La diferencia de precio de tu cambio se emitir\u00E1 como cr\u00E9dito de tienda (venta final).',
      successModalBodyExchangeSplit: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. Parte de la diferencia de precio de tu cambio vuelve como cr\u00E9dito de tienda (con tu bono del 5% donde lo elegiste); el resto volver\u00E1 a tu m\u00E9todo de pago original.',
      successMixedXSplit: 'Parte de la diferencia de precio de tu cambio vuelve como cr\u00E9dito de tienda (con tu bono del 5% donde lo elegiste); el resto volver\u00E1 a tu m\u00E9todo de pago original.',
      successModalBodyExchangeSplitFinal: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. Parte de la diferencia de precio de tu cambio vuelve como cr\u00E9dito de tienda (venta final); el resto volver\u00E1 a tu m\u00E9todo de pago original.',
      successMixedXSplitFinal: 'Parte de la diferencia de precio de tu cambio vuelve como cr\u00E9dito de tienda (venta final); el resto volver\u00E1 a tu m\u00E9todo de pago original.',
      successModalBodyExchangeCreditSome: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. La diferencia de precio de tu cambio se emitir\u00E1 como cr\u00E9dito de tienda, m\u00E1s tu bono del 5% donde lo elegiste.',
      successMixedXCreditSome: 'La diferencia de precio de tu cambio vuelve como cr\u00E9dito de tienda, m\u00E1s tu bono del 5% donde lo elegiste.',
      successModalBodyCreditSomeBonus: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. Tu reembolso se emitir\u00E1 como cr\u00E9dito de tienda, m\u00E1s tu bono del 5% en los art\u00EDculos donde lo elegiste.',
      successMixedCreditSomeBonus: 'Lo que devuelves vuelve como cr\u00E9dito de tienda, m\u00E1s tu bono del 5% en los art\u00EDculos donde lo elegiste.',
      successModalBodySplit: 'Te enviaremos una etiqueta de devoluci\u00F3n a tu correo electr\u00F3nico cuando sea aprobada. Los art\u00EDculos que vuelven como cr\u00E9dito de tienda se emiten como cr\u00E9dito (m\u00E1s tu bono del 5% donde lo elegiste); el resto se reembolsa a tu m\u00E9todo de pago original.'
    }
  };

  /* ========== Reason Buttons Config ========== */

  // Reason schema v2 (2026-08): fit chips are DIRECTIONAL (too-small /
  // too-large) so the recorded Shopify enum carries a real direction —
  // the old direction-less doesnt-fit / wrong-size chips were both filed
  // as SIZE_TOO_LARGE by the worker and made the reason report read
  // backwards. Legacy keys stay recognized everywhere for cached JS.
  var REASON_BUTTONS = {
    exchange: [
      { key: 'too-small', label: 'tooSmall' },
      { key: 'too-large', label: 'tooLarge' },
      { key: 'different-color', label: 'differentColor' },
      { key: 'different-style', label: 'differentStyle' },
      { key: 'damaged-defective', label: 'damagedDefective' },
      { key: 'wrong-item', label: 'wrongItem' },
      { key: 'other-reason', label: 'otherReason' }
    ],
    return: [
      { key: 'too-small', label: 'tooSmall' },
      { key: 'too-large', label: 'tooLarge' },
      { key: 'fit-shape', label: 'fitShape' },
      { key: 'looks-different', label: 'looksDifferent' },
      { key: 'damaged-defective', label: 'damagedDefective' },
      { key: 'wrong-item', label: 'wrongItem' },
      { key: 'changed-mind', label: 'changedMind' },
      { key: 'other-reason', label: 'otherReason' }
    ],
    question: [
      { key: 'sizing-fit', label: 'sizingFit' },
      { key: 'care-instructions', label: 'careInstructions' },
      { key: 'shipping-timelines', label: 'shippingTimelines' },
      { key: 'return-policy', label: 'returnPolicyTopic' }
    ]
  };

  // 2026-10-13 — mixed flow kill switch. false = no plan bubble: with 2+
  // pending lines the walk-through starts with every line on the pressed
  // card's intent, without wiping anything, and each step can still switch.
  // A one-line theme push turns the bubble off without restoring the old
  // wipe. The harness flips it with ?plan=off.
  var PLAN_ENABLED = true;

  /* ========== Helpers ========== */

  function detectLocale() {
    if (typeof Shopify !== 'undefined' && Shopify.locale) {
      return Shopify.locale.substring(0, 2);
    }
    var htmlLang = document.documentElement.lang || '';
    return htmlLang.substring(0, 2) || 'en';
  }

  function t(key) {
    var lang = detectLocale();
    var dict = STRINGS[lang] || STRINGS.en;
    return dict[key] || STRINGS.en[key] || key;
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        if (key === 'className') {
          node.className = attrs[key];
        } else if (key === 'htmlContent') {
          // handled below
        } else if (key.indexOf('on') === 0) {
          node.addEventListener(key.substring(2).toLowerCase(), attrs[key]);
        } else if (attrs[key] === false || attrs[key] === null || attrs[key] === undefined) {
          // Boolean HTML attributes (disabled, readonly, checked, etc.) are
          // "true" whenever present — setAttribute(key, 'false') still disables.
          // Skip when the author passed a falsy value.
        } else {
          node.setAttribute(key, attrs[key]);
        }
      });
      if (attrs.htmlContent) {
        node.innerHTML = attrs.htmlContent;
      }
    }
    if (children) {
      if (!Array.isArray(children)) children = [children];
      children.forEach(function (child) {
        if (typeof child === 'string') {
          node.appendChild(document.createTextNode(child));
        } else if (child) {
          node.appendChild(child);
        }
      });
    }
    return node;
  }

  function formatDate(dateStr) {
    try {
      var d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      var lang = detectLocale();
      return d.toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  }

  function formatMessage(text) {
    // Strip marker protocols from display:
    //   [[BTN:Label]]      — rendered as clickable chips after stream
    //   [[CARD:variant_id]] — rendered as a single product card after stream
    //   [[CARDS]]           — re-render all recommendations after stream
    // Also hide partial markers during streaming to avoid flicker.
    var clean = text.replace(/\[{2,}BTN:[^\]]*\]{2,}/g, '');
    clean = clean.replace(/\[{2,}CARD:[^\]]*\]{2,}/g, '');
    clean = clean.replace(/\[{2,}CARDS\]{2,}/g, '');
    // Partial markers mid-stream (e.g. "[[CA", "[[CARD:45123", "[[CARD:45123]")
    // NB: the class must exclude "[" too, or a long bracket run backtracks
    // catastrophically against \[{2,} on every streaming frame.
    clean = clean.replace(/\[{2,}(BTN|CARDS?)?:?[^\[\]]*\]?$/, '');
    clean = clean.replace(/\[\[?$/, '');
    // The model occasionally fumbles marker syntax (extra or orphan brackets);
    // the strict patterns above leave that debris behind, and a stray "]" at
    // the end traps the markers' blank lines inside the bubble. Square
    // brackets are protocol-only — never legit customer prose.
    clean = clean.replace(/^[ \t]*[\[\],]+[ \t]*$/gm, '');
    clean = clean.replace(/[ \t]+\n/g, '\n');
    clean = clean.replace(/\n{3,}/g, '\n\n');
    clean = clean.replace(/[\s\[\],]+$/, '');
    var html = clean.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\n/g, '<br>');
    return html;
  }

  // Extract AI marker protocols from output.
  //   [[BTN:Label]]      — action chips (max 3)
  //   [[CARD:variant_id]] — propose a specific variant for the customer to click/confirm
  //   [[CARDS]]           — re-render all current recommendations
  // Returns { text, buttons, cardIds, showAllCards }.
  function parseAIMarkers(text) {
    var buttons = [];
    var cardIds = [];
    var showAllCards = false;

    var stripped = text.replace(/\[{2,}BTN:([^\]]+)\]{2,}/g, function (_, label) {
      var clean = label.trim().substring(0, 20);
      if (clean) buttons.push(clean);
      return '';
    });
    stripped = stripped.replace(/\[{2,}CARD:([^\]]+)\]{2,}/g, function (_, id) {
      var clean = id.trim();
      if (clean) cardIds.push(clean);
      return '';
    });
    stripped = stripped.replace(/\[{2,}CARDS\]{2,}/g, function () {
      showAllCards = true;
      return '';
    });
    // The model occasionally fumbles marker syntax (extra or orphan brackets);
    // the strict patterns above leave that debris behind, and a stray "]" at
    // the end defeats the trim below, trapping the markers' blank lines
    // inside the bubble. Square brackets are protocol-only — never legit
    // customer prose.
    stripped = stripped.replace(/^[ \t]*[\[\],]+[ \t]*$/gm, '');
    stripped = stripped.replace(/[ \t]+\n/g, '\n');
    stripped = stripped.replace(/\n{3,}/g, '\n\n');
    stripped = stripped.replace(/[\s\[\],]+$/, '');

    return {
      text: stripped.trim(),
      buttons: buttons.slice(0, 3),
      cardIds: cardIds,
      showAllCards: showAllCards
    };
  }

  // Render AI-suggested buttons as clickable chips under the last message.
  // Clicking sends the label as the next user turn.
  // 2026-10-13 — mixed flow: bind ({seq, itemId}) ties the row to the
  // walk-through step whose reply produced it, so a button from item 1 can
  // never return item 2 (markCurrentItemAsReturn refuses a stale bind).
  function renderAIButtons(app, labels, bind) {
    if (!labels || labels.length === 0) return;
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;

    // Remove any previous AI button row so only the most recent turn has them
    var prev = messagesEl.querySelectorAll('.mojito-returns-chat__ai-buttons');
    prev.forEach(function (n) { n.remove(); });

    var row = el('div', { className: 'mojito-returns-chat__ai-buttons' });
    if (bind && bind.seq != null) stamp(row, bind.seq);
    labels.forEach(function (label, idx) {
      var btn = el('button', {
        type: 'button',
        className: 'mojito-returns-chat__ai-button' + (idx === 0 ? ' is-primary' : ''),
        onClick: function () {
          // 2026-10-13 (fix round 1): handleChatSend and handleAction now
          // refuse a turn while another is in flight — refuse here first,
          // before the row is removed, so the buttons stay usable.
          if (turnInFlight() || state.stepChanging) return;
          // Remove the button row once clicked to prevent double-sends.
          row.remove();
          // Return-confirmation labels bypass the AI round-trip:
          //   - In queue mode: mark just the CURRENT item and advance
          //   - Single-item: open the global return confirmation modal
          if (isReturnConfirmLabel(label)) {
            if (isInItemQueueMode()) {
              markCurrentItemAsReturn(bind ? { seq: bind.seq, itemId: bind.itemId, via: 'ai_button' } : { via: 'ai_button' });
              return;
            }
            // Auto-select the single eligible item if nothing's selected yet
            if (state.selectedItems.length === 0 && state.order && state.order.line_items) {
              var eligible = state.order.line_items.filter(function (li) { return li.eligible; });
              if (eligible.length === 1) state.selectedItems = [eligible[0].id];
              // 2026-10-13: the order card's tick has to follow the state.
              updateActionStates();
            }
            // Fix round 1: a newly ticked line waiting after earlier
            // decisions takes the Return card's append path, not the modal.
            if (appendPending()) { handleAction('return'); return; }
            openReturnConfirmModal();
            return;
          }
          // Store-credit labels only ride the return flow when the item(s)
          // in play are final-sale (that's the only structural store-credit
          // outcome). For regular items the label goes to the AI round-trip
          // — otherwise the click would silently submit a cash-refund
          // return the customer didn't choose.
          if (isStoreCreditLabel(label)) {
            if (isInItemQueueMode() && storeCreditContextApplies()) {
              markCurrentItemAsReturn(bind ? { seq: bind.seq, itemId: bind.itemId, via: 'ai_button' } : { via: 'ai_button' });
              return;
            }
            if (!isInItemQueueMode()) {
              // Auto-select only when it makes the context APPLY (exactly one
              // eligible FINAL-SALE item) — selecting a regular item and then
              // falling through to chat would strand a selection the customer
              // never made.
              if (state.selectedItems.length === 0 && state.order && state.order.line_items) {
                var eligibleFs = state.order.line_items.filter(function (li) {
                  return li.eligible && li.final_sale;
                });
                if (eligibleFs.length === 1) state.selectedItems = [eligibleFs[0].id];
                updateActionStates();
              }
              if (storeCreditContextApplies()) {
                if (appendPending()) { handleAction('return'); return; }   // fix round 1, as above
                openReturnConfirmModal();
                return;
              }
            }
          }
          // Route other labels through handleChatSend so they trigger the
          // same exchange-flow pre-fetch and intent detection as freeform input.
          handleChatSend(app, label);
        }
      }, label);
      row.appendChild(btn);
    });
    messagesEl.appendChild(row);
    scrollToBottom();
  }

  function statusLabel(status) {
    if (!status) return t('statusProcessing');
    var s = status.toLowerCase();
    if (s === 'delivered' || s === 'fulfilled') return t('statusDelivered');
    if (s === 'in_transit' || s === 'in transit') return t('statusInTransit');
    return t('statusProcessing');
  }

  function getSessionId() {
    var sid = sessionStorage.getItem('mojito_returns_session');
    if (!sid) {
      sid = 'mrs_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
      sessionStorage.setItem('mojito_returns_session', sid);
    }
    return sid;
  }

  /* ========== State ========== */

  var state = {
    screen: 'lookup',
    identifierType: 'order',
    verifierType: 'email',
    loading: false,
    // Chat state
    order: null,
    // Order-bound session token minted by /api/lookup; echoed on every
    // action call (auto-injected by apiFetch) so the worker can authorize.
    authToken: null,
    eligible: true,
    eligibilityReason: null,
    selectedItems: [],
    // Phase 2.7 — units the customer picked on the ORDER CARD, keyed by
    // line_item id. Lives on state rather than on a selection because the
    // picker runs one screen before any selection object exists;
    // commitSelection stamps it onto whichever selection the item becomes.
    itemUnits: {},
    messages: [],
    chatLoading: false,
    activeAction: null,
    streamingNode: null,
    lastRecommendations: null,
    lastExchangeContext: null,
    originalItemPrice: null,
    // Phase 2.4 — the selected item's original-order discount rate (0–1).
    // The worker carries this discount onto the exchange item, so every
    // price delta shown to the customer scales by (1 − rate).
    originalItemDiscountRate: 0,
    // 2026-09-11 — same-item price match: the selected item's product id,
    // snapshotted with the price above, so cards/PDM can tell a same-product
    // replacement (billed at the paid price) from a cross-product one.
    originalItemProductId: null,
    activeReason: null,
    showReturnConfirm: false,
    // Customer all-time purchase history for personalization (⭐ previously ordered).
    // Shape: { recent:[...], previousProductIds:[...], previousVariantIds:[...], sizeCounts:{}, colorCounts:{}, totalOrders:N }
    customerHistory: null,
    // Question mode: compact care/fabric/fit facts for the ordered products,
    // fetched from /api/product-info when the Question action is clicked and
    // sent with chat turns so the AI answers from real product data.
    productInfoContext: null,
    productInfoLoading: false,
    // Count of explicit "I want a return/refund" signals AFTER the AI has
    // pitched at least one exchange. Two mentions = switch to return flow.
    returnIntentCount: 0,
    hasPitchedExchange: false,
    // Sticky flag: customer explicitly said they're open to higher-priced items.
    // Drops the ±10% maxPrice cap on all subsequent recommendation fetches.
    priceOpen: false,
    // The product_id the AI most recently proposed via [[CARD:id]].
    // Used to target "See Other Colors/Sizes" fetches at the PROPOSED product
    // instead of defaulting back to the customer's original order item.
    currentProposedProductId: null,
    // Multi-item exchange queue: when the customer selects 2+ items to
    // exchange, we walk them through each one in turn. Can't submit until
    // every item has either a chosen variant or been deselected.
    //   exchangeQueue       — line_item IDs still awaiting a choice
    //   exchangeSelections  — items resolved via exchange: [{originalItemId, originalItem, recommendation}]
    //   returnSelections    — items resolved via return: [{originalItemId, originalItem, reason}]
    //   currentExchangeItemId — the line_item currently being worked on
    // The queue is used for BOTH exchange-action and return-action multi-item
    // flows. Each item can resolve to either an exchange OR a return.
    exchangeQueue: null,
    exchangeSelections: null,
    returnSelections: null,
    currentExchangeItemId: null,
    // Phase 2.9 — the worker's `shortages` from the last 409
    // insufficient_stock, plus the sentence shown for it. Which LINES are
    // short is derived from these against the current selections
    // (stockShortIds()), so a flag clears itself the moment the customer
    // changes one of the rows sharing that replacement or lowers its count.
    // Set only by a live rejection; cleared by every other server answer,
    // by clearQueueState and by the resets.
    stockShortages: null,
    stockShortMessage: null,
    // 2026-10-13 — mixed flow (one request, a choice per item):
    //   itemPlan     — {lineIdString: 'exchange'|'return'}; store credit is
    //                  'return' on a final_sale line. What each ticked line is
    //                  headed for, before and during the walk-through — the
    //                  chips, the verb and the AI's CURRENT ITEM tag read it
    //                  instead of the global activeAction.
    //   planDraft    — the open plan bubble:
    //                  {preset, intents, node, list, summaryEl, startBtn, rows}
    //   stepSeq      — monotonic; bumps on every walk-through step change.
    //                  Step-scoped controls carry it as data-step, and every
    //                  AI turn remembers the value it was SENT under, so a
    //                  control or reply from a step already left can never
    //                  act on the next one. Never reset.
    //   stepChanging — a step change is waiting for an in-flight reply.
    itemPlan: {},
    planDraft: null,
    stepSeq: 0,
    stepChanging: false,
    // 2026-10-13 — mixed flow (fix round 1):
    //   chatGen     — monotonic; bumps when the chat session ends (Back, the
    //                 success reset). A kit fetch or a reply started under an
    //                 older value belongs to a session already left and writes
    //                 nothing. Never reset.
    //   typingGen   — monotonic; bumps on every showTypingIndicator, so a
    //                 stale kit lets go of the typing dots only when they are
    //                 still its own (see kitDrop).
    //   singleLineId — the line the single path is about (pinned when it
    //                 starts), so its context, its inline confirm and its
    //                 commit all name the SAME line whatever else is ticked.
    chatGen: 0,
    typingGen: 0,
    singleLineId: null,
    // Both were created lazily before (handleChatSend / the kit fetches);
    // declared here so every reset can cover them.
    reasonDetails: {},
    pendingRecommendationsRender: false
  };

  /* ========== Analytics ========== */

  function trackEvent(name, data) {
    try {
      var detail = { event: name, timestamp: Date.now() };
      if (data) {
        Object.keys(data).forEach(function (k) { detail[k] = data[k]; });
      }
      window.dispatchEvent(new CustomEvent('mojito:analytics', { detail: detail }));
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && window.ShopifyAnalytics.lib.track) {
        window.ShopifyAnalytics.lib.track(name, data || {});
      }
    } catch (e) { /* analytics should never break the app */ }
  }

  /* ========== Config ========== */

  function getConfig(app) {
    var section = app.closest('[data-section-type="mojito-returns-portal"]');
    return {
      apiEndpoint: section ? section.getAttribute('data-api-endpoint') : '',
      supportEmail: section ? section.getAttribute('data-support-email') : '',
      maxMessageLength: parseInt(section ? section.getAttribute('data-max-message-length') : '500', 10)
    };
  }

  /* ========== API Fetch with Timeout ========== */

  function apiFetch(url, options, timeoutMs) {
    timeoutMs = timeoutMs || 30000;
    var controller = new AbortController();
    options = options || {};
    options.signal = controller.signal;

    // Inject the order-bound session token into POST JSON bodies so every
    // action call is authorized by the worker. Harmless on /api/lookup (no
    // token yet) and ignored by older workers that don't read it.
    if (state.authToken && options.body && typeof options.body === 'string') {
      try {
        var parsedBody = JSON.parse(options.body);
        if (parsedBody && typeof parsedBody === 'object' && !Array.isArray(parsedBody) && !parsedBody.token) {
          parsedBody.token = state.authToken;
          options.body = JSON.stringify(parsedBody);
        }
      } catch (e) { /* non-JSON body — leave untouched */ }
    }

    var timer = setTimeout(function () { controller.abort(); }, timeoutMs);

    return fetch(url, options)
      .then(function (res) {
        clearTimeout(timer);
        return res;
      })
      .catch(function (err) {
        clearTimeout(timer);
        if (err.name === 'AbortError') {
          throw new Error('timeout');
        }
        throw err;
      });
  }

  /* ========== Lookup Screen ========== */

  function renderLookupScreen(app) {
    app.innerHTML = '';

    var screen = el('div', { className: 'mojito-returns-screen mojito-returns-card' });

    // Header
    var header = el('div', { className: 'mojito-returns-header' }, [
      el('h2', { className: 'mojito-returns-header__title' }, t('title')),
      el('p', { className: 'mojito-returns-header__subtitle' }, t('subtitle'))
    ]);
    screen.appendChild(header);

    // Identifier toggle
    var identifierGroup = el('div', { className: 'mojito-returns-field-group' });
    identifierGroup.appendChild(el('span', { className: 'mojito-returns-field-label' }, t('identifierLabel')));

    var identifierToggle = el('div', { className: 'mojito-returns-toggle-group' });
    var btnOrder = el('button', {
      type: 'button',
      className: 'mojito-returns-toggle-btn is-active',
      'data-value': 'order',
      'aria-pressed': 'true'
    }, t('orderNumber'));
    var btnTracking = el('button', {
      type: 'button',
      className: 'mojito-returns-toggle-btn',
      'data-value': 'tracking',
      'aria-pressed': 'false'
    }, t('trackingNumber'));
    identifierToggle.appendChild(btnOrder);
    identifierToggle.appendChild(btnTracking);
    identifierGroup.appendChild(identifierToggle);

    var identifierInput = el('input', {
      type: 'text',
      className: 'mojito-returns-input',
      id: 'mojito-returns-identifier',
      placeholder: t('orderPlaceholder'),
      autocomplete: 'off',
      'aria-label': t('orderNumber')
    });
    identifierGroup.appendChild(el('div', { className: 'mojito-returns-input-wrap' }, [identifierInput]));
    screen.appendChild(identifierGroup);

    // Verifier toggle
    var verifierGroup = el('div', { className: 'mojito-returns-field-group' });
    verifierGroup.appendChild(el('span', { className: 'mojito-returns-field-label' }, t('verifierLabel')));

    var verifierToggle = el('div', { className: 'mojito-returns-toggle-group' });
    var btnEmail = el('button', {
      type: 'button',
      className: 'mojito-returns-toggle-btn is-active',
      'data-value': 'email',
      'aria-pressed': 'true'
    }, t('email'));
    var btnZip = el('button', {
      type: 'button',
      className: 'mojito-returns-toggle-btn',
      'data-value': 'zip',
      'aria-pressed': 'false'
    }, t('zipCode'));
    verifierToggle.appendChild(btnEmail);
    verifierToggle.appendChild(btnZip);
    verifierGroup.appendChild(verifierToggle);

    var verifierInput = el('input', {
      type: 'email',
      className: 'mojito-returns-input',
      id: 'mojito-returns-verifier',
      placeholder: t('emailPlaceholder'),
      autocomplete: 'off',
      'aria-label': t('email')
    });
    verifierGroup.appendChild(el('div', { className: 'mojito-returns-input-wrap' }, [verifierInput]));
    screen.appendChild(verifierGroup);

    // Error message
    var errorEl = el('div', {
      className: 'mojito-returns-error',
      id: 'mojito-returns-error',
      role: 'alert',
      'aria-live': 'polite'
    });
    screen.appendChild(errorEl);

    // Submit button
    var submitBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-submit',
      id: 'mojito-returns-submit'
    }, t('submit'));
    screen.appendChild(el('div', { className: 'mojito-returns-submit-wrap' }, [submitBtn]));

    // Policy link
    screen.appendChild(el('a', {
      className: 'mojito-returns-policy-link',
      href: '/policies/refund-policy',
      target: '_blank',
      rel: 'noopener'
    }, t('policyLink')));

    app.appendChild(screen);

    // ---- Event handlers ----

    identifierToggle.addEventListener('click', function (e) {
      var btn = e.target.closest('.mojito-returns-toggle-btn');
      if (!btn || btn.classList.contains('is-active')) return;
      var value = btn.getAttribute('data-value');
      state.identifierType = value;
      btnOrder.classList.toggle('is-active', value === 'order');
      btnTracking.classList.toggle('is-active', value === 'tracking');
      btnOrder.setAttribute('aria-pressed', value === 'order' ? 'true' : 'false');
      btnTracking.setAttribute('aria-pressed', value === 'tracking' ? 'true' : 'false');
      identifierInput.value = '';
      identifierInput.classList.remove('has-error');
      identifierInput.placeholder = value === 'order' ? t('orderPlaceholder') : t('trackingPlaceholder');
      identifierInput.setAttribute('aria-label', value === 'order' ? t('orderNumber') : t('trackingNumber'));
      hideError();
    });

    verifierToggle.addEventListener('click', function (e) {
      var btn = e.target.closest('.mojito-returns-toggle-btn');
      if (!btn || btn.classList.contains('is-active')) return;
      var value = btn.getAttribute('data-value');
      state.verifierType = value;
      btnEmail.classList.toggle('is-active', value === 'email');
      btnZip.classList.toggle('is-active', value === 'zip');
      btnEmail.setAttribute('aria-pressed', value === 'email' ? 'true' : 'false');
      btnZip.setAttribute('aria-pressed', value === 'zip' ? 'true' : 'false');
      verifierInput.value = '';
      verifierInput.classList.remove('has-error');
      verifierInput.type = value === 'email' ? 'email' : 'text';
      verifierInput.placeholder = value === 'email' ? t('emailPlaceholder') : t('zipPlaceholder');
      verifierInput.setAttribute('aria-label', value === 'email' ? t('email') : t('zipCode'));
      hideError();
    });

    submitBtn.addEventListener('click', function () {
      handleSubmit(identifierInput, verifierInput, submitBtn, errorEl, app);
    });

    identifierInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); handleSubmit(identifierInput, verifierInput, submitBtn, errorEl, app); }
    });
    verifierInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); handleSubmit(identifierInput, verifierInput, submitBtn, errorEl, app); }
    });

    identifierInput.addEventListener('input', function () { identifierInput.classList.remove('has-error'); hideError(); });
    verifierInput.addEventListener('input', function () { verifierInput.classList.remove('has-error'); hideError(); });
  }

  /* ========== Error display ========== */

  function showError(errorEl, message) {
    errorEl.textContent = message;
    errorEl.classList.add('is-visible');
  }

  function hideError() {
    var errorEl = document.getElementById('mojito-returns-error');
    if (errorEl) {
      errorEl.classList.remove('is-visible');
      errorEl.textContent = '';
    }
  }

  /* ========== Submit handler ========== */

  function handleSubmit(identifierInput, verifierInput, submitBtn, errorEl, app) {
    if (state.loading) return;

    var identifier = identifierInput.value.trim();
    var verifier = verifierInput.value.trim();

    var hasError = false;
    if (!identifier) { identifierInput.classList.add('has-error'); hasError = true; }
    if (!verifier) { verifierInput.classList.add('has-error'); hasError = true; }
    if (hasError) { showError(errorEl, t('errorEmpty')); return; }

    state.loading = true;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '';
    submitBtn.appendChild(el('span', { className: 'mojito-returns-spinner' }));
    submitBtn.appendChild(document.createTextNode(' ' + t('submitting')));
    hideError();

    var config = getConfig(app);

    apiFetch(config.apiEndpoint + '/api/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        identifier: identifier,
        identifierType: state.identifierType,
        verifier: verifier,
        verifierType: state.verifierType,
        // Capability flag: this build understands selectable final-sale
        // items (final_sale field). Without it the worker keeps the legacy
        // blocked shape so old cached builds stay safe.
        fsv: 1
      })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, status: res.status, data: data };
        });
      })
      .then(function (result) {
        state.loading = false;
        resetSubmitBtn(submitBtn);

        if (result.status === 429) {
          showError(errorEl, t('errorRateLimit'));
          return;
        }

        if (!result.ok || !result.data.success) {
          showError(errorEl, result.data.message || t('errorNotFound'));
          return;
        }

        state.order = result.data.order;
        state.authToken = result.data.token || null;
        state.eligible = result.data.eligible;
        state.eligibilityReason = result.data.reason || null;
        state.customerHistory = result.data.customerHistory || null;
        state.screen = 'chat';
        trackEvent('order_lookup', { success: true, orderName: result.data.order.name });
        transitionToChat(app);
      })
      .catch(function (err) {
        state.loading = false;
        resetSubmitBtn(submitBtn);
        if (err.message === 'timeout') {
          showError(errorEl, t('errorTimeout'));
        } else {
          showError(errorEl, t('errorNetwork'));
        }
      });
  }

  function resetSubmitBtn(submitBtn) {
    submitBtn.disabled = false;
    submitBtn.textContent = t('submit');
  }

  /* ========== Screen Transitions ========== */

  function transitionToChat(app) {
    var screen = app.querySelector('.mojito-returns-screen');
    if (screen) {
      screen.classList.add('is-exiting');
      setTimeout(function () { renderChatScreen(app); }, 300);
    } else {
      renderChatScreen(app);
    }
  }

  function transitionToLookup(app) {
    var screen = app.querySelector('.mojito-returns-chat');
    if (screen) {
      screen.classList.add('is-exiting');
      setTimeout(function () { resetChatState(); state.screen = 'lookup'; renderLookupScreen(app); }, 300);
    } else {
      resetChatState(); state.screen = 'lookup'; renderLookupScreen(app);
    }
  }

  function resetChatState() {
    state.selectedItems = [];
    state.itemUnits = {};
    state.stockShortages = null;
    state.stockShortMessage = null;
    // Phase 2.9: the walk-through belongs to the ORDER being left. Left in
    // place, exchangeQueue === [] (truthy) kept isInItemQueueMode() true
    // across the next lookup and drainedQueueTarget() resolved the previous
    // order's decided line as the commit target for a fresh pick. null, not
    // [], is what ends queue mode — the same set the success reset clears.
    state.exchangeQueue = null;
    state.exchangeSelections = null;
    state.returnSelections = null;
    state.currentExchangeItemId = null;
    state.activeReason = null;
    state.showReturnConfirm = false;
    state.lastExchangeContext = null;
    state.lastRecommendations = null;
    state.currentProposedProductId = null;
    state.messages = [];
    state.chatLoading = false;
    state.activeAction = null;
    state.streamingNode = null;
    // Drop the order-bound token too; a fresh lookup mints a new one.
    state.authToken = null;
    // Product facts belong to the previous order — refetch next session.
    state.productInfoContext = null;
    state.productInfoLoading = false;
    // 2026-10-13 — mixed flow. These leaked into the next lookup: the pitch
    // counters were reset only by the success modal, and reasonDetails never.
    // stepSeq is NOT reset — it is monotonic, so a control left over from the
    // previous order can never match a step of this one.
    state.itemPlan = {};
    state.planDraft = null;
    state.stepChanging = false;
    state.returnIntentCount = 0;
    state.hasPitchedExchange = false;
    state.priceOpen = false;
    state.reasonDetails = {};
    state.pendingRecommendationsRender = false;
    state.originalItemPrice = null;
    state.originalItemDiscountRate = 0;
    state.originalItemProductId = null;
    state.singleLineId = null;
    // Fix round 1: a kit fetch or a reply still in flight for the session
    // being left goes stale here — it must not write into the next lookup's
    // state (cards, history, context). stepSeq bumps too, so its controls
    // and its bound turns can never match a step of the next order.
    state.chatGen++;
    state.stepSeq++;
  }

  /* ========== Chat Screen ========== */

  function renderChatScreen(app) {
    app.innerHTML = '';
    var order = state.order;
    if (!order) return;
    var config = getConfig(app);

    var chat = el('div', { className: 'mojito-returns-screen mojito-returns-chat' });

    // Top bar
    var topbar = el('div', { className: 'mojito-returns-chat__topbar' }, [
      el('button', {
        type: 'button',
        className: 'mojito-returns-chat__back',
        'aria-label': t('back'),
        onClick: function () { transitionToLookup(app); }
      }, [
        el('span', { className: 'mojito-returns-chat__back-arrow', htmlContent: '&#8592;' }),
        el('span', null, ' ' + t('back'))
      ]),
      el('span', { className: 'mojito-returns-chat__brand' }, t('concierge'))
    ]);
    chat.appendChild(topbar);

    // Scrollable body
    var body = el('div', { className: 'mojito-returns-chat__body' });

    body.appendChild(renderOrderCard(order));

    if (!state.eligible) {
      body.appendChild(el('div', { className: 'mojito-returns-chat__warning' }, state.eligibilityReason || t('outsideWindow')));
    }

    body.appendChild(renderActionCards());

    var messagesContainer = el('div', { className: 'mojito-returns-chat__messages', role: 'log', 'aria-live': 'polite', 'aria-label': 'Chat messages' });
    body.appendChild(messagesContainer);

    chat.appendChild(body);

    // Input bar
    var chatInput = el('input', {
      type: 'text',
      className: 'mojito-returns-chat__input',
      placeholder: t('chatPlaceholder'),
      maxlength: String(config.maxMessageLength),
      'aria-label': t('chatPlaceholder')
    });

    var sendBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-chat__send',
      'aria-label': t('send'),
      onClick: function () { handleChatSend(app); }
    }, [
      el('span', { className: 'mojito-returns-chat__send-icon', htmlContent: '&#10148;' })
    ]);

    chatInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); handleChatSend(app); }
    });

    chat.appendChild(el('div', { className: 'mojito-returns-chat__input-bar' }, [chatInput, sendBtn]));
    app.appendChild(chat);
    // 2026-10-13 — mixed flow: updateActionStates finds its nodes through
    // document.querySelector, so it can only sync the cards' aria-disabled,
    // the hint and the boxes once the screen is in the document.
    updateActionStates();

    // Multi-line orders hear about the mixed case up front. noScroll: the
    // order card is the next thing to touch, and on mobile scrollToBottom
    // used to carry it out of view before the customer had seen it.
    addMessage('assistant', (state.eligible && eligibleLines().length >= 2) ? t('welcomeMessageMulti') : t('welcomeMessage'), { noScroll: true });
  }

  /* ========== Order Card ========== */

  function renderOrderCard(order) {
    var card = el('div', { className: 'mojito-returns-chat__order-card' });

    var headerRow = el('div', { className: 'mojito-returns-chat__order-header' });
    headerRow.appendChild(el('div', { className: 'mojito-returns-chat__order-meta' }, [
      el('span', { className: 'mojito-returns-chat__order-number' }, t('orderLabel') + ' ' + order.name),
      el('span', { className: 'mojito-returns-chat__order-date' }, formatDate(order.created_at))
    ]));
    headerRow.appendChild(el('div', { className: 'mojito-returns-chat__order-meta mojito-returns-chat__order-meta--right' }, [
      el('span', { className: 'mojito-returns-chat__order-total' }, '$' + order.total_price),
      el('span', { className: 'mojito-returns-chat__order-status' }, statusLabel(order.fulfillment_status))
    ]));
    card.appendChild(headerRow);

    var itemsList = el('div', { className: 'mojito-returns-chat__items' });
    order.line_items.forEach(function (item) {
      var isIneligible = item.eligible === false;
      var itemClass = 'mojito-returns-chat__item' + (isIneligible ? ' mojito-returns-chat__item--ineligible' : '');
      var itemRow = el('div', { className: itemClass });

      var checkbox = el('input', {
        type: 'checkbox',
        className: 'mojito-returns-chat__checkbox',
        'data-item-id': String(item.id),
        'aria-label': item.title
      });

      if (isIneligible) {
        checkbox.disabled = true;
      } else {
        checkbox.addEventListener('change', function () {
          handleItemToggle(item.id);
        });
      }
      itemRow.appendChild(checkbox);

      // Thumbnail — request a 200px-wide Shopify CDN variant for performance
      var thumb = el('div', { className: 'mojito-returns-chat__item-thumb' });
      if (item.image) {
        var thumbUrl = item.image.indexOf('?') !== -1
          ? item.image + '&width=200'
          : item.image + '?width=200';
        thumb.style.backgroundImage = 'url(' + thumbUrl + ')';
        thumb.style.backgroundSize = 'cover';
        thumb.style.backgroundPosition = 'center';
      }
      itemRow.appendChild(thumb);

      // Item info. Phase 2.6: the price line shows what the customer ACTUALLY
      // paid (list struck through when they differ) and the count on
      // multi-unit lines — the order card used to print catalog price only,
      // which read high on ~30% of lines and hid the second unit entirely.
      var orderedQty = orderedUnits(item);
      var infoChildren = [
        el('span', { className: 'mojito-returns-chat__item-title' }, item.title),
        el('span', { className: 'mojito-returns-chat__item-variant' }, item.variant_title),
        el('span', { className: 'mojito-returns-chat__item-price' }, priceLineNodes(item, orderedQty))
      ];
      // Partly returned already: say so, so "× 2" can't imply two are still
      // actionable when only one is.
      var stillReturnable = returnableUnits(item);
      if (orderedQty > 1 && stillReturnable > 0 && stillReturnable < orderedQty) {
        infoChildren.push(el('span', { className: 'mojito-returns-chat__item-variant' },
          t('partiallyReturned').replace('{n}', String(orderedQty - stillReturnable))));
      }
      if (isIneligible) {
        // Pick badge label from server-provided reason so "Final Sale" vs
        // "Not yet shipped" vs "Already returned" render differently.
        // (The 'final-sale' fallback only fires against an older worker —
        // the current one keeps final-sale items eligible.)
        var badgeLabel = item.ineligible_reason === 'not-shipped'
          ? t('notShipped')
          : item.ineligible_reason === 'already-returned'
            ? t('alreadyReturned')
            : t('finalSale');
        infoChildren.push(el('span', { className: 'mojito-returns-chat__final-sale-badge' }, badgeLabel));
      } else if (item.final_sale) {
        // Selectable, but exchange / store credit only — badge + hint make
        // the restriction visible before the customer picks a flow.
        infoChildren.push(el('span', { className: 'mojito-returns-chat__final-sale-badge' }, t('finalSale')));
        infoChildren.push(el('span', { className: 'mojito-returns-chat__item-variant' }, t('finalSaleHint')));
      }

      // Phase 2.7 — units picker ON the order card, where the customer first
      // meets the line. Before this it only existed in the final review
      // modal, so someone who bought two and wanted one back had no way to
      // say so until after the whole conversation, and every step in between
      // — the AI's context, the exchange price difference — assumed one unit.
      // Only rendered when more than one unit is still returnable: a
      // single-unit line has nothing to choose. Stepping it also ticks the
      // checkbox, so a count can never be set on an unselected row.
      if (!isIneligible && stillReturnable > 1) {
        infoChildren.push(buildUnitsPicker({
          prefix: 'mojito-returns-chat',
          labelText: t('qtyLabel'),
          ariaContext: item.title,
          max: stillReturnable,
          get: function () { return chosenUnits(item.id); },
          set: function (n) { setChosenUnits(item.id, n); },
          onChange: function () {
            // State first, DOM second — and through isItemSelected, which is
            // the only membership test that survives the return-confirm modal
            // rewriting these ids as strings. A raw indexOf here would push a
            // duplicate numeric id alongside the existing string one.
            checkbox.checked = true;
            if (!isItemSelected(item.id)) state.selectedItems.push(item.id);
            // 2026-10-13 — mixed flow: a line already decided (the walk-
            // through has drained) keeps ONE count — the selection follows the
            // picker, as the review modal's picker mirrors back the other way.
            // An open plan re-renders its "×n" / "Applies to all n units".
            // The buttons are disabled while a walk-through is running.
            var sel = selectionFor(item.id);
            if (sel) sel.quantity = chosenUnits(item.id);
            if (state.planDraft) refreshPlanDraft();
            updateActionStates();
          }
        }));
      }

      itemRow.appendChild(el('div', { className: 'mojito-returns-chat__item-info' }, infoChildren));

      itemsList.appendChild(itemRow);
    });
    card.appendChild(itemsList);

    return card;
  }

  /* ========== Item Selection ========== */

  function handleItemToggle(itemId) {
    // 2026-10-13 — mixed flow: the order card is locked while a walk-through
    // runs (updateActionStates disables the boxes). Defensive: put the box
    // back to what state says and do nothing.
    if (walkthroughActive() || state.stepChanging) { updateActionStates(); return; }
    // Phase 2.7: compare as strings. state.selectedItems arrives here holding
    // numbers from this checkbox but STRINGS once the return-confirm modal has
    // committed its working set, and a raw indexOf missed those — unticking a
    // box then pushed a duplicate instead of removing the item, leaving the
    // row visually unselected while state still counted it twice.
    var idStr = String(itemId);
    var idx = -1;
    for (var i = 0; i < state.selectedItems.length; i++) {
      if (String(state.selectedItems[i]) === idStr) { idx = i; break; }
    }
    if (idx === -1) {
      state.selectedItems.push(itemId);
    } else {
      state.selectedItems.splice(idx, 1);
    }
    // 2026-10-13 — mixed flow: decisions stay a subset of the ticked lines.
    // Unticking a line that was already decided (after the walk-through
    // drained) drops that decision through the single writer, and says so —
    // a silent drop would only surface as a missing row in the review.
    if (idx !== -1 && decisionOf(itemId)) {
      commitSelection(itemId, 'none');
      delete state.itemPlan[idStr];
      if (state.reasonDetails) delete state.reasonDetails[idStr];
      addMessage('assistant', t('itemRemoved').replace('{item}', function () { return lineLabel(itemId); }));
      if (itemDecisionsCount() > 0) {
        // Fix round 1: not under an open plan — its decided rows already
        // show those lines, and a review reopened from here could start a
        // walk-through beneath the live plan.
        if (!state.planDraft) showReopenReviewPrompt();
      } else {
        document.querySelectorAll('.mojito-returns-chat__reopen-review').forEach(function (n) { n.remove(); });
        // Nothing decided is left: end the empty queue, or a later
        // single-path "Return This Item" would fall into the drained-queue
        // branch with nothing to resolve.
        if (isInItemQueueMode() && !walkthroughActive()) clearQueueFields();
      }
    }
    if (state.planDraft) refreshPlanDraft();
    updateActionStates();
  }

  /**
   * 2026-10-13 — mixed flow: the ONE sync point for the action cards, the
   * order card and the hint under the cards. Replaces the CSS-only fake
   * disable (pointer-events) with real aria-disabled — a click on a
   * "disabled" card still reaches handleAction, which answers with a hint.
   * Locks the boxes and the units pickers while a walk-through runs (each
   * qty button gets its own limit back on unlock, the setBusy pattern), and
   * re-syncs every box from state so a programmatic selection change can
   * never leave the tick lying.
   */
  function updateActionStates() {
    var locked = walkthroughActive() || !!state.stepChanging;
    var pending = state.order ? pendingIds() : [];
    var decided = itemDecisionsCount();
    var startable = !locked && state.eligible && pending.length > 0;
    [['exchange', startable], ['return', startable], ['question', !locked]].forEach(function (p) {
      var b = document.querySelector('.mojito-returns-chat__action--' + p[0]);
      if (!b) return;
      b.classList.toggle('is-disabled', !p[1]);
      b.setAttribute('aria-disabled', p[1] ? 'false' : 'true');
    });
    var card = document.querySelector('.mojito-returns-chat__order-card');
    if (card) {
      card.classList.toggle('is-locked', locked);
      card.querySelectorAll('.mojito-returns-chat__checkbox').forEach(function (cb) {
        var id = cb.getAttribute('data-item-id');
        var li = orderLineItem(id);
        cb.checked = isItemSelected(id);
        cb.disabled = !li || li.eligible === false || locked;
      });
      card.querySelectorAll('.mojito-returns-chat__qty-btn').forEach(function (b) {
        b.disabled = locked || b.dataset.atLimit === '1';
      });
    }
    // The hint sits where a split request is born: two lines ticked, one
    // line ticked after earlier decisions, or one line on a multi-line order.
    var hint = document.querySelector('.mojito-returns-chat__actions-hint');
    if (hint) {
      // Fix round 1: no "tick another one now" while a single path is
      // running — the customer is mid-conversation about that one line.
      // Fix round 2: a path started by typing (no card) pins its line with
      // activeAction still null, and runs all the same — only Question is out.
      var singleRunning = decided === 0 && state.singleLineId != null
        && isItemSelected(state.singleLineId)
        && state.activeAction !== 'question';
      var key = null;
      if (locked) key = 'hintLocked';
      else if (!state.planDraft && state.eligible) {
        if (pending.length >= 2) key = 'hintMulti';
        else if (pending.length === 1 && decided > 0) key = 'hintAppend';
        else if (pending.length === 1 && eligibleLines().length >= 2 && !singleRunning) key = 'hintAddMore';
      }
      hint.hidden = !key;
      if (key) hint.textContent = t(key);
    }
  }

  // state.selectedItems holds line ids that arrive as NUMBERS from the order
  // card and as STRINGS from the return-confirm modal's working set, so every
  // membership test has to compare as strings — a raw indexOf silently
  // reported "nothing selected" once that modal had committed.
  function isItemSelected(itemId) {
    var idStr = String(itemId);
    return (state.selectedItems || []).some(function (id) { return String(id) === idStr; });
  }

  function getSelectedItemNames() {
    var order = state.order;
    if (!order) return [];
    return order.line_items
      .filter(function (item) { return isItemSelected(item.id); })
      .map(function (item) {
        // Phase 2.7: the opening message the customer "says" carries the
        // count, so the AI is working on two shirts from its first turn.
        var units = chosenUnits(item.id);
        return item.title + ' (' + item.variant_title + ')'
          + (units > 1 ? ' ×' + units : '');
      });
  }

  function getSelectedItems() {
    var order = state.order;
    if (!order) return [];
    return order.line_items
      .filter(function (item) { return isItemSelected(item.id); });
  }

  /**
   * True when the line item is tagged final-sale (server-provided flag).
   * Final-sale items are selectable but resolve to exchange or STORE
   * CREDIT only — every surface that promises a refund must check this.
   */
  function isFinalSaleItem(itemId) {
    if (!state.order || !Array.isArray(state.order.line_items)) return false;
    var item = state.order.line_items.find(function (i) {
      return String(i.id) === String(itemId);
    });
    return !!(item && item.final_sale);
  }

  /**
   * Returns the line_item the EXCHANGE flow is currently working on. In
   * single-item mode this is just the selected item. In multi-item queue mode
   * it's whichever item is at the head of the queue. Every exchange-flow
   * path (reason-click, freeform chat, modal, createExchange) should use this
   * so the queue stays consistent.
   */
  function getExchangeContextItem() {
    if (state.currentExchangeItemId && state.order) {
      var found = state.order.line_items.find(function (i) {
        return String(i.id) === String(state.currentExchangeItemId);
      });
      if (found) return found;
    }
    // Phase 2.9: a drained queue used to fall straight through to the first
    // ticked item, so every search after a sold-out rejection ran for the
    // wrong line. Prefer the line the last submit flagged.
    var drained = drainedQueueTarget(true);
    if (drained != null) {
      var flagged = orderLineItem(drained);
      if (flagged) return flagged;
    }
    // 2026-10-13 (fix round 1): outside a queue, the single path's pinned
    // line (while still ticked) — not whichever line sits first.
    if (!isInItemQueueMode()) return singlePathLine();
    var selected = getSelectedItems();
    return selected.length > 0 ? selected[0] : null;
  }

  /** String id of a selection entry — the one key both selection arrays share. */
  function selItemId(sel) {
    if (!sel) return '';
    var id = sel.originalItemId != null ? sel.originalItemId : (sel.originalItem && sel.originalItem.id);
    return id == null ? '' : String(id);
  }

  /**
   * Phase 2.9 — the lines whose replacement the LAST submit found short,
   * derived from the worker's shortages against the CURRENT selections: the
   * worker's own rule (demand summed per replacement variant vs. what it has)
   * applied to what would be submitted now. So when two rows share a
   * replacement with one unit and the customer changes one of them, the
   * other stops being flagged by itself; lowering a picker clears it too.
   * The worker's `available` is a snapshot from the rejection — every other
   * server answer clears it.
   */
  function stockShortIds() {
    var shs = state.stockShortages;
    if (!shs || !shs.length) return [];
    var availByVar = {};
    shs.forEach(function (sh) {
      if (!sh || sh.newVariantId == null) return;
      var a = Number(sh.available);
      availByVar[String(sh.newVariantId)] = isFinite(a) ? a : 0;
    });
    var xs = state.exchangeSelections || [];
    var ids = [];
    Object.keys(availByVar).forEach(function (vid) {
      var rows = xs.filter(function (sel) {
        return !!(sel && sel.recommendation && String(sel.recommendation.variant_id) === vid);
      });
      if (!rows.length) return;
      var demand = 0;
      rows.forEach(function (sel) { demand += selQuantity(sel); });
      if (demand <= availByVar[vid]) return;
      rows.forEach(function (sel) {
        var id = selItemId(sel);
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
    });
    return ids;
  }

  /**
   * Phase 2.9 — which decided line a fresh pick belongs to once the queue has
   * drained (every item decided, review modal dismissed). Before this the
   * pick landed on state.selectedItems[0]: the review modal's "choose a
   * different size or color" advice sent the customer back into chat, and
   * the item they then chose silently REPLACED the first line's decision
   * while the sold-out row kept its dead variant — the next submit failed
   * the same way. Candidates are the lines the last submit flagged short;
   * failing that, every decided line. Unambiguous only when there is exactly
   * one — forContext relaxes that to "the first flagged line", which is good
   * enough for steering a search but never for committing a decision.
   */
  function drainedQueueTarget(forContext) {
    if (!isInItemQueueMode() || state.currentExchangeItemId) return null;
    if (state.exchangeQueue.length > 0) return null;
    // Only lines of the order on screen can be targets — a selection left
    // over from a previous lookup must never receive a pick.
    var decided = state.exchangeSelections.map(selItemId)
      .concat(state.returnSelections.map(selItemId))
      .filter(function (id) { return id && !!orderLineItem(id); });
    var shorts = stockShortIds().filter(function (id) { return decided.indexOf(id) !== -1; });
    if (shorts.length === 1 || (forContext && shorts.length > 1)) return shorts[0];
    if (shorts.length === 0 && decided.length === 1) return decided[0];
    return null;
  }

  /* ========== Action Cards ========== */

  function renderActionCards() {
    var container = el('div', { className: 'mojito-returns-chat__actions' });

    var row1 = el('div', { className: 'mojito-returns-chat__actions-row' });

    // 2026-10-13 — mixed flow: nothing is ticked at render, so Exchange and
    // Return start disabled — a real aria-disabled, not just a look. The
    // click still reaches handleAction, which answers with a hint;
    // updateActionStates (run once the screen is mounted) keeps both in sync.
    var exchangeBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-chat__action mojito-returns-chat__action--exchange is-disabled',
      'aria-disabled': 'true',
      onClick: function () { handleAction('exchange'); }
    }, [
      el('span', { className: 'mojito-returns-chat__action-icon', htmlContent: '&#8634;' }),
      el('span', { className: 'mojito-returns-chat__action-label' }, t('exchangeAction')),
      el('span', { className: 'mojito-returns-chat__action-desc' }, t('exchangeDesc'))
    ]);

    var questionBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-chat__action mojito-returns-chat__action--question',
      onClick: function () { handleAction('question'); }
    }, [
      el('span', { className: 'mojito-returns-chat__action-icon', htmlContent: '&#63;' }),
      el('span', { className: 'mojito-returns-chat__action-label' }, t('questionAction')),
      el('span', { className: 'mojito-returns-chat__action-desc' }, t('questionDesc'))
    ]);

    row1.appendChild(exchangeBtn);
    row1.appendChild(questionBtn);
    container.appendChild(row1);

    var returnBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-chat__action mojito-returns-chat__action--return is-disabled',
      'aria-disabled': 'true',
      onClick: function () { handleAction('return'); }
    }, [
      el('span', { className: 'mojito-returns-chat__action-icon', htmlContent: '&#8629;' }),
      el('span', { className: 'mojito-returns-chat__action-label' }, t('returnAction')),
      el('span', { className: 'mojito-returns-chat__action-desc' }, t('returnDesc'))
    ]);
    container.appendChild(returnBtn);

    // 2026-10-13 — mixed flow: the hint slot under the cards. Text and
    // visibility belong to updateActionStates.
    var hint = el('p', { className: 'mojito-returns-chat__actions-hint' });
    hint.hidden = true;
    container.appendChild(hint);

    return container;
  }

  /* ========== Build Order Context for AI ========== */

  function buildOrderContext() {
    var order = state.order;
    if (!order) return '';
    var lines = [
      'Order ' + order.name + ', placed ' + formatDate(order.created_at),
      'Total: $' + order.total_price,
      'Status: ' + statusLabel(order.fulfillment_status)
    ];
    // 2026-10-13 — mixed flow: the AI is told which item the walk-through is
    // on, what each item's plan is, and which are settled. The CURRENT ITEM
    // header sits ahead of the item list so the worker's 2500-char cap can
    // never cut it, and it rides every turn, so it outlives the 20-message
    // history window. No '<' or '>' anywhere: the worker strips both.
    var inQueue = isInItemQueueMode();
    var curId = walkthroughActive() ? state.currentExchangeItemId : null;
    // Fix round 2: outside a queue too, the AI is told which lines are
    // already in the request (a single-path decision) and which ticked line
    // still waits for Exchange or Return — a plain turn in the append state
    // could otherwise answer as though the waiting line had been added.
    var decidedOutside = !inQueue && itemDecisionsCount() > 0;
    var waitingIds = appendPending() ? pendingIds().map(String) : [];
    if (curId != null) {
      var curLi = orderLineItem(curId);
      if (curLi) {
        lines.push('CURRENT ITEM: ' + curLi.title + ' (' + curLi.variant_title + ') — PLAN: ' + planWord(curId));
      }
    }
    lines.push('Items:');
    order.line_items.forEach(function (item) {
      var status = '';
      if (item.eligible === false) {
        status = item.ineligible_reason === 'not-shipped'
          ? ' [NOT YET SHIPPED - NOT ELIGIBLE]'
          : item.ineligible_reason === 'already-returned'
            ? ' [ALREADY RETURNED - NOT ELIGIBLE]'
            : ' [FINAL SALE - NOT ELIGIBLE]';
      } else if (item.final_sale) {
        status = ' [FINAL SALE - EXCHANGE OR STORE CREDIT ONLY - NO CASH REFUND]';
      }
      // Phase 2.7 — the ordered count, and how many of them the customer has
      // actually asked to send back, ride EVERY chat turn. Without this the
      // AI hunted a replacement for one shirt while the customer was
      // returning two, and never had cause to mention that stock was thinner
      // than the count they'd picked.
      var orderedQty = orderedUnits(item);
      var qtyNote = orderedQty > 1 ? ' — QTY ' + orderedQty + ' ORDERED' : '';
      var pickNote = '';
      if (orderedQty > 1 && isItemSelected(item.id)) {
        // Fix round 2: a decided line's count is the decision's (the
        // review's stock cap can lower it), not the order card's.
        pickNote = ' [CUSTOMER IS SENDING BACK ' + lineUnits(item.id)
          + ' OF ' + returnableUnits(item) + ']';
      }
      var planTag = '';
      if (inQueue) {
        if (decisionOf(item.id)) {
          planTag = ' [DECIDED: ' + planWord(item.id) + ']';
        } else if (curId != null && String(item.id) === String(curId)) {
          planTag = ' [CURRENT ITEM — PLAN: ' + planWord(item.id) + ']';
        } else if ((state.exchangeQueue || []).some(function (q) { return String(q) === String(item.id); })) {
          planTag = ' [UP NEXT — PLAN: ' + planWord(item.id) + ']';
        }
      } else if (decidedOutside && decisionOf(item.id)) {
        planTag = ' [DECIDED: ' + planWord(item.id) + ']';
      }
      if (!planTag && waitingIds.indexOf(String(item.id)) !== -1) {
        planTag = ' [TICKED, NOT YET IN THE REQUEST — the customer taps Exchange or Return above to add it]';
      }
      lines.push('  - ' + item.title + ' (' + item.variant_title + ') — $' + item.price
        + qtyNote + status + pickNote + planTag);
    });
    if (!state.eligible) {
      lines.push('NOTE: This order is outside the 36-day return window.');
    }
    return lines.join('\n');
  }

  /* ========== SSE Streaming Chat ========== */

  // 2026-10-13 (fix round 1): release a reply body nobody will read — a
  // turn from a session already left, or a stalled stream. Both calls can
  // reject (already closed, already cancelled); neither failure matters.
  function cancelBody(res) {
    try {
      var p = (res && res.body && typeof res.body.cancel === 'function') ? res.body.cancel() : null;
      if (p && typeof p.catch === 'function') p.catch(function () { /* already closed */ });
    } catch (e) { /* already closed or locked */ }
  }

  function cancelReader(reader) {
    try {
      var p = (reader && typeof reader.cancel === 'function') ? reader.cancel() : null;
      if (p && typeof p.catch === 'function') p.catch(function () { /* already closed */ });
    } catch (e) { /* already closed */ }
  }

  function sendChatMessage(app, messageText, showButtonsAfter, exchangeContext) {
    // 2026-10-13 (fix round 1): a reply still streaming counts as in flight —
    // chatLoading alone drops to false when its headers land, so a retry
    // (or a late send) could run a second stream beside it.
    if (turnInFlight()) return;

    // 2026-10-13 — mixed flow: bind this turn to the step that was live when
    // it was SENT. Product cards and the PDM are not chatLoading-gated, so
    // stamping at render time alone could bind item 1's reply to item 2. A
    // turn that starts while a step change is pending is stale by definition.
    var turnSeq = state.stepChanging ? -1 : state.stepSeq;
    var turnItemId = isInItemQueueMode() ? state.currentExchangeItemId : null;
    // Fix round 1: and to the chat session. A reply that lands after Back (or
    // after the success reset) belongs to no screen the customer can see:
    // it writes nothing — no history, no cards, no typing state.
    var turnGen = state.chatGen;
    var turnNode = null;   // this turn's streaming bubble, once opened

    var config = getConfig(app);
    var sessionId = getSessionId();

    showTypingIndicator();

    // Build conversation history for the API
    var history = state.messages
      .filter(function (m) { return m.role === 'user' || m.role === 'assistant'; })
      .map(function (m) { return { role: m.role, content: m.text }; });

    // Persist exchange context for follow-up messages
    if (exchangeContext) {
      state.lastExchangeContext = exchangeContext;
    }

    var chatBody = {
      sessionId: sessionId,
      message: messageText,
      orderContext: buildOrderContext(),
      conversationHistory: history
    };
    if (exchangeContext || state.lastExchangeContext) {
      chatBody.exchangeContext = exchangeContext || state.lastExchangeContext;
    }
    // Pass customer purchase history so AI can personalize (e.g. reference
    // previously ordered sizes/colors in recommendations).
    if (state.customerHistory) {
      chatBody.customerHistory = state.customerHistory;
    }
    // Question mode: ride the product facts along so the AI answers care/
    // fit/fabric questions from the store's real copy (token cost only paid
    // in question mode).
    if (state.activeAction === 'question' && state.productInfoContext) {
      chatBody.productContext = state.productInfoContext;
    }

    apiFetch(config.apiEndpoint + '/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(chatBody)
    }, 60000)
      .then(function (res) {
        // Fix round 1: the session this turn was sent in has ended. Its
        // typing dots left with that screen; the ones showing now (if any)
        // belong to the next session's turn and are not this one's to hide.
        if (turnGen !== state.chatGen) {
          cancelBody(res);
          return;
        }
        hideTypingIndicator();

        // 2026-10-13 — mixed flow (fix round 1): a failed reply must not
        // leave its step without a control. The chip row and the step's
        // switch link went when the reason was tapped, and the order card is
        // locked while a walk-through runs — so Retry, as the network catch
        // gives, plus the step's own actions (placeStepActions; they need no
        // AI reply) on a live walk-through step with its reason in.
        function keepStepUsable() {
          if (turnSeq !== state.stepSeq) return;
          addRetryButton(app, messageText, showButtonsAfter, turnSeq);
          var stepLive = turnItemId != null && walkthroughActive()
            && String(turnItemId) === String(state.currentExchangeItemId);
          if (stepLive && state.activeReason != null) placeStepActions(turnSeq);
        }

        if (res.status === 429) {
          addMessage('assistant', t('errorRateLimit'));
          keepStepUsable();
          return;
        }

        // SESSION EXPIRED — the order-bound token is dead (2h TTL) or absent.
        // Use the localized copy, and clear the token so apiFetch stops
        // resending a dead credential; re-lookup mints a fresh one.
        if (res.status === 401) {
          addMessage('assistant', t('sessionExpired'));
          state.authToken = null;
          return;
        }

        if (!res.ok) {
          addMessage('assistant', t('errorNetwork'));
          keepStepUsable();
          return;
        }

        // Create a streaming message bubble
        var messagesEl = document.querySelector('.mojito-returns-chat__messages');
        if (!messagesEl) return;

        var msgNode = el('div', {
          className: 'mojito-returns-chat__message mojito-returns-chat__message--assistant is-entering'
        });
        messagesEl.appendChild(msgNode);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { msgNode.classList.remove('is-entering'); });
        });

        state.streamingNode = msgNode;
        turnNode = msgNode;
        var fullText = '';

        var reader = res.body.getReader();
        var decoder = new TextDecoder();
        var buffer = '';

        // Fix round 1: every new turn now waits for this stream to finish
        // (turnInFlight reads streamingNode), so a body that stalls must not
        // hold streamingNode — and the whole chat with it — forever. No chunk
        // for 45 s: cancel the read and end through the error path below
        // (the partial text is kept, and Retry offered).
        var stallTimer = null;
        var stalled = false;
        function armStallTimer() {
          if (stallTimer) clearTimeout(stallTimer);
          stallTimer = setTimeout(function () {
            stalled = true;
            cancelReader(reader);
          }, 45000);
        }
        function clearStallTimer() {
          if (stallTimer) { clearTimeout(stallTimer); stallTimer = null; }
        }

        function processStream() {
          armStallTimer();
          reader.read().then(function (result) {
            clearStallTimer();
            if (stalled) throw new Error('stalled');
            // Fix round 1: the session was left mid-stream. Stop reading and
            // write nothing — the history and the screen are the next one's.
            if (turnGen !== state.chatGen) {
              cancelReader(reader);
              return;
            }
            if (result.done) {
              // Stream complete — parse all marker protocols out of the final text
              var parsed = parseAIMarkers(fullText);
              // Re-render the message bubble with markers stripped clean.
              // A markers-only reply parses to empty text — drop the bubble
              // entirely rather than leave an empty styled box above the cards.
              // (Fix round 1: this turn's OWN bubble — state.streamingNode is
              // only ever this one now, but never another turn's.)
              if (parsed.text) {
                msgNode.innerHTML = formatMessage(parsed.text);
              } else {
                msgNode.remove();
              }
              // 2026-10-13 — mixed flow: the customer has moved to another
              // walk-through step (or opened the plan) since this turn was
              // sent. Its text stays; it renders no cards and no controls,
              // and it does not count as a pitch for the step now live.
              var stale = turnSeq !== state.stepSeq;
              // Track the pitch/intent state: if AI recommended something
              // (product cards will render OR message mentioned exchange),
              // flag that an exchange has been pitched. Per item: the
              // current line's plan, not the global action.
              if (!stale && (currentIntent() === 'exchange' ||
                  (state.lastRecommendations && state.lastRecommendations.length > 0))) {
                state.hasPitchedExchange = true;
              }
              state.messages.push({ role: 'assistant', text: parsed.text });
              // Fix round 1: chatLoading is NOT cleared here. This turn gave
              // it up when its headers landed; if it is set now, a kit fetch
              // owns it, and clearing it released a step change waiting on
              // afterTurn while that kit was still in flight.
              if (state.streamingNode === msgNode) state.streamingNode = null;
              if (stale) {
                state.pendingRecommendationsRender = false;
                scrollToBottom();
                return;
              }
              var bind = { seq: turnSeq, itemId: turnItemId };

              // Resolve which cards to render this turn — AI markers take priority
              // over the auto-render-on-new-recs flag.
              var cardsRenderedThisTurn = false;

              // 1. [[CARD:variant_id]] — one or more specific variants the AI
              // named. Collect ALL matched recs and render as a single grid so
              // the layout mirrors the default 4-up — not one grid per marker.
              // Track the last-proposed product so subsequent "See Other Colors"
              // requests fetch against the PROPOSED product, not the original.
              // 2XL-incident fix: an emitted id that matches NOTHING means the
              // AI invented it (or referenced a stale batch) — track it so the
              // fallback below surfaces the substitution instead of silently
              // rendering our own recs under the AI's (possibly false) sentence.
              var markerMiss = false;
              var cardRenderSource = 'none';
              if (parsed.cardIds.length > 0 && state.lastRecommendations) {
                var matchedRecs = [];
                parsed.cardIds.forEach(function (vid) {
                  var rec = state.lastRecommendations.find(function (r) {
                    return String(r.variant_id) === String(vid);
                  });
                  if (rec) matchedRecs.push(rec);
                });
                if (matchedRecs.length > 0) {
                  state.currentProposedProductId = matchedRecs[0].product_id;
                  renderProductCards(matchedRecs, matchedRecs.length, bind);
                  cardsRenderedThisTurn = true;
                  cardRenderSource = 'marker';
                } else {
                  markerMiss = true;
                }
              } else if (parsed.cardIds.length > 0) {
                markerMiss = true;
              }

              // 2. [[CARDS]] — re-render all current recommendations
              if (parsed.showAllCards && state.lastRecommendations && state.lastRecommendations.length > 0) {
                if (markerMiss) addMessage('assistant', t('cardMismatchNote'));
                renderProductCards(state.lastRecommendations, undefined, bind);
                cardsRenderedThisTurn = true;
                cardRenderSource = 'cards';
              }

              // 3. Fallback: first render of a fresh recommendation batch.
              // pendingRecommendationsRender is set when the frontend just
              // fetched new recs and wants to show them on the next stream end.
              // 2XL-incident fix: when the AI's card ids matched NOTHING
              // (invented or stale), never substitute our own recs silently
              // under its sentence — say so first, and log every miss with
              // what actually rendered so these incidents become visible.
              if (!cardsRenderedThisTurn
                  && state.pendingRecommendationsRender
                  && state.lastRecommendations
                  && state.lastRecommendations.length > 0) {
                if (markerMiss) addMessage('assistant', t('cardMismatchNote'));
                renderProductCards(state.lastRecommendations, undefined, bind);
                cardsRenderedThisTurn = true;
                cardRenderSource = 'fallback';
              }
              if (markerMiss) {
                trackEvent('card_marker_mismatch', { ids: parsed.cardIds.join(','), rendered: cardRenderSource });
              }
              state.pendingRecommendationsRender = false;

              // 2026-10-13 — mixed flow: a walk-through step gets its own
              // deterministic actions after every reasoned turn — an exchange
              // step a "Return this one instead" link (even when cards
              // rendered, where AI buttons are suppressed), a return step
              // [Return This Item] + "Exchange this one instead". The return
              // footer pre-empts AI buttons; an open plan bubble does too.
              var stepLive = turnItemId != null && walkthroughActive()
                && String(turnItemId) === String(state.currentExchangeItemId);
              var returnFooter = stepLive && state.activeReason != null
                && (intentOf(turnItemId) || 'exchange') === 'return';
              // Preset buttons (reason chips / return confirm) may pre-empt AI buttons
              // Fix round 1: while a newly ticked line waits (appendPending),
              // an AI [[BTN]] could only re-decide the line already on file —
              // suppressed, as under an open plan.
              var hasPresetButtons = !!showButtonsAfter
                || cardsRenderedThisTurn
                || state.showReturnConfirm
                || returnFooter
                || !!state.planDraft
                || appendPending();
              if (showButtonsAfter) {
                showReasonButtons(showButtonsAfter);
              }
              // Show return confirmation button after return reason response
              if (state.showReturnConfirm) {
                renderReturnConfirmButton();
                state.showReturnConfirm = false;
              }
              // AI buttons only if no preset UI is showing
              if (parsed.buttons.length > 0 && !hasPresetButtons) {
                renderAIButtons(app, parsed.buttons, stepLive ? bind : null);
              }
              if (stepLive && state.activeReason != null) placeStepActions(turnSeq);
              // The open plan stays the last thing in the chat.
              if (state.planDraft && state.planDraft.node.parentNode === messagesEl) {
                messagesEl.appendChild(state.planDraft.node);
              }
              scrollToBottom();
              return;
            }

            buffer += decoder.decode(result.value, { stream: true });

            // Parse SSE lines
            var lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (var i = 0; i < lines.length; i++) {
              var line = lines[i].trim();
              if (!line || !line.startsWith('data: ')) continue;
              var data = line.substring(6);
              if (data === '[DONE]') continue;

              try {
                var parsed = JSON.parse(data);
                var token = parsed.choices && parsed.choices[0] && parsed.choices[0].delta && parsed.choices[0].delta.content;
                if (token) {
                  fullText += token;
                  msgNode.innerHTML = formatMessage(fullText);
                  scrollToBottom();
                }
              } catch (e) {
                // Skip unparseable chunks
              }
            }

            processStream();
          }).catch(function () {
            clearStallTimer();
            // Fix round 1: a session already left gets nothing; and, as at
            // the stream end, chatLoading is not this turn's to clear.
            if (turnGen !== state.chatGen) return;
            if (state.streamingNode === msgNode) state.streamingNode = null;
            if (!fullText) {
              msgNode.innerHTML = formatMessage(t('streamError'));
            } else {
              state.messages.push({ role: 'assistant', text: fullText });
            }
            // 2026-10-13: a retry from a step already left would resend in
            // the next item's context.
            if (turnSeq === state.stepSeq) addRetryButton(app, messageText, showButtonsAfter, turnSeq);
          });
        }

        processStream();
      })
      .catch(function (err) {
        // Fix round 1: nothing to say to a session already left. And should
        // the throw have come after the bubble was opened (a reader that
        // would not start), release it — every new turn waits on it.
        if (turnGen !== state.chatGen) return;
        if (turnNode && state.streamingNode === turnNode) state.streamingNode = null;
        hideTypingIndicator();
        if (err.message === 'timeout') {
          addMessage('assistant', t('errorTimeout'));
        } else {
          addMessage('assistant', t('errorNetwork'));
        }
        if (turnSeq === state.stepSeq) addRetryButton(app, messageText, showButtonsAfter, turnSeq);
      });
  }

  /* ========== Retry Button ========== */

  // 2026-10-13 — mixed flow: seq stamps the button with its step, so the
  // next step change retires it with the step's other controls.
  function addRetryButton(app, messageText, showButtonsAfter, seq) {
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;

    var retryBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-chat__retry-btn',
      onClick: function () {
        // Fix round 1: kept until no other turn is running — removed and
        // then refused by sendChatMessage, the retry would just vanish.
        if (turnInFlight() || state.stepChanging) return;
        retryBtn.remove();
        sendChatMessage(app, messageText, showButtonsAfter);
      }
    }, t('retryBtn'));
    if (seq != null && seq >= 0) stamp(retryBtn, seq);

    messagesEl.appendChild(retryBtn);
    scrollToBottom();
  }

  /* ========== Action Handlers ========== */

  function handleAction(action) {
    // 2026-10-13 (fix round 1): turnInFlight, not chatLoading — chatLoading
    // is false while a reply streams, and a turn started then ran beside it.
    if (turnInFlight() || state.stepChanging) return;

    var app = document.getElementById('mojito-returns-app');

    // 2026-10-13 — mixed flow. Exchange/Return now open a per-item plan when
    // 2+ lines are pending, and APPEND a newly ticked line to the decisions
    // already made instead of wiping them (the old reset here is what left
    // the drained-queue dead end behind). One pending line with nothing
    // decided, and Question, keep the single path byte-for-byte.
    if (action !== 'question') {
      // The cards only LOOK disabled for an outside-window order — the
      // keyboard could still reach them.
      if (!state.eligible) {
        addMessage('assistant', state.eligibilityReason || t('outsideWindow'));
        return;
      }
      if (walkthroughActive()) return;
      // The plan is open: the pressed card applies to every row, never wipes.
      if (state.planDraft) { setAllPlanIntents(action); return; }
      if (isInItemQueueMode() && itemDecisionsCount() === 0) clearQueueFields();
      var pending = pendingIds();
      if (pending.length === 0) {
        if (itemDecisionsCount() > 0) { openItemReviewModal(); return; }
        addMessage('assistant', state.selectedItems.length ? t('selectEligibleItem') : t('selectItemFirst'));
        return;
      }
      if (pending.length > 1 || itemDecisionsCount() > 0) {
        trackEvent('action_selected', {
          action: action,
          items: pending.length,
          mode: pending.length > 1 ? (PLAN_ENABLED ? 'plan' : 'queue') : 'append'
        });
        if (pending.length > 1 && PLAN_ENABLED) { openPlanBubble(action, pending); return; }
        var all = {};
        pending.forEach(function (id) { all[String(id)] = action; });
        startPlan(action, pending, all, 0);
        return;
      }
    } else if (walkthroughActive()) {
      // Question mid-walk-through would hijack activeAction; the hint says
      // to finish the item first.
      return;
    }

    // ONE pending line and nothing decided, or Question: today's path. The
    // userMsg strings stay English — the worker's REASON FIRST rule keys on
    // them.
    var userMsg;
    if (action === 'exchange') {
      userMsg = "I'd like to exchange: " + getSelectedItemNames().join(', ');
    } else if (action === 'return') {
      userMsg = "I'd like to return: " + getSelectedItemNames().join(', ');
    } else {
      userMsg = "I have a question about my order";
    }

    state.activeAction = action;
    if (action !== 'question') {
      // Fix round 1: pin the line this single path is about. With a second
      // line ticked mid-path (or after "Yes, submit"), the kit and the inline
      // confirm took the first line in ORDER-CARD order while the commit took
      // the first in TICK order — wrong item named, wrong money quoted.
      state.singleLineId = String(pending[0]);
      updateActionStates();   // the "tick another" hint goes while it runs
    }
    trackEvent('action_selected', { action: action, items: 1, mode: 'single' });
    addMessage('user', userMsg);

    // Question mode: warm the product-facts context in the background so the
    // AI can answer with real product data by the time a topic is picked.
    if (action === 'question') {
      fetchProductInfoContext(app);
    }

    // (2026-10-13: the multi-item seeding that lived here — and wiped every
    // earlier decision — is gone. 2+ pending lines take the plan above; a
    // line ticked after earlier decisions is appended by startPlan.)
    sendChatMessage(app, userMsg, action);
  }

  /**
   * True when we're iterating multi-item queue (exchange OR return action).
   * Several callers (Confirm Return button, return-intent detection,
   * createExchange) branch on this to route to the right queue action.
   */
  function isInItemQueueMode() {
    return !!(state.exchangeQueue && state.exchangeSelections && state.returnSelections);
  }

  /* ---------- 2026-10-13 — mixed flow: building blocks ----------
     Per-item intent (itemPlan), step identity (stepSeq / data-step) and the
     order-card lock. A walk-through is ACTIVE while an item is current or
     queued; a drained queue (every line decided, review dismissed) is still
     queue mode but not active. */

  function walkthroughActive() {
    return isInItemQueueMode() && (state.exchangeQueue.length > 0 || state.currentExchangeItemId != null);
  }

  /** 'exchange' | 'return' when the line is already decided, else null. */
  function decisionOf(id) {
    var k = String(id);
    if ((state.exchangeSelections || []).some(function (s) { return selItemId(s) === k; })) return 'exchange';
    if ((state.returnSelections || []).some(function (s) { return selItemId(s) === k; })) return 'return';
    return null;
  }

  /** The line's selection entry from either array, or null. */
  function selectionFor(id) {
    var k = String(id);
    var hit = (state.exchangeSelections || []).find(function (s) { return selItemId(s) === k; });
    if (hit) return hit;
    return (state.returnSelections || []).find(function (s) { return selItemId(s) === k; }) || null;
  }

  /** The reason already on file for a decided line — re-deciding keeps it. */
  function priorReason(id) {
    var s = selectionFor(id);
    return s && s.reason ? s.reason : null;
  }

  function intentOf(id) {
    return (id != null && state.itemPlan && state.itemPlan[String(id)]) || null;
  }

  /** The intent that governs the CURRENT turn: the walk-through item's plan, else the action. */
  function currentIntent() {
    return (isInItemQueueMode() && state.currentExchangeItemId != null)
      ? (intentOf(state.currentExchangeItemId) || state.activeAction)
      : state.activeAction;
  }

  /** EXCHANGE | RETURN | STORE CREDIT (= return on a final-sale line) — the AI's plan tags. */
  function planWord(id) {
    var i = decisionOf(id) || intentOf(id) || 'exchange';
    return i === 'exchange' ? 'EXCHANGE' : (isFinalSaleItem(id) ? 'STORE CREDIT' : 'RETURN');
  }

  /** Lines that can still be sent back at all. */
  function eligibleLines() {
    if (!state.order || !Array.isArray(state.order.line_items)) return [];
    return state.order.line_items.filter(function (li) {
      return li.eligible !== false && returnableUnits(li) > 0;
    });
  }

  /**
   * Ticked, eligible lines with no decision yet — in ORDER-CARD order (the
   * old queue used tick order), so "Item 1 of 2" is the upper row.
   */
  function pendingIds() {
    return getSelectedItems().filter(function (li) {
      return li.eligible !== false && returnableUnits(li) > 0 && !decisionOf(li.id);
    }).map(function (li) { return li.id; });
  }

  /** "Title (Variant) ×n" — the format getSelectedItemNames uses. */
  function lineLabel(id) {
    var li = orderLineItem(id);
    if (!li) return 'Item';
    var n = chosenUnits(id);
    return (li.title || 'Item') + (li.variant_title ? ' (' + li.variant_title + ')' : '')
      + (n > 1 ? ' ×' + n : '');
  }

  /** null, not [], is what ends queue mode (see resetChatState). */
  function clearQueueFields() {
    state.exchangeQueue = null;
    state.exchangeSelections = null;
    state.returnSelections = null;
    state.currentExchangeItemId = null;
    state.itemPlan = {};
    state.singleLineId = null;
  }

  /**
   * 2026-10-13 — mixed flow (fix round 1): the line the single path works
   * on — the pinned line while it is still ticked, else the first ticked
   * line (the old behaviour, and the only one when one line is ticked).
   */
  function singlePathLine() {
    if (state.singleLineId != null && isItemSelected(state.singleLineId)) {
      var pinned = orderLineItem(state.singleLineId);
      if (pinned) return pinned;
    }
    var selected = getSelectedItems();
    return selected.length > 0 ? selected[0] : null;
  }

  /**
   * 2026-10-13 — mixed flow (fix round 2): pin the single path's line
   * wherever a single-path conversation starts or fetches — a reason chip,
   * a typed turn's kit, the question-mode exit, the return modal's Exchange
   * Instead — not only from the Exchange/Return card. Started by typing, the
   * path never pinned: a line ticked mid-path became singlePathLine, and the
   * inline confirm and "Yes, submit" committed the replacement chosen for
   * one shirt onto the other, at the other's price. A pin still ticked is
   * kept; outside the single path (queue mode) this does nothing. Re-syncs
   * the cards so the "tick another one now" hint goes while the path runs.
   */
  function pinSingleLine(item) {
    if (isInItemQueueMode()) return;
    if (item && item.id != null && isItemSelected(item.id)
        && (state.singleLineId == null || !isItemSelected(state.singleLineId))) {
      state.singleLineId = String(item.id);
    }
    updateActionStates();
  }

  /**
   * 2026-10-13 — mixed flow (fix round 1): decisions exist (a single-path
   * "Yes, submit" or a drained walk-through) and a newly ticked line waits
   * for Exchange or Return. Typed turns, AI buttons and the return fallbacks
   * must not run the single path then: they would re-decide the line
   * already on file (or file the new one under its reason).
   */
  function appendPending() {
    return !walkthroughActive() && !state.planDraft && itemDecisionsCount() > 0
      && pendingIds().length > 0;
  }

  /**
   * A reply or a kit fetch is still running. chatLoading drops to false the
   * moment the response headers land (hideTypingIndicator) and stays false
   * while the body streams, so the stream itself is read off streamingNode.
   */
  function turnInFlight() {
    return !!state.chatLoading || !!state.streamingNode;
  }

  /**
   * 2026-10-13 — mixed flow (fix round 1): an exchange-kit fetch (inventory /
   * recommendations, then a chat turn) is bound to the step and the chat
   * session it STARTED under. Card picks and the PDM are not gated on a
   * turn, so a step can be left while a kit is still in flight — and the kit
   * used to land on the next step anyway: item 1's variants written into
   * lastRecommendations and sent (and rendered, and committable) as item 2's
   * replacements, or into the next lookup after Back. Take the token AFTER
   * the kit's showTypingIndicator; every .then/.catch checks kitLive first,
   * and a stale kit only calls kitDrop: no state write, no send.
   */
  function kitToken() {
    return { seq: state.stepSeq, gen: state.chatGen, typing: state.typingGen };
  }

  function kitLive(tok) {
    return tok.seq === state.stepSeq && tok.gen === state.chatGen;
  }

  /**
   * A stale kit lets go of the typing dots — which is what releases a step
   * change waiting on afterTurn — but only while they are still its own: a
   * newer turn's (or a new session's) indicator stays up.
   */
  function kitDrop(tok) {
    if (tok.gen === state.chatGen && state.chatLoading && state.typingGen === tok.typing) {
      hideTypingIndicator();
    }
  }

  /**
   * Run fn once the in-flight turn has finished (sendQuestionTopic's poll):
   * every 150 ms, up to 70 s, then regardless. Dropped if the customer has
   * left for another order — or ended the chat session — meanwhile.
   * 2026-10-13 — mixed flow (fix round 2): the cap was 15 s, shorter than a
   * healthy turn can run (kit fetches up to 20 s, then a stream the stall
   * timer only cancels after 45 s), so a deferred step change or intent
   * switch fired under a reply still streaming and half-applied. 70 s
   * outlasts both; callers that must never act mid-turn re-check anyway.
   */
  function afterTurn(fn) {
    var orderRef = state.order;
    var genRef = state.chatGen;
    var waited = 0;
    (function attempt() {
      if (state.order !== orderRef || state.chatGen !== genRef) return;
      if (turnInFlight() && waited < 70000) {
        waited += 150;
        setTimeout(attempt, 150);
        return;
      }
      fn();
    })();
  }

  function stamp(node, seq) {
    node.setAttribute('data-step', String(seq != null ? seq : state.stepSeq));
    return node;
  }

  function removeStepNodes(sel, seq) {
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;
    messagesEl.querySelectorAll(sel + '[data-step="' + seq + '"]').forEach(function (n) { n.remove(); });
  }

  // Controls that belong to ONE step. Unstamped single-path controls count
  // too: entering a walk-through retires them.
  var STEP_SCOPED = '.mojito-returns-chat__buttons, .mojito-returns-chat__step-actions, '
    + '.mojito-returns-chat__ai-buttons, .mojito-returns-chat__item-prompt-switch, '
    + '.mojito-returns-chat__which-item, .mojito-returns-chat__retry-btn, '
    + '.mojito-returns-chat__confirm-wrapper, .mojito-returns-chat__confirm-preview, '
    + '.mojito-returns-chat__confirm-summary, .mojito-returns-chat__reopen-review';

  /**
   * The step being left can never act on the next one: its controls go, its
   * product cards stay visible as history but dimmed and inert.
   */
  function retireStepControls() {
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;
    var cur = String(state.stepSeq);
    messagesEl.querySelectorAll(STEP_SCOPED).forEach(function (n) {
      if (n.getAttribute('data-step') !== cur) n.remove();
    });
    messagesEl.querySelectorAll('.mojito-returns-chat__product-cards').forEach(function (c) {
      if (c.getAttribute('data-step') === cur) return;
      c.classList.add('is-retired');
      c.querySelectorAll('button').forEach(function (b) {
        b.disabled = true;
        b.setAttribute('aria-disabled', 'true');
      });
    });
  }

  /** Scroll back up to the order card and put focus on one line's box. */
  function revealOrderCard(id) {
    setTimeout(function () {
      var body = document.querySelector('.mojito-returns-chat__body');
      if (body) {
        try { body.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { body.scrollTop = 0; }
      }
      var cb = document.querySelector('.mojito-returns-chat__checkbox[data-item-id="' + String(id) + '"]');
      if (cb) { try { cb.focus({ preventScroll: true }); } catch (e) { /* older engines */ } }
    }, 150);
  }

  /** "1 to exchange · 1 to return · 1 for store credit" — non-zero parts only. */
  function countsText(ex, rt, cr) {
    var parts = [];
    if (ex) parts.push(t('countExchange').replace('{n}', String(ex)));
    if (rt) parts.push(t('countReturn').replace('{n}', String(rt)));
    if (cr) parts.push(t('countCredit').replace('{n}', String(cr)));
    return parts.join(' · ');
  }

  /* ---------- 2026-10-13 — mixed flow: the plan bubble ----------
     Asked in the concierge's voice, right where the customer is: one row per
     pending line with an Exchange | Return toggle (Store credit on a final-
     sale line), one Continue. Presets follow the card that was pressed;
     Exchange is always the LEFT segment. aria-live=off and in-place repaints,
     so a screen reader does not re-announce the whole bubble on every tap. */

  function openPlanBubble(preset, ids) {
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;
    state.stepSeq++;
    retireStepControls();
    // The single path's pitch kit belongs to the line it was fetched for.
    // Left in place, a typed turn while the plan is open resends it and can
    // render cards that would commit onto selectedItems[0] — or, through a
    // leftover showReturnConfirm, a single-path "Confirm Return" button.
    state.lastExchangeContext = null;
    state.lastRecommendations = null;
    state.currentProposedProductId = null;
    state.pendingRecommendationsRender = false;
    state.showReturnConfirm = false;
    state.singleLineId = null;   // fix round 1: the single path is over

    var titleId = 'mojito-returns-plan-title-' + state.stepSeq;
    var list = el('ul', { className: 'mojito-returns-chat__plan-list' });
    var summaryEl = el('span', { className: 'mojito-returns-chat__plan-start-summary', 'aria-live': 'polite' });
    var startBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-chat__plan-start',
      // Wrapped: the click event must never arrive as startPlanFromDraft's force.
      onClick: function () { startPlanFromDraft(); }
    }, [
      el('span', { className: 'mojito-returns-chat__plan-start-label' }, t('planContinue')),
      summaryEl
    ]);
    var node = el('div', {
      className: 'mojito-returns-chat__message mojito-returns-chat__message--assistant mojito-returns-chat__plan',
      role: 'group',
      'aria-labelledby': titleId,
      'aria-live': 'off'
    }, [
      el('div', { className: 'mojito-returns-chat__plan-title', id: titleId }, t('planTitle')),
      el('div', { className: 'mojito-returns-chat__plan-sub' }, t('planSub')),
      list,
      startBtn
    ]);
    var intents = {};
    ids.forEach(function (id) { intents[String(id)] = preset; });
    state.planDraft = {
      preset: preset, intents: intents, node: node, list: list,
      summaryEl: summaryEl, startBtn: startBtn, rows: {}
    };
    renderPlanRows();
    // DOM only — the plan is a control, not a line of the transcript.
    messagesEl.appendChild(node);
    trackEvent('plan_shown', { items: ids.length, preset: preset });
    updateActionStates();
    scrollToBottom();
    setTimeout(function () {
      var first = node.querySelector('.mojito-returns-chat__plan-opt.is-active');
      if (first) { try { first.focus({ preventScroll: true }); } catch (e) { /* older engines */ } }
    }, 80);
  }

  function renderPlanRows() {
    var d = state.planDraft;
    if (!d) return;
    var ids = pendingIds();
    // Intents survive for lines still pending; a newly ticked line gets the preset.
    var intents = {};
    ids.forEach(function (id) {
      var k = String(id);
      intents[k] = d.intents[k] || d.preset;
    });
    d.intents = intents;
    d.rows = {};
    while (d.list.firstChild) d.list.removeChild(d.list.firstChild);
    // Already-decided lines first, in order-card order — shown, not editable.
    ((state.order && state.order.line_items) || []).forEach(function (li) {
      if (!decisionOf(li.id)) return;
      d.list.appendChild(el('li', { className: 'mojito-returns-chat__plan-row is-decided' }, [
        planItemNode(li),
        el('span', { className: 'mojito-returns-chat__plan-note' }, t('planDecided'))
      ]));
    });
    ids.forEach(function (id) {
      var k = String(id);
      var li = orderLineItem(id);
      if (!li) return;
      var exBtn = el('button', {
        type: 'button',
        className: 'mojito-returns-chat__plan-opt mojito-returns-chat__plan-opt--exchange',
        onClick: function () { setPlanIntent(k, 'exchange'); }
      }, t('exchangeAction'));
      var rtBtn = el('button', {
        type: 'button',
        className: 'mojito-returns-chat__plan-opt mojito-returns-chat__plan-opt--return',
        onClick: function () { setPlanIntent(k, 'return'); }
      }, li.final_sale ? t('intentCredit') : t('returnAction'));
      var toggle = el('div', {
        className: 'mojito-returns-chat__plan-toggle',
        role: 'group',
        'aria-label': t('planToggleAria').replace('{item}', function () { return lineLabel(id); })
      }, [exBtn, rtBtn]);
      d.rows[k] = { exBtn: exBtn, rtBtn: rtBtn };
      paintPlanRow(k);
      d.list.appendChild(el('li', { className: 'mojito-returns-chat__plan-row', 'data-item-id': k }, [
        planItemNode(li),
        toggle
      ]));
    });
    paintPlanSummary();
  }

  function planItemNode(li) {
    // Fix round 2: a decided row shows the decision's count (lineUnits) —
    // the review's stock cap can have lowered it below the order card's.
    var n = lineUnits(li.id);
    var thumb = li.image
      ? el('img', { className: 'mojito-returns-chat__plan-thumb', src: withImageWidth(li.image, 160), alt: '', loading: 'lazy' })
      : el('div', { className: 'mojito-returns-chat__plan-thumb' });
    var variantParts = [];
    if (li.variant_title) variantParts.push(li.variant_title);
    if (n > 1) variantParts.push('×' + n);
    var info = [
      el('span', { className: 'mojito-returns-chat__plan-name' }, li.title || 'Item'),
      el('span', { className: 'mojito-returns-chat__plan-variant' }, variantParts.join(' · '))
    ];
    if (li.final_sale) {
      info.push(el('span', { className: 'mojito-returns-chat__final-sale-badge' }, t('finalSale')));
    }
    if (n > 1) {
      info.push(el('span', { className: 'mojito-returns-chat__plan-note' },
        t('planUnitsNote').replace('{n}', String(n))));
    }
    return el('div', { className: 'mojito-returns-chat__plan-item' }, [
      thumb,
      el('div', { className: 'mojito-returns-chat__plan-info' }, info)
    ]);
  }

  /** Paint one row's two segments from the draft (is-active + aria-pressed), in place. */
  function paintPlanRow(k) {
    var d = state.planDraft;
    var r = d && d.rows[k];
    if (!r) return;
    var ex = d.intents[k] !== 'return';
    r.exBtn.classList.toggle('is-active', ex);
    r.exBtn.setAttribute('aria-pressed', ex ? 'true' : 'false');
    r.rtBtn.classList.toggle('is-active', !ex);
    r.rtBtn.setAttribute('aria-pressed', ex ? 'false' : 'true');
  }

  function setPlanIntent(k, intent) {
    var d = state.planDraft;
    if (!d || !d.rows[k]) return;
    var from = d.intents[k] || d.preset;
    if (from === intent) return;
    d.intents[k] = intent;
    paintPlanRow(k);
    paintPlanSummary();
    trackEvent('item_intent_changed', { from: from, to: intent, via: 'plan', hasReason: false });
  }

  /** A card pressed while the plan is open applies to every row. */
  function setAllPlanIntents(action) {
    var d = state.planDraft;
    if (!d) return;
    var intent = action === 'return' ? 'return' : 'exchange';
    d.preset = intent;
    Object.keys(d.intents).forEach(function (k) {
      d.intents[k] = intent;
      paintPlanRow(k);
    });
    paintPlanSummary();
  }

  /** Counts per outcome for a set of line ids under a set of intents. */
  function planCounts(ids, intents, preset) {
    var c = { ex: 0, rt: 0, cr: 0 };
    ids.forEach(function (id) {
      var k = String(id);
      if ((intents[k] || preset) !== 'return') c.ex++;
      else if (isFinalSaleItem(k)) c.cr++;
      else c.rt++;
    });
    return c;
  }

  function paintPlanSummary() {
    var d = state.planDraft;
    if (!d) return;
    var ids = Object.keys(d.intents);
    var c = planCounts(ids, d.intents, d.preset);
    d.summaryEl.textContent = countsText(c.ex, c.rt, c.cr);
    d.startBtn.disabled = !ids.length;
  }

  /** The order card changed under an open plan: re-render, or drop it when nothing is pending. */
  function refreshPlanDraft() {
    if (!state.planDraft) return;
    if (pendingIds().length === 0) { discardPlanDraft(); return; }
    renderPlanRows();
  }

  function discardPlanDraft() {
    var d = state.planDraft;
    if (d && d.node && d.node.parentNode) d.node.parentNode.removeChild(d.node);
    state.planDraft = null;
    if (itemDecisionsCount() > 0) showReopenReviewPrompt();
    updateActionStates();
  }

  /**
   * 2026-10-13 — mixed flow (fix round 1): a walk-through is starting from
   * outside the plan (the review's Change / Exchange instead, the reopen
   * prompt's Change) while a plan bubble is still open. A walk-through never
   * runs under an open plan — it suppressed the step's kit, AI buttons and
   * typed routing, and its Continue could restart the step. Dropped quietly:
   * no reopen prompt (the step would strand it), and the lines it listed stay
   * ticked, ready for Exchange or Return once the step is done.
   */
  function dropPlanDraft() {
    var d = state.planDraft;
    if (!d) return;
    if (d.node && d.node.parentNode) d.node.parentNode.removeChild(d.node);
    state.planDraft = null;
  }

  /**
   * Continue. Waits for an in-flight reply first (a typed question may be
   * streaming), then freezes the bubble as history and starts the
   * walk-through. force = the wait already ran; go regardless.
   */
  function startPlanFromDraft(force) {
    var d = state.planDraft;
    if (!d) return;
    if (!force && turnInFlight()) {
      if (d.waiting) return;
      d.waiting = true;
      afterTurn(function () {
        if (state.planDraft !== d) return;
        d.waiting = false;
        startPlanFromDraft(true);
      });
      return;
    }
    var ids = pendingIds();
    if (!ids.length) { discardPlanDraft(); return; }
    var intents = {};
    var changed = 0;
    ids.forEach(function (id) {
      var k = String(id);
      intents[k] = d.intents[k] || d.preset;
      if (intents[k] !== d.preset) changed++;
    });
    var c = planCounts(ids, intents, d.preset);
    // Freeze: the bubble stays in the transcript as what was chosen.
    d.node.classList.add('is-started');
    d.node.querySelectorAll('.mojito-returns-chat__plan-opt').forEach(function (b) {
      b.disabled = true;
      b.setAttribute('aria-disabled', 'true');
    });
    var frozen = el('div', { className: 'mojito-returns-chat__plan-frozen' }, countsText(c.ex, c.rt, c.cr));
    if (d.startBtn.parentNode) d.startBtn.parentNode.replaceChild(frozen, d.startBtn);
    state.planDraft = null;
    startPlan(d.preset, ids, intents, changed);
  }

  /**
   * Queue the lines under their intents and walk them. NEVER resets the
   * selection arrays: a line ticked after earlier decisions joins the same
   * request (the old seeding wiped them, which is how one customer ended up
   * filing two requests).
   */
  function startPlan(preset, ids, intents, changed) {
    if (!Array.isArray(state.exchangeSelections)) state.exchangeSelections = [];
    if (!Array.isArray(state.returnSelections)) state.returnSelections = [];
    if (!Array.isArray(state.exchangeQueue)) state.exchangeQueue = [];
    var also = itemDecisionsCount() > 0;
    ids.forEach(function (id) {
      var k = String(id);
      state.itemPlan[k] = intents[k] === 'return' ? 'return' : 'exchange';
      if (!state.exchangeQueue.some(function (q) { return String(q) === k; })) state.exchangeQueue.push(id);
    });
    var c = planCounts(ids, state.itemPlan, 'exchange');
    state.activeAction = preset;
    state.currentExchangeItemId = null;
    state.singleLineId = null;   // fix round 1: the walk-through owns the lines now
    addMessage('user', planOpenerText(ids, also));
    trackEvent('plan_started', { exchanges: c.ex, returns: c.rt, credits: c.cr, changed: changed || 0, appended: also });
    advanceToNextItem();
  }

  /** "I'd like to exchange: A (L); return: B (M)" — "also" when appending. */
  function planOpenerText(ids, also) {
    var groups = { exchange: [], ret: [], credit: [] };
    ids.forEach(function (id) {
      var intent = intentOf(id) || 'exchange';
      var g = intent === 'exchange' ? 'exchange' : (isFinalSaleItem(id) ? 'credit' : 'ret');
      groups[g].push(lineLabel(id));
    });
    var parts = [];
    [['exchange', 'planPartExchange'], ['ret', 'planPartReturn'], ['credit', 'planPartCredit']].forEach(function (p) {
      if (!groups[p[0]].length) return;
      var names = groups[p[0]].join(', ');
      parts.push(t(p[1]).replace('{items}', function () { return names; }));
    });
    var joined = parts.join('; ');
    return t(also ? 'planOpenerAlso' : 'planOpener').replace('{parts}', function () { return joined; });
  }

  /**
   * Question mode's grounding data: fetch compact care/fabric/fit facts for
   * every product in the order (one call per lookup session, best-effort —
   * chat works without it, answers are just less specific).
   */
  function fetchProductInfoContext(app) {
    if (state.productInfoContext || state.productInfoLoading) return;
    var order = state.order;
    if (!order || !order.line_items) return;
    var ids = [];
    order.line_items.forEach(function (it) {
      if (it.product_id && ids.indexOf(it.product_id) === -1) ids.push(it.product_id);
    });
    if (!ids.length) return;
    state.productInfoLoading = true;
    var config = getConfig(app);
    // Guard against a reset/new lookup landing while this fetch is in
    // flight: only apply the result if it's still the same order.
    var orderRef = order;
    apiFetch(config.apiEndpoint + '/api/product-info', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productIds: ids.slice(0, 6) })
    }, 15000)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        state.productInfoLoading = false;
        if (state.order !== orderRef) return;
        if (data && data.success && data.info) {
          state.productInfoContext = data.info;
        }
      })
      .catch(function () {
        state.productInfoLoading = false;
      });
  }

  /**
   * Send a question topic, waiting briefly (max ~1.5s) for the background
   * product-facts fetch so the FIRST answer is grounded in real product
   * data instead of punting to the support email while facts land a
   * second later. Falls through and sends regardless after the wait.
   */
  function sendQuestionTopic(app, label) {
    var waited = 0;
    // 2026-10-13 (fix round 1): a session left during the wait gets nothing.
    var genRef = state.chatGen;
    (function attempt() {
      if (state.chatGen !== genRef) return;
      if (state.productInfoLoading && !state.productInfoContext && waited < 1500) {
        waited += 150;
        setTimeout(attempt, 150);
        return;
      }
      // 2026-10-13 — mixed flow (fix round 2): nothing marks this wait as a
      // turn, so a typed message could start one meanwhile — and the topic,
      // already shown as sent, was then refused by sendChatMessage and never
      // answered. Send it once that turn ends (afterTurn drops it if the
      // customer leaves the order or the chat session first).
      if (turnInFlight()) {
        afterTurn(attempt);
        return;
      }
      sendChatMessage(app, label, null);
    })();
  }

  /**
   * Total of items already decided (exchanged + returned) — useful for the
   * "Item N of M" progress copy.
   */
  function itemDecisionsCount() {
    var ex = state.exchangeSelections ? state.exchangeSelections.length : 0;
    var rt = state.returnSelections ? state.returnSelections.length : 0;
    return ex + rt;
  }

  /**
   * Commit a line item into either exchangeSelections or returnSelections
   * while enforcing MUTUAL EXCLUSIVITY: the same originalItemId cannot live
   * in both arrays at once (the same physical item can't be refunded AND
   * exchanged). Also dedupes repeat entries within the target array — the
   * latest commit wins. All flows that add to selections MUST go through
   * this helper.
   *
   * Edge case this prevents: customer picks items for return → dismisses
   * the review modal → scrolls back in chat history → clicks an old
   * Exchange recommendation for one of the return items. Without dedupe,
   * the item ends up in BOTH sections of the review modal (and would get
   * double-submitted as refund + exchange).
   */
  function commitSelection(itemId, kind, entry) {
    var idStr = String(itemId);
    // Phase 2.7 — carry the order-card count onto the selection. Every path
    // that creates a selection funnels through here, so this is the one place
    // the customer's "1 of 2" has to survive; the review modal's picker then
    // edits the same field, and selQuantity re-clamps it at submit.
    if (entry && entry.quantity == null) {
      entry.quantity = chosenUnits(itemId);
    }
    state.returnSelections = (state.returnSelections || []).filter(function (s) {
      return String(s.originalItemId) !== idStr;
    });
    state.exchangeSelections = (state.exchangeSelections || []).filter(function (s) {
      return String(s.originalItemId) !== idStr;
    });
    // (Phase 2.9: nothing to clear here — which lines are short is derived
    // from the last rejection against the selections as they stand, see
    // stockShortIds(); this commit changes the input, not the flag.)
    // 2026-10-13: any other kind ('none') just drops the line's decision —
    // the untick path in handleItemToggle.
    if (kind === 'exchange') {
      state.exchangeSelections.push(entry);
    } else if (kind === 'return') {
      state.returnSelections.push(entry);
    }
  }

  /**
   * Advance the multi-item flow to the next pending item. If the queue is
   * empty, opens the combined review modal (exchanges + returns).
   * 2026-10-13 — mixed flow: the step's verb, pill and chip set come from
   * the item's OWN plan (state.itemPlan), not the global activeAction. The
   * step counter bumps first and the step being left is retired at once;
   * when a reply or kit fetch for that step is still in flight, the next
   * prompt waits for it, so it never appears under a reply that is still
   * streaming — and that reply, now stale, renders no controls.
   */
  function advanceToNextItem(opts) {
    // Fix round 1: never a walk-through under an open plan (a no-op from
    // Continue — startPlanFromDraft has already released the draft).
    dropPlanDraft();
    state.stepSeq++;
    retireStepControls();
    if (turnInFlight()) {
      if (!state.stepChanging) {
        state.stepChanging = true;
        updateActionStates();
        afterTurn(function () { state.stepChanging = false; advanceBody(opts); });
      }
      return;
    }
    advanceBody(opts);
  }

  function advanceBody(opts) {
    opts = opts || {};
    state.stepChanging = false;
    // Per-item resets. activeReason used to survive the drain and become
    // every row's submit fallback; lastExchangeContext resent item 1's
    // inventory with item 2's turns; the pitch counters were session-global.
    state.activeReason = null;
    state.currentProposedProductId = null;
    state.lastRecommendations = null;
    state.showReturnConfirm = false;
    state.lastExchangeContext = null;
    state.pendingRecommendationsRender = false;
    state.returnIntentCount = 0;
    state.hasPitchedExchange = false;
    if (!state.exchangeQueue || state.exchangeQueue.length === 0) {
      state.currentExchangeItemId = null;
      if (itemDecisionsCount() === 0) { clearQueueFields(); updateActionStates(); return; }
      updateActionStates();   // unlock the order card
      openItemReviewModal();
      return;
    }
    var itemId = state.exchangeQueue[0];
    state.currentExchangeItemId = itemId;

    var item = state.order && state.order.line_items.find(function (i) {
      return String(i.id) === String(itemId);
    });
    if (!item) {
      state.exchangeQueue.shift();
      advanceToNextItem();
      return;
    }

    var decided = itemDecisionsCount();
    var totalCount = decided + state.exchangeQueue.length;
    var stepNum = decided + 1;
    // Phase 2.9: re-queued from the review modal's "Change" — the customer
    // is swapping a REPLACEMENT, so this is an exchange step whatever the
    // walk-through started as, and the question says so.
    var changing = !!opts.changing;
    var intent = changing ? 'exchange'
      : (intentOf(itemId) || (state.activeAction === 'return' ? 'return' : 'exchange'));
    state.itemPlan[String(itemId)] = intent;
    // The review's "Exchange instead" already has the reason on file: go
    // straight to the exchange kit with it instead of asking again. 'other'
    // is only the fallback, not a reason — ask. (Should a reply still be
    // loading after afterTurn's cap, ask too: handleReasonClick would refuse
    // and leave the step with no way forward. Fix round 1: it now refuses on
    // turnInFlight, a reply still streaming included — so test the same.)
    var known = (opts.knownReason && opts.knownReason !== 'other' && !turnInFlight())
      ? opts.knownReason : null;
    var qKey = known ? 'stepQInstead'
      : opts.fromReturn ? 'xToExchangeQuestion'
      : changing ? 'xChangeQuestion' : null;
    addItemPromptMessage(item, stepNum, totalCount, intent, qKey, { allowSwitch: !known });
    updateActionStates();   // lock
    var seq = state.stepSeq;
    if (known) {
      handleReasonClick('exchange', known, { userText: t('switchToExchangeMsg') });
      // Fix round 1: the review's "Exchange instead" that brought us here
      // is gone with the modal, and this step has no chips to focus — focus
      // fell to <body>. Land on this step's prompt instead (programmatically
      // focusable only), the chips' 80 ms step-scoped pattern below.
      setTimeout(function () {
        var p = document.querySelector('.mojito-returns-chat__message--item-prompt[data-step="' + seq + '"]');
        if (!p) return;
        p.setAttribute('tabindex', '-1');
        try { p.focus({ preventScroll: true }); } catch (e) { /* older engines */ }
      }, 80);
      return;
    }
    showReasonButtons(intent);
    setTimeout(function () {
      var c = document.querySelector('.mojito-returns-chat__buttons[data-step="' + seq + '"] .mojito-returns-chat__reason-btn');
      if (c) { try { c.focus({ preventScroll: true }); } catch (e) { /* older engines */ } }
    }, 80);
  }

  /**
   * Mark the current queue item as "to be returned" (not exchanged) and
   * advance. Used when the customer confirms the return inline or says
   * "return it" during a queue walk-through.
   * 2026-10-13 — mixed flow: bind ({seq, itemId, via}) comes from the
   * control that was tapped. A control from a step already left — a retired
   * "Return This Item", an old AI button — is swallowed (true = handled)
   * rather than returning whatever item is current now.
   */
  function markCurrentItemAsReturn(bind) {
    if (!isInItemQueueMode()) return false;
    if (bind && bind.seq != null && bind.seq !== state.stepSeq) return true;
    if (bind && bind.itemId != null && state.currentExchangeItemId != null &&
        String(bind.itemId) !== String(state.currentExchangeItemId)) return true;
    var currentId = state.currentExchangeItemId;
    // Defensive: if the current ID has been cleared (e.g. queue advanced past
    // the last item before this click fired), jump straight to the review
    // modal instead of silently swallowing the click.
    if (!currentId) {
      if (state.exchangeQueue && state.exchangeQueue.length > 0) {
        currentId = state.exchangeQueue[0];
        state.currentExchangeItemId = currentId;
      } else {
        // Phase 2.9: the queue has drained and the customer is asking to
        // REFUND a line ("actually just return it"). This used to reopen the
        // review modal and swallow the ask — and "Change" now lets a
        // one-item order reach this state. Resolve WHICH line the same way
        // createExchange does; ask when it is ambiguous.
        // 2026-10-13 — mixed flow (fix round 1): unless a newly ticked line is
        // waiting ("Add it to this request"). Then the ask is about THAT
        // line: it takes the Return card's append path (its own step, its
        // own reason), and no line already decided is ever re-decided here.
        if (pendingIds().length > 0) {
          handleAction('return');
          return true;
        }
        var rTarget = drainedQueueTarget(false);
        if (rTarget == null && itemDecisionsCount() > 0) {
          askWhichItem(t('xWhichItemReturn'), function (pickedId) {
            state.currentExchangeItemId = pickedId;
            markCurrentItemAsReturn();
          });
          return true;
        }
        if (rTarget == null) {
          openItemReviewModal();
          return true;
        }
        currentId = rTarget;
        state.currentExchangeItemId = currentId;
      }
    }
    var origItem = state.order && state.order.line_items.find(function (i) {
      return String(i.id) === String(currentId);
    });
    // 2026-10-13: a drained-queue re-decision keeps the line's OWN earlier
    // reason (activeReason is reset per step) instead of filing 'other'.
    var prior = intentOf(currentId);
    var rReason = state.activeReason || priorReason(currentId) || 'other';
    commitSelection(currentId, 'return', {
      originalItemId: currentId,
      originalItem: origItem || null,
      reason: rReason,
      storeCredit: isFinalSaleItem(currentId)
    });
    state.itemPlan[String(currentId)] = 'return';
    if (prior && prior !== 'return') {
      trackEvent('item_intent_changed', {
        from: prior, to: 'return', via: (bind && bind.via) || 'commit', hasReason: !!state.activeReason
      });
    }
    state.exchangeQueue = state.exchangeQueue.filter(function (id) {
      return String(id) !== String(currentId);
    });
    addDecisionCard('return', { original: origItem, storeCredit: isFinalSaleItem(currentId) });
    advanceToNextItem();
    return true;
  }

  /**
   * Phase 2.9 — put ONE decided line back at the head of the walk-through so
   * the customer can pick a different replacement for it. Reached from the
   * review modal's per-row "Change" and from the chat prompt after a
   * sold-out rejection. Works from the single-item flow too: it simply
   * enters queue mode, whose createExchange branch commits the pick and
   * reopens the review — the well-trodden path.
   * 2026-10-13 — mixed flow: opts.knownReason (the review's "Exchange
   * instead" on a return row) reuses the reason already on file and goes
   * straight to the exchange kit; opts.fromReturn phrases the question for
   * a line that was being returned. Callers without opts are unchanged.
   */
  function requeueExchangeItem(itemId, opts) {
    opts = opts || {};
    dropPlanDraft();   // fix round 1: never a walk-through under an open plan
    var idStr = String(itemId);
    if (!state.exchangeSelections) state.exchangeSelections = [];
    if (!state.returnSelections) state.returnSelections = [];
    if (!state.exchangeQueue) state.exchangeQueue = [];
    state.exchangeSelections = state.exchangeSelections.filter(function (s) { return selItemId(s) !== idStr; });
    state.returnSelections = state.returnSelections.filter(function (s) { return selItemId(s) !== idStr; });
    state.exchangeQueue = [itemId].concat(state.exchangeQueue.filter(function (id) { return String(id) !== idStr; }));
    // The words the customer typed for the OLD reason would otherwise win
    // over anything they type now — handleChatSend only records a detail
    // into an empty slot. A KNOWN reason is kept, and its detail still applies.
    if (!opts.knownReason && state.reasonDetails) delete state.reasonDetails[idStr];
    // createExchange refuses to run with nothing ticked; every real path has
    // this line ticked already, so this is insurance, not a state change.
    // (The box itself is re-synced by updateActionStates in advanceBody.)
    if (!isItemSelected(itemId)) state.selectedItems.push(itemId);
    state.itemPlan[idStr] = 'exchange';
    // The last pick's search context would steer the next search back at
    // the replacement being abandoned.
    state.lastExchangeContext = null;
    state.currentProposedProductId = null;
    state.lastRecommendations = null;
    if (!state.activeAction || state.activeAction === 'question') state.activeAction = 'exchange';
    advanceToNextItem({ changing: true, knownReason: opts.knownReason || null, fromReturn: !!opts.fromReturn });
  }

  /**
   * 2026-10-13 — mixed flow: change the CURRENT step's intent, in either
   * direction, before or after a reason. bind is the tapped control's
   * step; via is 'prompt' | 'step' | 'typed' (analytics, and a typed
   * switch already has its user bubble).
   *   no reason yet  → the pill, the question and the chips swap in place;
   *                    nothing is committed
   *   reason, → return   → commit the return with that reason, move on
   *   reason, → exchange → re-run the exchange kit with the same reason
   * Fix round 2: a switch tapped while a turn is in flight waits for it,
   * however long (the old "deferred" flag went ahead regardless once
   * afterTurn gave up). Nothing here may flip itemPlan or remove the step's
   * controls under a reply still running: handleReasonClick then refused,
   * and the step was left an exchange with no kit and no [Return This Item].
   */
  function switchCurrentItemIntent(to, bind, via) {
    if (!walkthroughActive() || state.stepChanging) return;
    if (bind && bind.seq !== state.stepSeq) return;
    var id = state.currentExchangeItemId;
    if (id == null) return;
    if (bind && bind.itemId != null && String(bind.itemId) !== String(id)) return;
    if (turnInFlight()) {
      // Fix round 1: the switch meant what it meant AT THE CLICK. Should the
      // step's reason state have moved meanwhile (a chip tapped after the
      // link), the queued switch is dropped — fired anyway, it read the new
      // reason and committed a return the customer had just walked away from.
      // (Re-deferred, it re-reads the same reason, so the test carries over.)
      var reasonAtClick = state.activeReason;
      afterTurn(function () {
        if (state.activeReason !== reasonAtClick) return;
        switchCurrentItemIntent(to, bind, via);
      });
      return;
    }
    var from = intentOf(id) || 'exchange';
    if (from === to) return;
    var fs = isFinalSaleItem(id);
    var seq = state.stepSeq;
    var hadReason = state.activeReason != null;
    state.itemPlan[String(id)] = to;
    trackEvent('item_intent_changed', {
      from: from, to: to, via: via, hasReason: hadReason,
      step: itemDecisionsCount() + 1,
      total: itemDecisionsCount() + state.exchangeQueue.length
    });
    var msg = to === 'exchange' ? t('switchToExchangeMsg') : t(fs ? 'switchToCreditMsg' : 'switchToReturnMsg');
    if (hadReason && to === 'exchange') {
      removeStepNodes('.mojito-returns-chat__step-actions', seq);
      // Fix round 2: the step reads Exchange too — its pill said RETURN /
      // STORE CREDIT while its only control was "Return this one instead".
      paintStepIntent(seq, to, fs);
      handleReasonClick('exchange', state.activeReason, { userText: msg });
      return;
    }
    if (via !== 'typed') addMessage('user', msg);
    if (hadReason) {
      markCurrentItemAsReturn({ seq: seq, itemId: id, via: via });
      return;
    }
    // No reason yet: swap this step's question and chips in place.
    removeStepNodes('.mojito-returns-chat__buttons', seq);
    paintStepIntent(seq, to, fs);
    showReasonButtons(to);
  }

  /**
   * 2026-10-13 — mixed flow (fix round 2): repaint one step's prompt for a
   * new intent in place — the pill, the question and the prompt's switch
   * link (gone once a reason is in; then there is nothing to relabel).
   */
  function paintStepIntent(seq, to, fs) {
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    var prompt = messagesEl && messagesEl.querySelector('.mojito-returns-chat__message--item-prompt[data-step="' + seq + '"]');
    if (!prompt) return;
    var pillKey = to === 'exchange' ? 'exchange' : (fs ? 'credit' : 'return');
    var pill = prompt.querySelector('.mojito-returns-chat__item-prompt-intent');
    if (pill) {
      pill.className = 'mojito-returns-chat__item-prompt-intent mojito-returns-chat__item-prompt-intent--' + pillKey;
      pill.textContent = t(pillKey === 'exchange' ? 'exchangeAction' : pillKey === 'credit' ? 'intentCredit' : 'returnAction');
    }
    var q = prompt.querySelector('.mojito-returns-chat__item-prompt-question');
    if (q) q.textContent = t(to === 'exchange' ? 'stepQExchange' : (fs ? 'stepQCredit' : 'stepQReturn'));
    var sw = prompt.querySelector('.mojito-returns-chat__item-prompt-switch');
    if (sw) sw.textContent = t(to === 'exchange' ? (fs ? 'switchToCreditLink' : 'switchToReturnLink') : 'switchToExchangeLink');
  }

  /**
   * 2026-10-13 — mixed flow: the step's own way forward after a reasoned
   * reply, placed at the bottom on every turn. A return step gets [Return
   * This Item] (Confirm Store Credit on a final-sale line) plus "Exchange
   * this one instead"; an exchange step gets "Return this one instead".
   * Never depends on English typed phrases or on AI [[BTN]]s, which are
   * suppressed whenever cards render.
   */
  function placeStepActions(seq) {
    var id = state.currentExchangeItemId;
    if (id == null || state.activeReason == null || seq !== state.stepSeq) return;
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;
    removeStepNodes('.mojito-returns-chat__step-actions', seq);
    var fs = isFinalSaleItem(id);
    var bind = { seq: seq, itemId: id };
    var wrap = stamp(el('div', { className: 'mojito-returns-chat__step-actions' }), seq);
    if ((intentOf(id) || 'exchange') === 'return') {
      var btn = el('button', {
        type: 'button',
        className: 'mojito-returns-chat__confirm-btn mojito-returns-chat__confirm-btn--return',
        onClick: function () {
          btn.disabled = true;
          markCurrentItemAsReturn({ seq: seq, itemId: id, via: 'button' });
        }
      }, t(fs ? 'confirmStoreCredit' : 'returnThisItem'));
      wrap.appendChild(btn);
      wrap.appendChild(el('button', {
        type: 'button',
        className: 'mojito-returns-chat__step-switch',
        onClick: function () { switchCurrentItemIntent('exchange', bind, 'step'); }
      }, t('switchToExchangeLink')));
    } else {
      wrap.appendChild(el('button', {
        type: 'button',
        className: 'mojito-returns-chat__step-switch',
        onClick: function () { switchCurrentItemIntent('return', bind, 'step'); }
      }, t(fs ? 'switchToCreditLink' : 'switchToReturnLink')));
    }
    messagesEl.appendChild(wrap);
  }

  /* ========== Reason Buttons ========== */

  function showReasonButtons(action) {
    var buttons = REASON_BUTTONS[action];
    if (!buttons) return;

    var container = el('div', { className: 'mojito-returns-chat__buttons' });
    // 2026-10-13 — mixed flow: a walk-through chip row belongs to its step,
    // and the click passes THIS row so exactly this row is removed.
    if (isInItemQueueMode()) stamp(container);
    buttons.forEach(function (btn) {
      container.appendChild(el('button', {
        type: 'button',
        className: 'mojito-returns-chat__reason-btn',
        'data-action': action,
        'data-reason': btn.key,
        onClick: function () { handleReasonClick(action, btn.key, { row: container }); }
      }, t(btn.label)));
    });

    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (messagesEl) {
      messagesEl.appendChild(container);
      scrollToBottom();
    }
  }

  // 2026-10-13 — mixed flow: the chip label for a reason key, looked up in
  // the action's own set first, then either set. The step switch and the
  // review's "Exchange instead" re-run the exchange kit with a reason that
  // may be return-only (changed-mind) — it still needs a human label.
  function reasonLabel(action, reason) {
    var lists = [REASON_BUTTONS[action], REASON_BUTTONS.exchange, REASON_BUTTONS['return']];
    for (var i = 0; i < lists.length; i++) {
      var hit = (lists[i] || []).find(function (b) { return b.key === reason; });
      if (hit) return t(hit.label);
    }
    return reason;
  }

  // opts.row      — the chip row that was tapped (stale rows are refused)
  // opts.userText — what the customer "says" instead of the chip label: the
  //                 switch messages, which re-run the kit for a reason
  //                 already given.
  function handleReasonClick(action, reason, opts) {
    opts = opts || {};
    // 2026-10-13 (fix round 1): turnInFlight, not chatLoading — a chip tapped
    // while a reply streamed started its kit beside it, and that reply's end
    // released a step change while the kit was still out.
    if (turnInFlight() || state.stepChanging) return;
    if (opts.row && walkthroughActive() && opts.row.getAttribute('data-step') !== String(state.stepSeq)) return;

    var app = document.getElementById('mojito-returns-app');
    var config = getConfig(app);

    // Remove THIS reason row (it used to be the first row in the DOM, which
    // in a walk-through could be another step's), and the step's switch link
    // — once a reason is in, the switch moves under the reply.
    if (opts.row) opts.row.remove();
    if (walkthroughActive()) removeStepNodes('.mojito-returns-chat__item-prompt-switch', state.stepSeq);

    var label = reasonLabel(action, reason);
    var sendText = opts.userText || label;
    trackEvent('reason_selected', { action: action, reason: reason, via: opts.userText ? 'switch' : 'chip' });
    addMessage('user', sendText);

    // Question topics are QUESTIONS, not exchange reasons — never fetch
    // inventory or recommendations for them (that fed the AI an exchange
    // pitch kit), and never record them in activeReason (they'd leak into
    // submitted return reasons). The AI answers from productContext + policy.
    if (action === 'question') {
      sendQuestionTopic(app, label);
      return;
    }

    state.activeReason = reason;
    // 2026-10-13: a walk-through step gets its return button from
    // placeStepActions (bound to the step), not this one-shot flag.
    state.showReturnConfirm = (action === 'return') && !walkthroughActive();

    // Determine if we need same-product inventory or smart recommendations.
    // Same-product reasons:
    //   too-small / too-large (v2) + legacy doesnt-fit / wrong-size
    //                                → keep color, swap size
    //   different-color / looks-different → keep size, swap color
    //   damaged-defective / wrong-item → any replacement variant (no filter)
    // Everything else (changed-mind, fit-shape, different-style, etc.)
    // → smart-rec (same product can't fix a shape complaint).
    var needsSameProduct = (
      reason === 'too-small' ||
      reason === 'too-large' ||
      reason === 'wrong-size' ||
      reason === 'different-color' ||
      reason === 'damaged-defective' ||
      reason === 'wrong-item' ||
      reason === 'doesnt-fit' ||
      reason === 'looks-different'
    );
    // Use the queue-aware helper so multi-item exchanges operate on the
    // current item, not blindly the first selected.
    var selectedItem = getExchangeContextItem();
    pinSingleLine(selectedItem);   // fix round 2: the reason is this line's

    if (!selectedItem || !selectedItem.product_id) {
      sendChatMessage(app, sendText, null);
      return;
    }

    var preferredSize = parseVariantSize(selectedItem.variant_title);
    // Drop the ±10% cap once the customer has signaled they're open to
    // pricier options ("I can pay the difference" etc.) — persists for session.
    var maxPrice = state.priceOpen
      ? 9999
      : Math.ceil(parseFloat(selectedItem.price) * 1.10 * 100) / 100;

    if (needsSameProduct) {
      // Fetch same product inventory
      showTypingIndicator();
      // Fix round 1: bound to this step and session (kitToken).
      var tok = kitToken();
      apiFetch(config.apiEndpoint + '/api/check-inventory', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: selectedItem.product_id })
      }, 15000)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (!kitLive(tok)) { kitDrop(tok); return; }
          hideTypingIndicator();
          var ctx = '';
          if (data.success && data.variants) {
            // Map the reason to a variant filter intent so we show the RIGHT
            // alternatives — not just "anything in stock."
            //   too-small / too-large (+ legacy wrong-size / doesnt-fit)
            //                                              → same color, different sizes
            //   different-color / looks-different          → same size, different colors
            //   damaged-defective / wrong-item / else      → any other in-stock variant
            var reasonIntent = null;
            if (reason === 'too-small' || reason === 'too-large' ||
                reason === 'wrong-size' || reason === 'doesnt-fit') reasonIntent = 'size';
            else if (reason === 'different-color' || reason === 'looks-different') reasonIntent = 'color';

            // 2XL-incident fix (27AUG2026): too-small/too-large carry a
            // DIRECTION. The old direction-blind filter offered a too-small
            // customer the only in-stock size — a SMALLER one — and the AI,
            // ordered by the prompt to pitch bigger, fabricated a 2XL. Use
            // the direction-aware filter, assert sold-out directional sizes
            // as rule-201 negatives, and when NOTHING in the ordered style
            // goes the right direction, escalate straight to the cross-style
            // size search for the adjacent size — the honest "not in this
            // style, but this other style has it" answer.
            var sizeDirection = reason === 'too-small' ? 'larger'
              : reason === 'too-large' ? 'smaller' : null;
            var currentParts = parseVariantTitleParts(selectedItem.variant_title);
            // Only rankable letter sizes get the directional treatment —
            // numeric (waist) and one-size items sit outside SIZE_ORDER
            // (indexOf -1 makes the directional filter return [] regardless
            // of stock), so they keep the old same-color listing instead of
            // a false "nothing else in stock".
            var curIdx = SIZE_ORDER.indexOf(canonicalSize(currentParts.size));
            var useDirectional = (reasonIntent === 'size' && sizeDirection && curIdx !== -1);
            var filtered = useDirectional
              ? filterVariantsBySizeRequest(data.variants, selectedItem, { direction: sizeDirection })
              : filterVariantsByIntent(data.variants, selectedItem, reasonIntent);

            if (useDirectional && filtered.length === 0) {
              var targetIdx = sizeDirection === 'larger' ? curIdx + 1 : curIdx - 1;
              if (targetIdx >= 0 && targetIdx < SIZE_ORDER.length) {
                var targetLabel = SIZE_ORDER[targetIdx].toUpperCase();
                // Verdict only — no forward promise about other styles: the
                // search result isn't known yet, and a baked-in "other styles
                // DO stock X" line would contradict an empty result and
                // license the exact fabrication this path exists to prevent.
                var note = buildDirectionalStockNotes(data.variants, selectedItem, sizeDirection)
                  + 'The ordered style has NO ' + sizeDirection + ' size in stock in ANY color — never offer one, and never claim this style has it.\n';
                showTypingIndicator();
                tok.typing = state.typingGen;   // the re-shown dots are still this kit's
                return runCrossStyleSizeSearch(app, config, selectedItem, sendText, targetLabel, note, tok);
              }
            }

            // Directional lists are proximity-sorted by the filter itself —
            // passing the color would re-sort same-color-first and break the
            // "FIRST item is the closest size" instruction below.
            var inv = buildInventoryCtx(filtered, useDirectional ? null : currentParts.color);
            var inStock = inv.list;

            // 2026-09-11 — wrong item / damaged: the customer wants what they
            // ORDERED. filterVariantsByIntent drops the current variant by
            // design (a fit or color complaint wants something different), so
            // the AI was handed every color except the one they bought and
            // asked "which color works best?". Lead with the ordered variant
            // when it has stock — first card, and an ORDERED VARIANT line the
            // prompt's SAME ITEM FIRST rule keys on. Its [id:] is what
            // licenses the AI to offer it (the worker's stock-claim validator
            // reads sizes off [id:] lines only); the sold-out line carries
            // none, on purpose.
            var wantsSameItem = (reason === 'wrong-item' || reason === 'damaged-defective');
            var orderedNote = '';
            if (wantsSameItem) {
              var orderedVariant = data.variants.find(function (v) {
                return String(v.id) === String(selectedItem.variant_id);
              }) || null;
              if (orderedVariant && orderedVariant.inStock) {
                inStock = [orderedVariant].concat(inv.list);
                orderedNote = 'ORDERED VARIANT (what they should have received — offer to re-send THIS first, no extra charge):\n' +
                  '- ' + orderedVariant.title + ' — $' + orderedVariant.price + ' — ' +
                  (orderedVariant.quantity > 0 ? orderedVariant.quantity + ' in stock' : 'in stock') +
                  ' [id:' + orderedVariant.id + ']\n';
              } else {
                orderedNote = 'ORDERED VARIANT SOLD OUT: ' + (selectedItem.variant_title || 'the variant they ordered') +
                  ' has no stock right now — say so plainly and apologize, then offer the options below or a refund.\n';
              }
            }

            // Tell the AI what the customer's CURRENT variant is so it can
            // reason about direction (size up/down, color alternatives).
            ctx = 'CUSTOMER CURRENT VARIANT: ' + (selectedItem.variant_title || 'unknown') +
                  ' (complaint: ' + label + ')\n';
            ctx += orderedNote;
            if (useDirectional) {
              var sizeReqLine = formatSizeRequestForAI({ direction: sizeDirection }, currentParts.size);
              if (sizeReqLine) ctx += sizeReqLine + '\n';
              ctx += buildDirectionalStockNotes(data.variants, selectedItem, sizeDirection);
            }
            ctx += (wantsSameItem
              ? 'OTHER IN-STOCK OPTIONS OF THIS STYLE — mention only if they ask for something different ('
              : 'SAME PRODUCT INVENTORY (') + (data.product ? data.product.title : selectedItem.title) + '):\n';
            ctx += inv.text;
            if (inv.list.length === 0) ctx += 'No other variants currently in stock.';
            ctx += '\nThe lines above are the ONLY in-stock options of this style — never name a size or color of it that is not listed. If nothing listed solves the complaint, offer [[BTN:Show Other Styles]].';

            // Build recommendation-like objects for product cards (max 4)
            if (inStock.length > 0 && data.product) {
              var productHandle = data.product.handle || '';
              var productTitle = data.product.title || selectedItem.title;
              state.lastRecommendations = inStock.slice(0, MAX_CTX_VARIANTS).map(function (v) {
                return {
                  product_id: data.product.id,
                  variant_id: v.id,
                  title: productTitle + ' — ' + v.title,
                  handle: productHandle,
                  price: v.price,
                  // Prefer the variant's own color image; fall back to the
                  // customer's original order thumb only if the variant has none.
                  image: v.image || selectedItem.image || null,
                  fabric: null
                };
              });
              state.originalItemPrice = parseFloat(selectedItem.price);
              state.originalItemDiscountRate = itemDiscountRate(selectedItem);
              state.originalItemProductId = selectedItem.product_id || null;
              state.pendingRecommendationsRender = true;
            }
          }
          sendChatMessage(app, sendText, null, ctx || null);
        })
        .catch(function () {
          if (!kitLive(tok)) { kitDrop(tok); return; }
          hideTypingIndicator();
          sendChatMessage(app, sendText, null);
        });
    } else {
      // Fetch smart recommendations (different products)
      showTypingIndicator();
      // Fix round 1: bound to this step and session (kitToken).
      var recTok = kitToken();
      apiFetch(config.apiEndpoint + '/api/smart-recommendations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: selectedItem.product_id,
          reason: reason,
          preferredSize: preferredSize,
          maxPrice: maxPrice
        })
      }, 20000)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (!kitLive(recTok)) { kitDrop(recTok); return; }
          // Fix round 1: the typing dots stay up through the check-inventory
          // fallback below — hidden here, that fetch ran with nothing in
          // flight, so a step change could fire under it. Each branch hides
          // them as it finishes.
          var ctx = '';
          if (data.success && data.recommendations && data.recommendations.length > 0) {
            hideTypingIndicator();
            state.lastRecommendations = data.recommendations;
            state.originalItemPrice = parseFloat(selectedItem.price);
            state.originalItemDiscountRate = itemDiscountRate(selectedItem);
            state.originalItemProductId = selectedItem.product_id || null;
            state.pendingRecommendationsRender = true;
            ctx = 'ORIGINAL ITEM: ' + (data.original ? data.original.title : selectedItem.title);
            ctx += ' — $' + (data.original ? data.original.price : selectedItem.price);
            ctx += ' — ' + (data.original ? data.original.fabric : 'unknown') + ' fabric';
            // Phase 2.4: keep the AI's prose consistent with the discount-
            // scaled deltas the cards show — quote the paid price and forbid
            // catalog-based difference math.
            if (state.originalItemDiscountRate > 0) {
              ctx += '\nCUSTOMER PAID: $' + selectedItem.discounted_price + ' per unit after their original-order discount.';
              ctx += ' The same discount carries over to any exchange item, so NEVER quote price differences from the catalog prices above — the cards already show the correct amounts.';
            }
            ctx += '\nCustomer complaint: ' + label;
            ctx += '\nCustomer size: ' + (preferredSize || 'unknown');
            ctx += '\n\nAVAILABLE ALTERNATIVES (in stock, within price range):\n';
            ctx += data.recommendations.map(function (r, i) {
              var sizeNote = r.preferred_size_in_stock ? '✓ ' + preferredSize + ' in stock' : 'Sizes: ' + r.available_sizes.join(', ');
              return (i + 1) + '. ' + r.title + ' — $' + r.price + ' — ' + r.fabric.toUpperCase() + ' — ' + sizeNote + ' [id:' + r.variant_id + ']';
            }).join('\n');
            sendChatMessage(app, sendText, null, ctx);
          } else {
            // No smart recommendations — fall back to same-product inventory
            return apiFetch(config.apiEndpoint + '/api/check-inventory', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ productId: selectedItem.product_id })
            }, 15000)
              .then(function (res) { return res.json(); })
              .then(function (invData) {
                if (!kitLive(recTok)) { kitDrop(recTok); return; }
                hideTypingIndicator();
                if (invData.success && invData.variants) {
                  var curVariantId = selectedItem.variant_id ? String(selectedItem.variant_id) : '';
                  var inv = buildInventoryCtx(
                    invData.variants.filter(function (v) { return v.inStock && String(v.id) !== curVariantId; }),
                    parseVariantTitleParts(selectedItem.variant_title).color
                  );
                  var inStock = inv.list;
                  ctx = 'SAME PRODUCT INVENTORY (' + (invData.product ? invData.product.title : selectedItem.title) + '):\n';
                  ctx += inv.text;
                  if (inStock.length === 0) ctx += 'No variants currently in stock.';
                  if (inStock.length > 0 && invData.product) {
                    state.lastRecommendations = inStock.slice(0, MAX_CTX_VARIANTS).map(function (v) {
                      return {
                        product_id: invData.product.id,
                        variant_id: v.id,
                        title: invData.product.title + ' — ' + v.title,
                        handle: invData.product.handle || '',
                        price: v.price,
                        image: v.image || selectedItem.image || null,
                        fabric: null
                      };
                    });
                    state.originalItemPrice = parseFloat(selectedItem.price);
                    state.originalItemDiscountRate = itemDiscountRate(selectedItem);
                    state.originalItemProductId = selectedItem.product_id || null;
                    state.pendingRecommendationsRender = true;
                  }
                }
                sendChatMessage(app, sendText, null, ctx || null);
              })
              .catch(function () {
                if (!kitLive(recTok)) { kitDrop(recTok); return; }
                hideTypingIndicator();
                sendChatMessage(app, sendText, null);
              });
          }
        })
        .catch(function () {
          if (!kitLive(recTok)) { kitDrop(recTok); return; }
          hideTypingIndicator();
          sendChatMessage(app, sendText, null);
        });
    }
  }

  function parseVariantSize(variantTitle) {
    if (!variantTitle) return null;
    var parts = variantTitle.split('/').map(function (s) { return s.trim(); });
    // [2-8]XL? covers both 2XL..8XL and the Big & Tall bare 2X..8X labels —
    // without them the parts[0] fallback returned the COLOR as the size.
    var sizePattern = /^(XXS|XS|S|M|L|XL|XXL|XXXL|[2-8]XL?|\d{1,2})$/i;
    for (var i = 0; i < parts.length; i++) {
      if (sizePattern.test(parts[i])) return parts[i];
    }
    return parts[0] || null;
  }

  /* ========== Customer history helpers (⭐ markers) ========== */

  // Has the customer previously ordered this exact product (any variant)?
  function hasPreviouslyOrderedProduct(productId) {
    if (!state.customerHistory || !productId) return false;
    var ids = state.customerHistory.previousProductIds || [];
    return ids.indexOf(String(productId)) !== -1;
  }

  // Has the customer previously ordered this exact variant?
  function hasPreviouslyOrderedVariant(variantId) {
    if (!state.customerHistory || !variantId) return false;
    var ids = state.customerHistory.previousVariantIds || [];
    return ids.indexOf(String(variantId)) !== -1;
  }

  // Has the customer frequently ordered this size? (2+ times)
  function isFrequentSize(size) {
    if (!state.customerHistory || !size) return false;
    var counts = state.customerHistory.sizeCounts || {};
    return (counts[size] || 0) >= 2;
  }

  // Has the customer frequently ordered this color? (2+ times, case-insensitive)
  function isFrequentColor(color) {
    if (!state.customerHistory || !color) return false;
    var counts = state.customerHistory.colorCounts || {};
    return (counts[color.toLowerCase()] || 0) >= 2;
  }

  /* ========== Chat Messages ========== */

  /* ========== Exchange price math (Phase 2.4) ========== */

  // The lookup API now returns discounted_price — the per-unit price the
  // customer ACTUALLY paid after all discounts (sale/bundle/code). The worker
  // carries that same discount onto the exchange item in Shopify, so the
  // honest delta between the returned item and a replacement is
  // (newPrice − origCatalogPrice) × (1 − rate). Older cached API responses
  // without discounted_price yield rate 0 → exactly the pre-2.4 catalog math.
  function itemDiscountRate(item) {
    if (!item) return 0;
    var full = parseFloat(item.price);
    var paid = parseFloat(item.discounted_price);
    if (!(full > 0) || isNaN(paid) || paid < 0 || paid >= full) return 0;
    return 1 - paid / full;
  }

  /* ========== Line-item quantity + price (Phase 2.6) ========== */

  // The LIVE order line for a selection (or a bare id). state.order is the
  // source of truth — the trimmed copy cached on a selection predates
  // quantity/discounted_price and only serves a restored session.
  function orderLineItem(selOrId) {
    var id = (selOrId && typeof selOrId === 'object')
      ? (selOrId.originalItemId || (selOrId.originalItem && selOrId.originalItem.id))
      : selOrId;
    if (id == null) return null;
    var li = ((state.order && state.order.line_items) || []).find(function (i) {
      return String(i.id) === String(id);
    });
    return li || (selOrId && typeof selOrId === 'object' ? selOrId.originalItem : null) || null;
  }

  // Units ORDERED on this line (what the customer recognizes from their
  // receipt) vs units still actionable (Shopify's returnable count, which
  // drops as partial returns land). Both default to 1 so a payload without
  // them behaves exactly as this portal did before quantities existed.
  function orderedUnits(item) {
    var q = Number(item && item.quantity);
    return isFinite(q) && q > 0 ? q : 1;
  }
  function returnableUnits(item) {
    // null = the worker could not determine the count (it fails open).
    // Number(null) is 0, which read as "nothing returnable": the units picker
    // vanished and every multi-unit line was silently capped at one.
    var raw = item ? item.returnable_quantity : null;
    if (raw === null || raw === undefined || raw === '') return orderedUnits(item);
    var r = Number(raw);
    if (isFinite(r) && r >= 0) return r;
    return orderedUnits(item);
  }

  // Per-unit money, in cents. paid = what actually left the customer's card
  // (discounted_price, Phase 2.4); list = the pre-discount catalog price.
  // Every customer-facing refund promise must use paid, never list.
  function paidUnitCents(item) {
    if (!item) return 0;
    var paid = parseFloat(item.discounted_price);
    if (isFinite(paid) && paid >= 0) return Math.round(paid * 100);
    var full = parseFloat(item.price);
    return isFinite(full) ? Math.round(full * 100) : 0;
  }
  function listUnitCents(item) {
    var full = parseFloat(item && item.price);
    return isFinite(full) ? Math.round(full * 100) : 0;
  }
  function fmtCents(cents) { return '$' + (cents / 100).toFixed(2); }

  // Units this selection acts on. Defaults to 1 (the portal's behavior
  // before quantity was selectable) and is always clamped to what's still
  // returnable, so a stale selection can never over-return.
  function selQuantity(sel) {
    var max = returnableUnits(orderLineItem(sel));
    var q = Number(sel && sel.quantity);
    if (!isFinite(q) || q < 1) q = 1;
    return Math.max(1, Math.min(q, Math.max(1, max)));
  }

  // Phase 2.7 — the same count for a BARE line id, which is all the order
  // card has: the customer picks how many units to send back before any
  // selection object exists. Same clamp as selQuantity so the two entry
  // points can never disagree, and the same default of 1 — under-returning
  // costs the customer a second request, over-returning files a Shopify
  // return against goods they are keeping.
  function chosenUnits(itemId) {
    var max = returnableUnits(orderLineItem(itemId));
    var q = Number(state.itemUnits && state.itemUnits[String(itemId)]);
    if (!isFinite(q) || q < 1) q = 1;
    return Math.max(1, Math.min(q, Math.max(1, max)));
  }
  function setChosenUnits(itemId, n) {
    if (!state.itemUnits) state.itemUnits = {};
    state.itemUnits[String(itemId)] = n;
    return chosenUnits(itemId);
  }

  // 2026-10-13 — mixed flow (fix round 2): the count a line stands at — its
  // decision's when it has one (the review's stock cap lowers that one only,
  // so the plan's decided row and the AI's pick note said ×2 for a 1-unit
  // exchange), else the order card's.
  function lineUnits(itemId) {
    var sel = selectionFor(itemId);
    return sel ? selQuantity(sel) : chosenUnits(itemId);
  }

  // Phase 2.7 — ONE units picker, two homes: the order card (before the
  // customer has chosen return vs exchange) and the review modal (after).
  // Neither owns the number — the caller's get/set do, so the clamp always
  // lives with the data. `controls` collects the buttons for callers that
  // freeze the UI during submit; `prefix` picks the BEM block so the two
  // homes can be styled for their own surface without forking the logic.
  function buildUnitsPicker(opts) {
    var prefix = opts.prefix;
    // With one picker per row, a bare "One more" tells a screen reader
    // nothing about WHICH line it steps. Callers that render more than one
    // pass the item title as ariaContext.
    var ctx = opts.ariaContext ? ' — ' + opts.ariaContext : '';
    var readout = el('span', {
      className: prefix + '__qty-readout', role: 'status', 'aria-live': 'polite'
    });
    var minus = el('button', {
      type: 'button', className: prefix + '__qty-btn',
      'aria-label': t('qtyDecrease') + ctx, onClick: function () { step(-1); }
    }, '−');
    var plus = el('button', {
      type: 'button', className: prefix + '__qty-btn',
      'aria-label': t('qtyIncrease') + ctx, onClick: function () { step(1); }
    }, '+');
    function render() {
      var q = opts.get();
      readout.textContent = t('qtyOfMax')
        .replace('{n}', String(q)).replace('{max}', String(opts.max));
      // dataset.atLimit is the button's OWN reason to be disabled; setBusy
      // reads it back so unfreezing can't enable a step past the limit.
      minus.dataset.atLimit = q <= 1 ? '1' : '0';
      plus.dataset.atLimit = q >= opts.max ? '1' : '0';
      minus.disabled = q <= 1;
      plus.disabled = q >= opts.max;
    }
    function step(delta) {
      opts.set(opts.get() + delta);
      render();
      if (opts.onChange) opts.onChange();
    }
    if (opts.controls) opts.controls.push(minus, plus);
    render();
    // The three controls live in their own box so a narrow column wraps the
    // LABEL onto its own line and never splits "+" away from "−".
    return el('div', { className: prefix + '__qty' }, [
      el('span', { className: prefix + '__qty-label' }, opts.labelText),
      el('div', { className: prefix + '__qty-controls' }, [minus, readout, plus])
    ]);
  }

  // The price block for one line: paid-per-unit, the list price struck
  // through when it differs, and — for multi-unit lines — the count and the
  // line total. Returns an array of spans for the caller to place.
  function priceLineNodes(item, units) {
    var paid = paidUnitCents(item);
    var list = listUnitCents(item);
    var nodes = [el('span', { className: 'mojito-returns-price__paid' }, fmtCents(paid))];
    if (list > paid) {
      nodes.push(el('span', { className: 'mojito-returns-price__was' }, fmtCents(list)));
    }
    if (units > 1) {
      nodes.push(el('span', { className: 'mojito-returns-price__qty' }, '× ' + units));
      nodes.push(el('span', { className: 'mojito-returns-price__total' }, fmtCents(paid * units)));
    }
    return nodes;
  }

  // Phase 2.5: what an exchange settles to, in CENTS, using the same
  // carryover-scaled math as the inline confirm card — negative means the
  // store owes the customer money back. Reads the line item from state.order
  // (not the selection's cached copy) so a stale snapshot can never make the
  // promised amount disagree with what the worker computes. Returns 0 when
  // either price is unknown: no amount, no promise, no toggle.
  // Phase 2.6: scaled by the selected quantity — exchanging 2 units settles
  // twice the difference, and the worker multiplies the same way.
  function exchangeDifferenceCents(sel, unitsOverride) {
    if (!sel || !sel.recommendation) return 0;
    var li = orderLineItem(sel);
    if (!li) return 0;
    var units = unitsOverride != null ? unitsOverride : selQuantity(sel);
    return unitDifferenceCents(li, sel.recommendation) * units;
  }

  // 2026-09-11 — same-item price match (USER DECISION, all three surfaces):
  // a replacement from the SAME product — any color or size — is charged
  // exactly what the customer paid for the original, so nothing is ever owed
  // on it. The worker prices the exchange line the same way (its
  // appliedDiscount is re-derived onto the paid unit price), so this is a
  // promise the settlement keeps. A replacement CHEAPER than what they paid
  // still comes back as money to the customer — Shopify can't price a line
  // above catalog — and that is the only non-zero this branch returns.
  function isSameProductRec(li, rec) {
    return !!(li && rec && li.product_id != null && rec.product_id != null &&
      String(li.product_id) === String(rec.product_id));
  }
  // Per-unit settlement in cents: positive = the customer pays, negative =
  // money back to them. Cross-product exchanges keep the Phase 2.4 math.
  function unitDifferenceCents(li, rec) {
    if (!li || !rec) return 0;
    var newPrice = parseFloat(rec.price);
    if (!isFinite(newPrice)) return 0;
    if (isSameProductRec(li, rec)) {
      var newCents = Math.round(newPrice * 100);
      var paidCents = paidUnitCents(li);
      return newCents > paidCents ? 0 : newCents - paidCents;
    }
    var origPrice = parseFloat(li.price);
    if (!isFinite(origPrice)) return 0;
    return Math.round((newPrice - origPrice) * (1 - itemDiscountRate(li)) * 100);
  }
  // Cards/PDM read the snapshot taken when the pitch kit was fetched (the
  // context item can move on in a multi-item walk-through).
  function sameProductAsOriginal(productId) {
    return productId != null && state.originalItemProductId != null &&
      String(productId) === String(state.originalItemProductId);
  }

  /* ========== Product Recommendation Cards ========== */

  // 2026-10-13 — mixed flow: bind ({seq, itemId}, the turn's step) rides
  // from the card through the PDM to createExchange, so a card from item 1
  // can never commit onto item 2. retireStepControls dims a step's cards
  // once the step is left.
  function renderProductCards(recommendations, cap, bind) {
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;

    var container = el('div', { className: 'mojito-returns-chat__product-cards' });
    if (bind && bind.seq != null) stamp(container, bind.seq);
    var origPrice = state.originalItemPrice || 0;
    // Default cap = 4 (auto-render fallback). When the caller already filtered
    // the list to what AI explicitly mentioned ([[CARD:id]] matches), they can
    // pass their own cap so we don't truncate AI's chosen set.
    var maxCards = typeof cap === 'number' ? cap : 4;
    var recs = recommendations.slice(0, maxCards);

    recs.forEach(function (rec, idx) {
      var recPrice = parseFloat(rec.price);

      // Thumbnail
      var thumbEl;
      if (rec.image) {
        var imgUrl = rec.image.indexOf('?') !== -1
          ? rec.image + '&width=400'
          : rec.image + '?width=400';
        thumbEl = el('img', {
          className: 'mojito-returns-chat__product-thumb',
          src: imgUrl,
          alt: rec.title,
          loading: 'lazy'
        });
      } else {
        thumbEl = el('div', { className: 'mojito-returns-chat__product-thumb mojito-returns-chat__product-thumb--empty' });
      }

      // Price line — delta scaled by the carried-over discount (Phase 2.4):
      // the replacement inherits the original item's discount, so the real
      // upcharge is the catalog gap × (1 − rate).
      var priceText = '$' + rec.price;
      var detailChildren = [document.createTextNode(priceText)];
      // 2026-09-11 — a same-product replacement is billed at the paid price,
      // so it never earns a "+$" chip however the variants are priced.
      if (origPrice > 0 && recPrice > origPrice && !sameProductAsOriginal(rec.product_id)) {
        var diff = (recPrice - origPrice) * (1 - (state.originalItemDiscountRate || 0));
        // A 100%-discounted original scales any upcharge to $0 — skip the
        // chip below one cent instead of rendering a "+$0.00".
        if (diff >= 0.01) {
          detailChildren.push(el('span', { className: 'mojito-returns-chat__price-diff' }, ' +$' + diff.toFixed(2)));
        }
      }
      if (rec.fabric && rec.fabric !== 'unknown') {
        detailChildren.push(document.createTextNode(' · ' + rec.fabric.charAt(0).toUpperCase() + rec.fabric.slice(1)));
      }

      // Derive the clean product title and the variant line (e.g. "X-Large /
      // WHITE"). Recs from the check-inventory path historically concatenated
      // the variant into `rec.title` with an em-dash separator; smart-rec recs
      // carry a dedicated `variant_title` field. Handle both so the card shows
      // [product title] / [size / color] / [price] consistently either way.
      var cleanTitle = rec.title;
      var variantLine = rec.variant_title || '';
      if (!variantLine && typeof rec.title === 'string' && rec.title.indexOf(' \u2014 ') !== -1) {
        var splitParts = rec.title.split(' \u2014 ');
        cleanTitle = splitParts[0];
        variantLine = splitParts.slice(1).join(' \u2014 ');
      }

      var titleEl = el('span', { className: 'mojito-returns-chat__product-title' }, cleanTitle);
      var variantEl = variantLine
        ? el('span', { className: 'mojito-returns-chat__product-variant' }, variantLine)
        : null;
      var detailsEl = el('span', { className: 'mojito-returns-chat__product-details' });
      detailChildren.forEach(function (child) { detailsEl.appendChild(child); });

      var infoChildren = [titleEl];
      if (hasPreviouslyOrderedVariant(rec.variant_id) || hasPreviouslyOrderedProduct(rec.product_id)) {
        infoChildren.push(el('span', {
          className: 'mojito-returns-chat__product-badge',
          'aria-label': t('pdmPreviouslyOrdered')
        }, '\u2B50 Previously ordered'));
      }
      if (variantEl) infoChildren.push(variantEl);
      infoChildren.push(detailsEl);

      var infoEl = el('div', { className: 'mojito-returns-chat__product-info' }, infoChildren);

      // The ENTIRE card is the click target — opens the product detail modal.
      // No separate CTA button and no external product-page link (modal covers both).
      var card = el('button', {
        type: 'button',
        className: 'mojito-returns-chat__product-card',
        'data-product-id': rec.product_id,
        'aria-label': 'View options for ' + rec.title,
        onClick: (function (startIndex) {
          return function () {
            if (container.classList.contains('is-retired')) return;
            openProductDetailModal(recs, startIndex, bind);
          };
        })(idx)
      }, [thumbEl, infoEl]);
      container.appendChild(card);
    });

    messagesEl.appendChild(container);
    scrollToBottom();
  }

  /* ========== Product Detail Modal (REDO-style size/color picker) ========== */

  // Per-product cache so Prev/Next navigation doesn't re-fetch. Keyed by product_id.
  var pdmProductCache = {};

  // Shopify CDN image URL with a width param appended safely regardless of
  // whether the source URL already has a query string.
  function withImageWidth(src, w) {
    if (!src) return '';
    return src.indexOf('?') !== -1 ? src + '&width=' + w : src + '?width=' + w;
  }

  // 2026-10-13: bind is the card's step (see renderProductCards), handed
  // to createExchange on Continue.
  function openProductDetailModal(recommendations, initialIndex, bind) {
    if (!recommendations || recommendations.length === 0) return;

    var pdmState = {
      recommendations: recommendations,
      index: Math.max(0, Math.min(initialIndex || 0, recommendations.length - 1)),
      currentSize: null,
      currentColor: null,
      currentVariantId: null,
      currentHeroIdx: 0
    };

    // Backdrop + card shell
    var backdrop = el('div', {
      className: 'mojito-returns-pdm',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'mojito-returns-pdm-title'
    });
    var card = el('div', { className: 'mojito-returns-pdm__card' });
    backdrop.appendChild(card);

    // Close button (always present regardless of loading state)
    var closeBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-pdm__close',
      'aria-label': t('pdmClose'),
      onClick: close
    }, '\u00D7');
    card.appendChild(closeBtn);

    // Body region that swaps content between loading/loaded
    var body = el('div', { className: 'mojito-returns-pdm__body' });
    card.appendChild(body);

    // Keyboard + outside-click dismissal
    function onKey(e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft' && !e.target.matches('input,textarea')) gotoProduct(pdmState.index - 1);
      else if (e.key === 'ArrowRight' && !e.target.matches('input,textarea')) gotoProduct(pdmState.index + 1);
    }
    document.addEventListener('keydown', onKey);
    backdrop.addEventListener('click', function (e) { if (e.target === backdrop) close(); });

    function close() {
      backdrop.remove();
      document.removeEventListener('keydown', onKey);
    }

    function gotoProduct(newIndex) {
      if (newIndex < 0 || newIndex >= pdmState.recommendations.length) return;
      pdmState.index = newIndex;
      pdmState.currentSize = null;
      pdmState.currentColor = null;
      pdmState.currentVariantId = null;
      pdmState.currentHeroIdx = 0;
      render();
    }

    function render() {
      var rec = pdmState.recommendations[pdmState.index];
      body.innerHTML = '';
      var cached = pdmProductCache[rec.product_id];
      if (cached) {
        renderLoaded(rec, cached);
        return;
      }
      body.appendChild(el('div', { className: 'mojito-returns-pdm__loading' }, t('pdmLoading')));
      var app = document.getElementById('mojito-returns-app');
      var config = getConfig(app);
      apiFetch(config.apiEndpoint + '/api/check-inventory', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: rec.product_id })
      }, 15000)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data.success && data.product && Array.isArray(data.variants)) {
            pdmProductCache[rec.product_id] = data;
            renderLoaded(rec, data);
          } else {
            body.innerHTML = '';
            body.appendChild(el('div', { className: 'mojito-returns-pdm__loading' }, t('errorNetwork')));
          }
        })
        .catch(function () {
          body.innerHTML = '';
          body.appendChild(el('div', { className: 'mojito-returns-pdm__loading' }, t('errorNetwork')));
        });
    }

    // Pick the initial variant: prefer the rec's variant_id, else first in-stock.
    function chooseInitialVariant(rec, data) {
      var match = data.variants.find(function (v) { return String(v.id) === String(rec.variant_id); });
      if (!match) match = data.variants.find(function (v) { return v.inStock; });
      if (!match) match = data.variants[0];
      return match;
    }

    function findVariant(variants, size, color) {
      return variants.find(function (v) {
        return (!size || v.size === size) && (!color || v.color === color);
      });
    }

    // Pick a hero image index: prefer the image attached to the current variant.
    function pickHeroIndex(product, variant) {
      if (!product.images || product.images.length === 0) return 0;
      if (variant && variant.image) {
        var idx = product.images.findIndex(function (img) { return img.src === variant.image; });
        if (idx !== -1) return idx;
      }
      return 0;
    }

    function renderLoaded(rec, data) {
      var product = data.product;
      var variants = data.variants;

      // First render OR after navigation: initialize selection from the rec.
      if (!pdmState.currentVariantId) {
        var initial = chooseInitialVariant(rec, data);
        pdmState.currentSize = initial.size;
        pdmState.currentColor = initial.color;
        pdmState.currentVariantId = initial.id;
        pdmState.currentHeroIdx = pickHeroIndex(product, initial);
      }

      var currentVariant = variants.find(function (v) { return String(v.id) === String(pdmState.currentVariantId); }) || variants[0];
      var images = (product.images && product.images.length > 0)
        ? product.images
        : (rec.image ? [{ src: rec.image, alt: rec.title }] : []);
      var heroSrc = images[pdmState.currentHeroIdx] ? images[pdmState.currentHeroIdx].src : (rec.image || '');

      body.innerHTML = '';

      // ---------- LEFT: Gallery ----------
      var gallery = el('div', { className: 'mojito-returns-pdm__gallery' });

      var heroWrap = el('div', { className: 'mojito-returns-pdm__hero-wrap' });
      var heroImg = el('img', {
        className: 'mojito-returns-pdm__hero',
        src: withImageWidth(heroSrc, 700),
        alt: product.title
      });
      heroWrap.appendChild(heroImg);
      gallery.appendChild(heroWrap);

      if (images.length > 1) {
        var thumbsRow = el('div', { className: 'mojito-returns-pdm__thumbs' });
        images.forEach(function (img, idx) {
          var thumbBtn = el('button', {
            type: 'button',
            className: 'mojito-returns-pdm__thumb' + (idx === pdmState.currentHeroIdx ? ' is-active' : ''),
            'aria-label': 'Image ' + (idx + 1),
            onClick: function () {
              pdmState.currentHeroIdx = idx;
              renderLoaded(rec, data);
            }
          }, [el('img', { src: withImageWidth(img.src, 120), alt: img.alt || product.title })]);
          thumbsRow.appendChild(thumbBtn);
        });
        gallery.appendChild(thumbsRow);
      }

      body.appendChild(gallery);

      // ---------- RIGHT: Info + pickers ----------
      var info = el('div', { className: 'mojito-returns-pdm__info' });

      // Header copy (context-specific based on reason)
      var headerTitle = t('pdmSizeSuggestions');
      info.appendChild(el('div', { className: 'mojito-returns-pdm__header' }, [
        el('span', { className: 'mojito-returns-pdm__sparkle', 'aria-hidden': 'true' }, '\u2728'),
        el('span', { className: 'mojito-returns-pdm__header-title', id: 'mojito-returns-pdm-title' }, headerTitle)
      ]));
      info.appendChild(el('p', { className: 'mojito-returns-pdm__desc' }, t('pdmSizeSuggestionsBody')));

      // "Recommended" pill when current variant matches the AI's top pick
      if (String(currentVariant.id) === String(rec.variant_id)) {
        info.appendChild(el('span', { className: 'mojito-returns-pdm__badge' }, t('pdmRecommended')));
      }

      // Title + price delta
      info.appendChild(el('h3', { className: 'mojito-returns-pdm__title' }, product.title));

      var origPrice = state.originalItemPrice || 0;
      var curPrice = parseFloat(currentVariant.price) || 0;
      // Phase 2.4: the exchange inherits the original item's discount, so the
      // customer's real delta is the catalog gap × (1 − rate). A same-price
      // swap on a discounted item stays a genuine "$0.00 · Free exchange".
      // 2026-09-11 — same-product replacement: billed at the paid price, so
      // the delta is never positive (see unitDifferenceCents).
      var diff = sameProductAsOriginal(product.id)
        ? 0
        : (curPrice - origPrice) * (1 - (state.originalItemDiscountRate || 0));
      var priceEl = el('div', { className: 'mojito-returns-pdm__price' });
      if (origPrice > 0 && diff > 0.01) {
        priceEl.appendChild(el('span', { className: 'mojito-returns-pdm__price-diff' }, '+$' + diff.toFixed(2)));
        priceEl.appendChild(document.createTextNode(' more'));
      } else if (origPrice > 0 && diff <= 0.01) {
        priceEl.appendChild(el('span', { className: 'mojito-returns-pdm__price-free' }, '$0.00'));
        priceEl.appendChild(document.createTextNode(' \u00B7 ' + t('pdmFreeExchange')));
      } else {
        priceEl.appendChild(document.createTextNode('$' + currentVariant.price));
      }
      info.appendChild(priceEl);

      // ----- Size picker -----
      var uniqueSizes = [];
      variants.forEach(function (v) {
        if (v.size && uniqueSizes.indexOf(v.size) === -1) uniqueSizes.push(v.size);
      });

      if (uniqueSizes.length > 0) {
        info.appendChild(el('div', { className: 'mojito-returns-pdm__group-label' }, t('pdmSize') + ': ' + (pdmState.currentSize || '—')));
        var sizeRow = el('div', { className: 'mojito-returns-pdm__size-options' });
        uniqueSizes.forEach(function (sz) {
          // Is there a matching variant for this size + current color, and is it in stock?
          var matching = findVariant(variants, sz, pdmState.currentColor) || findVariant(variants, sz, null);
          var inStock = matching && matching.inStock;
          var isSelected = sz === pdmState.currentSize;
          var prevOrdered = matching && hasPreviouslyOrderedVariant(matching.id);
          var btn = el('button', {
            type: 'button',
            className: 'mojito-returns-pdm__size-btn'
              + (isSelected ? ' is-selected' : '')
              + (!inStock ? ' is-unavailable' : ''),
            'aria-pressed': isSelected ? 'true' : 'false',
            'aria-label': sz + (inStock ? '' : ' (' + t('pdmOutOfStock') + ')'),
            disabled: !inStock,
            onClick: function () {
              // Prefer a variant in the chosen size with the current color;
              // fall back to any in-stock variant of that size.
              var target = findVariant(variants, sz, pdmState.currentColor);
              if (!target || !target.inStock) {
                target = variants.find(function (v) { return v.size === sz && v.inStock; });
              }
              if (!target) return;
              pdmState.currentSize = target.size;
              pdmState.currentColor = target.color;
              pdmState.currentVariantId = target.id;
              pdmState.currentHeroIdx = pickHeroIndex(product, target);
              renderLoaded(rec, data);
            }
          }, sz);
          if (inStock && prevOrdered) {
            btn.appendChild(el('span', {
              className: 'mojito-returns-pdm__sparkle-mark',
              'aria-hidden': 'true'
            }, '\u2728'));
          }
          sizeRow.appendChild(btn);
        });
        info.appendChild(sizeRow);
      }

      // ----- Color swatches -----
      // Group variants by color; pick the first image for each color swatch.
      var colorMap = {};
      variants.forEach(function (v) {
        if (!v.color) return;
        if (!colorMap[v.color]) {
          colorMap[v.color] = { color: v.color, image: v.image, inStock: v.inStock, variant: v };
        } else if (!colorMap[v.color].inStock && v.inStock) {
          colorMap[v.color] = { color: v.color, image: v.image || colorMap[v.color].image, inStock: true, variant: v };
        }
      });
      var colors = Object.keys(colorMap).map(function (k) { return colorMap[k]; });

      if (colors.length > 0) {
        info.appendChild(el('div', { className: 'mojito-returns-pdm__group-label' }, t('pdmColor') + ': ' + (pdmState.currentColor || '—')));
        var colorGrid = el('div', { className: 'mojito-returns-pdm__color-options' });
        colors.forEach(function (c) {
          var isSelected = c.color === pdmState.currentColor;
          // Swatch is "available" when there's any in-stock variant of this color
          // (with preferred size or at minimum fallback to another size).
          var matching = findVariant(variants, pdmState.currentSize, c.color) || findVariant(variants, null, c.color);
          var inStock = matching && matching.inStock;
          var prevOrdered = matching && hasPreviouslyOrderedVariant(matching.id);

          var swatch = el('button', {
            type: 'button',
            className: 'mojito-returns-pdm__color-swatch'
              + (isSelected ? ' is-selected' : '')
              + (!inStock ? ' is-unavailable' : ''),
            'aria-pressed': isSelected ? 'true' : 'false',
            'aria-label': c.color + (inStock ? '' : ' (' + t('pdmOutOfStock') + ')'),
            title: c.color,
            disabled: !inStock,
            onClick: function () {
              var target = findVariant(variants, pdmState.currentSize, c.color);
              if (!target || !target.inStock) {
                target = variants.find(function (v) { return v.color === c.color && v.inStock; });
              }
              if (!target) return;
              pdmState.currentSize = target.size;
              pdmState.currentColor = target.color;
              pdmState.currentVariantId = target.id;
              pdmState.currentHeroIdx = pickHeroIndex(product, target);
              renderLoaded(rec, data);
            }
          });
          if (c.image) {
            swatch.appendChild(el('img', { src: withImageWidth(c.image, 120), alt: c.color }));
          } else {
            // No image → show truncated color name
            swatch.appendChild(el('span', { className: 'mojito-returns-pdm__color-name' },
              c.color.substring(0, 4).toUpperCase()));
          }
          if (inStock && prevOrdered) {
            swatch.appendChild(el('span', {
              className: 'mojito-returns-pdm__sparkle-mark',
              'aria-hidden': 'true'
            }, '\u2728'));
          }
          colorGrid.appendChild(swatch);
        });
        info.appendChild(colorGrid);
      }

      // Previously-ordered hint for the currently selected variant
      if (hasPreviouslyOrderedVariant(currentVariant.id)) {
        info.appendChild(el('div', { className: 'mojito-returns-pdm__prev-ordered' },
          '\u2B50 ' + t('pdmPreviouslyOrdered').toLowerCase()));
      }

      body.appendChild(info);

      // ---------- Footer ----------
      var footer = card.querySelector('.mojito-returns-pdm__footer');
      if (footer) footer.remove();
      footer = el('div', { className: 'mojito-returns-pdm__footer' });

      var prevBtn = el('button', {
        type: 'button',
        className: 'mojito-returns-pdm__btn mojito-returns-pdm__btn--ghost',
        disabled: pdmState.index === 0,
        onClick: function () { gotoProduct(pdmState.index - 1); }
      }, t('pdmPrevious'));
      var hasNext = pdmState.index < pdmState.recommendations.length - 1;
      var noThanksBtn = el('button', {
        type: 'button',
        className: 'mojito-returns-pdm__btn mojito-returns-pdm__btn--ghost',
        onClick: close
      }, t('pdmNoThanks'));
      // "Continue" runs the existing confirmExchange flow with the user's
      // selected variant. If a later rec is available we offer "Next" instead
      // when the customer isn't ready yet.
      var continueBtn = el('button', {
        type: 'button',
        className: 'mojito-returns-pdm__btn mojito-returns-pdm__btn--primary',
        onClick: function () {
          // Build a recommendation object from current selection so the
          // existing createExchange() works unchanged.
          var chosen = {
            product_id: product.id,
            variant_id: currentVariant.id,
            title: product.title + ' — ' + currentVariant.title,
            handle: product.handle || '',
            price: currentVariant.price,
            image: currentVariant.image || (images[0] && images[0].src) || rec.image,
            fabric: rec.fabric || null,
            // Phase 2.7: carry the variant's own stock depth. This is the ONLY
            // object that becomes sel.recommendation, so without it the review
            // modal's stock cap has nothing to cap against and a customer
            // exchanging 2 units into a variant with 1 left sails through.
            quantity: currentVariant.quantity
          };
          close();
          createExchange(chosen, bind);
        }
      }, t('pdmContinue'));

      footer.appendChild(prevBtn);
      var rightGroup = el('div', { className: 'mojito-returns-pdm__footer-right' });
      if (hasNext) {
        var nextBtn = el('button', {
          type: 'button',
          className: 'mojito-returns-pdm__btn mojito-returns-pdm__btn--ghost',
          onClick: function () { gotoProduct(pdmState.index + 1); }
        }, t('pdmNext'));
        rightGroup.appendChild(nextBtn);
      }
      rightGroup.appendChild(noThanksBtn);
      rightGroup.appendChild(continueBtn);
      footer.appendChild(rightGroup);
      card.appendChild(footer);

      // Focus management
      closeBtn.focus();
    }

    document.body.appendChild(backdrop);
    render();
  }

  /* ========== Exchange/Return Confirmation ========== */

  /**
   * Confirmation gate for exchange. On first click, shows a summary and
   * requires a second explicit Confirm click before submitting. This prevents
   * accidental one-click exchanges and mirrors REDO's review-before-submit UX.
   */
  /**
   * Phase 2.9 — ask which decided line an action applies to, one button per
   * line, when the drained queue leaves it ambiguous. Its own markup, not an
   * AI-button row: those are swept by the next AI reply (taking the pending
   * pick with them) and ellipsise long labels — and the variant, the one
   * thing that tells two sizes of the same style apart, is the part that
   * gets cut. Here the title and variant each get a line and wrap freely.
   * onPick receives the chosen line's string id.
   */
  function askWhichItem(question, onPick) {
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;
    var entries = (state.exchangeSelections || []).concat(state.returnSelections || []);
    addMessage('assistant', question);
    var prev = messagesEl.querySelectorAll('.mojito-returns-chat__which-item');
    prev.forEach(function (n) { n.remove(); });
    var row = el('div', { className: 'mojito-returns-chat__which-item', role: 'group', 'aria-label': question });
    entries.forEach(function (sel) {
      var id = selItemId(sel);
      if (!id || !orderLineItem(id)) return;
      var item = orderLineItem(id);
      var label = (item.title || 'Item') + (item.variant_title ? ' (' + item.variant_title + ')' : '');
      var parts = [el('span', { className: 'mojito-returns-chat__which-item-title' }, item.title || 'Item')];
      if (item.variant_title) {
        parts.push(el('span', { className: 'mojito-returns-chat__which-item-variant' }, item.variant_title));
      }
      row.appendChild(el('button', {
        type: 'button',
        className: 'mojito-returns-chat__which-item-btn',
        onClick: function () {
          row.remove();
          addMessage('user', label);
          onPick(id);
        }
      }, parts));
    });
    messagesEl.appendChild(row);
    scrollToBottom();
  }

  /** The chosen line becomes current and the same pick re-runs through createExchange's queue branch. */
  function askWhichItemToReplace(recommendation) {
    askWhichItem(t('xWhichItem'), function (id) {
      state.currentExchangeItemId = id;
      createExchange(recommendation);
    });
  }

  function createExchange(recommendation, bind) {
    var app = document.getElementById('mojito-returns-app');

    // 2026-10-13: queue mode commits onto the current/decided line, never
    // onto selectedItems — an empty tick list there is not an error.
    if (!state.order || (!isInItemQueueMode() && state.selectedItems.length === 0)) {
      addMessage('assistant', 'Something went wrong. Please try again.');
      return;
    }

    // Multi-item queue: save the exchange pick for the CURRENT item and
    // advance to the next one instead of showing inline confirmation.
    // Works for both exchange-action and return-action queues — in return
    // action, picking a card means "exchange instead of return this one."
    if (isInItemQueueMode()) {
      // 2026-10-13 — mixed flow: a card (or its PDM) from a step already
      // left must not commit onto the item that is current now. Unbound
      // picks (cards fetched after a drain) keep the Phase 2.9 routing below.
      if (bind && bind.seq != null && bind.seq !== state.stepSeq) {
        addMessage('assistant', t('staleOption'));
        return;
      }
      if (bind && bind.itemId != null && state.currentExchangeItemId != null &&
          String(bind.itemId) !== String(state.currentExchangeItemId)) {
        addMessage('assistant', t('staleOption'));
        return;
      }
      var currentId = state.currentExchangeItemId;
      if (!currentId && state.exchangeQueue.length > 0) {
        // Same defensive step markCurrentItemAsReturn takes: a pending line
        // with the pointer cleared is still the head of the queue.
        currentId = state.exchangeQueue[0];
        state.currentExchangeItemId = currentId;
      }
      // Phase 2.9: the queue has drained (every line decided, review
      // dismissed) and the customer picked again. Resolve WHICH line this
      // replaces instead of falling through to the single-item path below —
      // that committed against selectedItems[0] and reopened the review with
      // the sold-out row untouched, so the next submit failed the same way.
      if (!currentId) {
        var drainedTarget = drainedQueueTarget(false);
        if (drainedTarget != null) {
          currentId = drainedTarget;
          state.currentExchangeItemId = drainedTarget;
        } else if (itemDecisionsCount() > 0) {
          askWhichItemToReplace(recommendation);
          return;
        }
      }
      if (currentId) {
        var origItem = state.order.line_items.find(function (i) {
          return String(i.id) === String(currentId);
        });
        // 2026-10-13: queue exchange rows carried NO reason and inherited
        // whatever activeReason the LAST item left behind at submit. Each
        // row now files its own; a drained re-pick keeps the line's earlier
        // reason. A card picked on a return step flips that line to exchange.
        var xReason = state.activeReason || priorReason(currentId) || 'other';
        var prior = intentOf(currentId);
        commitSelection(currentId, 'exchange', {
          originalItemId: currentId,
          originalItem: origItem || null,
          recommendation: recommendation,
          reason: xReason
        });
        state.itemPlan[String(currentId)] = 'exchange';
        if (prior === 'return') {
          trackEvent('item_intent_changed', { from: 'return', to: 'exchange', via: 'card_pick', hasReason: !!state.activeReason });
        }
        state.exchangeQueue = state.exchangeQueue.filter(function (id) {
          return String(id) !== String(currentId);
        });
        addDecisionCard('exchange', { original: origItem, recommendation: recommendation });
        advanceToNextItem();
        return;
      }
    }

    // Single-item flow: keep the inline confirmation summary + submit
    // 2026-10-13 (fix round 1): ONE line for the summary, the money and the
    // commit — the single path's pinned line. The summary used to quote
    // getSelectedItems()[0] (order-card order) while "Yes, submit"
    // committed onto state.selectedItems[0] (tick order).
    var line = singlePathLine();
    var selectedItems = line ? [line] : [];
    var origTitle = selectedItems.length > 0 ? selectedItems[0].title : 'your item';
    var newTitle = recommendation.title;
    var origPrice = parseFloat(selectedItems[0] ? selectedItems[0].price : '0') || 0;
    var newPrice = parseFloat(recommendation.price) || 0;
    // Phase 2.4: scale the delta by the carried-over discount so the promise
    // matches what Shopify will actually charge (or credit).
    // Phase 2.7: and by the UNITS, because this card's number is now the last
    // one the customer sees on the "you owe money" path — the review modal
    // hides its settlement line when the balance runs against them. Quoting
    // the per-unit difference here while submitting two units billed double
    // what the card promised.
    var exchangeUnits = selectedItems[0] ? chosenUnits(selectedItems[0].id) : 1;
    // 2026-09-11 — same-item price match rides through unitDifferenceCents
    // (same product → billed at the paid price, never an upcharge).
    var diff = (unitDifferenceCents(selectedItems[0], recommendation) * exchangeUnits) / 100;
    var priceMatched = isSameProductRec(selectedItems[0], recommendation) &&
      Math.round(newPrice * 100) > paidUnitCents(selectedItems[0]);
    var priceNote = '';
    if (diff > 0.01) priceNote = " You'll pay an extra $" + diff.toFixed(2) + ".";
    else if (diff < -0.01 && selectedItems[0] && isFinalSaleItem(selectedItems[0].id)) {
      // 2026-10-13 — mixed flow (fix round 2): a final-sale original's money
      // back is store credit by policy, and the review offers no choice —
      // say so here in the review row's own words, not "you choose next".
      var backAmount = '$' + Math.abs(diff).toFixed(2);
      priceNote = ' ' + t('xDiffFinalSaleLine').replace('{amount}', function () { return backAmount; });
    } else if (diff < -0.01) {
      // Phase 2.5: never call this "a credit" here — the form it comes back in
      // is the customer's choice, made one screen later. Promising "credit" at
      // this step is what made the old copy misleading whenever the merchant
      // settled the difference to the original card instead.
      priceNote = " You'll get $" + Math.abs(diff).toFixed(2)
        + ' back — you choose store credit or your original payment method next.';
    } else priceNote = priceMatched
      ? ' No price difference — same item, priced at what you paid.'
      : ' No price difference.';

    // Show summary card with Confirm / Cancel
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;

    // Disable other exchange buttons while we wait for confirmation
    var allBtns = document.querySelectorAll('.mojito-returns-chat__confirm-btn--exchange');
    allBtns.forEach(function (btn) { btn.disabled = true; });

    // Visual product card: hero thumb + title + price delta. Gives the
    // customer a final visual check before committing. Mirrors REDO's pattern
    // of never asking for confirmation against text alone.
    var previewImgSrc = recommendation.image
      ? (recommendation.image.indexOf('?') !== -1 ? recommendation.image + '&width=300' : recommendation.image + '?width=300')
      : null;

    var previewChildren = [];
    if (previewImgSrc) {
      previewChildren.push(el('div', { className: 'mojito-returns-chat__confirm-preview-thumb' }, [
        el('img', { src: previewImgSrc, alt: newTitle, loading: 'lazy' })
      ]));
    } else {
      previewChildren.push(el('div', { className: 'mojito-returns-chat__confirm-preview-thumb mojito-returns-chat__confirm-preview-thumb--empty' }));
    }
    var priceStr = '$' + (recommendation.price || newPrice.toFixed(2));
    if (diff > 0.01) priceStr += ' · +$' + diff.toFixed(2);
    previewChildren.push(el('div', { className: 'mojito-returns-chat__confirm-preview-info' }, [
      el('div', { className: 'mojito-returns-chat__confirm-preview-title' }, newTitle),
      el('div', { className: 'mojito-returns-chat__confirm-preview-price' }, priceStr)
    ]));
    var preview = el('div', { className: 'mojito-returns-chat__confirm-preview' }, previewChildren);

    // Phase 2.7: name the count in the sentence too — an unqualified
    // "extra $10.00" against a $5.00 catalog gap reads like an error.
    var unitsPhrase = exchangeUnits > 1 ? ' (' + exchangeUnits + ' units)' : '';
    var summary = el('div', { className: 'mojito-returns-chat__confirm-summary' }, [
      el('div', { className: 'mojito-returns-chat__confirm-summary-title' }, 'Please confirm your exchange'),
      el('div', { className: 'mojito-returns-chat__confirm-summary-body' },
        'Exchanging "' + origTitle + '"' + unitsPhrase + ' for this item.' + priceNote)
    ]);

    var confirmRow = el('div', { className: 'mojito-returns-chat__ai-buttons' });
    var confirmBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-chat__ai-button is-primary',
      onClick: function () {
        // Route through the final review modal (has email-confirm gate)
        // instead of firing /api/create-exchange directly. Shape matches
        // the queue-mode branch of createExchange so openItemReviewModal
        // + submitAllDecisions already handle it.
        // Fix round 1: the line the summary above named.
        var currentId = line ? line.id : state.selectedItems[0];
        // Unticked since the card appeared: the customer has just confirmed
        // THIS line, so it is ticked again — a decision stays a ticked line.
        if (line && !isItemSelected(currentId)) {
          state.selectedItems.push(currentId);
          updateActionStates();
        }
        var origItem = (state.order && Array.isArray(state.order.line_items))
          ? state.order.line_items.find(function (i) { return String(i.id) === String(currentId); })
          : null;
        commitSelection(currentId, 'exchange', {
          originalItemId: String(currentId),
          originalItem: origItem || null,
          recommendation: recommendation,
          reason: state.activeReason || 'other'
        });
        preview.remove();
        summary.remove();
        confirmRow.remove();
        openItemReviewModal();
      }
    }, 'Yes, submit');
    var cancelBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-chat__ai-button',
      onClick: function () {
        preview.remove();
        summary.remove();
        confirmRow.remove();
        allBtns.forEach(function (btn) { btn.disabled = false; });
        addMessage('assistant', 'No problem — take your time.');
      }
    }, 'Change something');
    confirmRow.appendChild(confirmBtn);
    confirmRow.appendChild(cancelBtn);

    messagesEl.appendChild(preview);
    messagesEl.appendChild(summary);
    messagesEl.appendChild(confirmRow);
    scrollToBottom();
  }

  function submitExchange(recommendation, allBtns) {
    var app = document.getElementById('mojito-returns-app');
    var config = getConfig(app);
    var sessionId = getSessionId();

    // Use variant_id from recommendation (set by smart-recommendations endpoint)
    var variantId = recommendation.variant_id || recommendation.product_id;

    // Re-label all exchange buttons to show progress
    allBtns.forEach(function (btn) { btn.disabled = true; btn.textContent = '...'; });
    var clickedBtn = document.querySelector('[data-product-id="' + recommendation.product_id + '"]');
    if (clickedBtn) clickedBtn.textContent = 'Creating exchange...';

    apiFetch(config.apiEndpoint + '/api/create-exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: sessionId,
        originalOrderId: state.order.id,
        originalItemId: state.selectedItems[0],
        newVariantId: variantId,
        chatTranscript: state.messages
      })
    }, 30000)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.success) {
          renderSuccessModal('exchange');
          trackEvent('exchange_completed', { orderName: state.order.name, newItem: recommendation.title });
          // Remove all exchange buttons
          allBtns.forEach(function (btn) { btn.remove(); });
        } else {
          addMessage('assistant', data.message || 'Unable to create exchange. Please contact customer.service@mojitocollection.com.');
          allBtns.forEach(function (btn) { btn.disabled = false; btn.textContent = 'Exchange for this'; });
        }
      })
      .catch(function () {
        addMessage('assistant', 'Something went wrong. Please try again or contact customer.service@mojitocollection.com.');
        allBtns.forEach(function (btn) { btn.disabled = false; btn.textContent = 'Exchange for this'; });
      });
  }

  /**
   * 2026-10-13 — mixed flow: "1 to exchange · 1 to return" for the success
   * modal. Final-sale returns are counted as store credit.
   */
  function mixedCountsLine(exchanges, returns) {
    var rt = 0, cr = 0;
    returns.forEach(function (sel) {
      if (sel.storeCredit || isFinalSaleItem(selItemId(sel))) cr++; else rt++;
    });
    return countsText(exchanges.length, rt, cr);
  }

  /**
   * 2026-10-13 — mixed flow: the body of a mixed success, one sentence per
   * money leg. A mixed request used to be titled "Return initiated", and its
   * bonus claim covered rows that never chose credit. choseCredit is
   * submitAllDecisions' own test, so the copy and the payload agree.
   */
  function mixedSuccessBody(returns, xBackRows, choseCredit) {
    var parts = [t('successMixedLabel'), t('successMixedExchange')];
    // Fix round 2: the same per-row outcome as a return-only request (see
    // returnBackOutcome) — "some chose credit, the rest final sale" gets its
    // own sentence instead of dropping the bonus the review showed.
    var rOutcome = returnBackOutcome(returns, choseCredit);
    if (rOutcome === 'creditBonus') parts.push(t('successMixedCreditBonus'));
    else if (rOutcome === 'credit') parts.push(t('successMixedCredit'));
    else if (rOutcome === 'creditSome') parts.push(t('successMixedCreditSomeBonus'));
    else if (rOutcome === 'split' || rOutcome === 'splitBonus') parts.push(t('successMixedSplit'));
    else if (rOutcome === 'refund') parts.push(t('successMixedRefund'));
    // Fix round 1: one outcome over EVERY money-back exchange row — any()
    // claimed store credit (and the bonus) for all of them when only one
    // row was credit.
    var xOutcome = exchangeBackOutcome(xBackRows, choseCredit);
    if (xOutcome === 'credit') parts.push(t('successMixedXCreditBonus'));
    else if (xOutcome === 'creditFinal') parts.push(t('successMixedXCredit'));
    else if (xOutcome === 'creditSome') parts.push(t('successMixedXCreditSome'));
    else if (xOutcome === 'split') parts.push(t('successMixedXSplit'));
    else if (xOutcome === 'splitFinal') parts.push(t('successMixedXSplitFinal'));
    else if (xOutcome === 'refund') parts.push(t('successMixedXRefund'));
    return parts.join(' ');
  }

  /**
   * 2026-10-13 — mixed flow (fix round 2): where the RETURN rows' money
   * goes, over every row. A row is credit when the customer chose credit at
   * review (5% bonus) or it is final sale (storeCredit / the line's tag —
   * credit by policy, never a bonus); only a non-final row can choose.
   *   null          — no return row
   *   'refund'      — no credit row
   *   'creditBonus' — all credit, every row chose it, none final sale
   *   'credit'      — all credit, none chose it (all final sale)
   *   'creditSome'  — all credit, some chose it, some final sale
   *   'splitBonus'  — credit and refund rows; every credit row chose it
   *   'split'       — credit and refund rows; a final-sale row among the credit
   * The non-mixed copy said "refund" for a final-sale row in 'split', and
   * claimed the bonus on final-sale credit in 'creditSome'.
   */
  function returnBackOutcome(returns, choseCredit) {
    if (!returns || !returns.length) return null;
    function isFinal(sel) { return !!sel.storeCredit || isFinalSaleItem(selItemId(sel)); }
    var creditRows = returns.filter(function (sel) { return choseCredit(sel) || isFinal(sel); });
    if (creditRows.length === 0) return 'refund';
    var anyFinal = creditRows.some(isFinal);
    if (creditRows.length < returns.length) return anyFinal ? 'split' : 'splitBonus';
    if (!anyFinal) return 'creditBonus';
    var anyChose = creditRows.some(function (sel) { return choseCredit(sel) && !isFinal(sel); });
    return anyChose ? 'creditSome' : 'credit';
  }

  /**
   * 2026-10-13 — mixed flow (fix round 1): where the exchange rows' money
   * back goes, over EVERY such row (a final-sale original is credit by
   * policy):
   *   null          — no exchange row settles money back
   *   'refund'      — none is credit
   *   'credit'      — all credit, every row CHOSE it and none is final sale
   *                   (the only case that may claim the 5% bonus outright)
   *   'creditFinal' — all credit, none chose it (every row final sale)
   *   'split'       — some credit, some back to the original payment
   * Fix round 2: a chosen row beside a final-sale one was 'creditFinal' (its
   * bonus dropped, its choice called final sale), and 'split' said "as you
   * chose" when only policy made a row credit. Now:
   *   'creditSome'  — all credit, some chose it, some final sale
   *   'split'       — some credit, some refund; at least one row chose credit
   *   'splitFinal'  — some credit, some refund; the credit is final sale only
   */
  function exchangeBackOutcome(xBackRows, choseCredit) {
    if (!xBackRows || !xBackRows.length) return null;
    function isFinal(sel) { return isFinalSaleItem(selItemId(sel)); }
    var creditRows = xBackRows.filter(function (sel) { return choseCredit(sel) || isFinal(sel); });
    if (creditRows.length === 0) return 'refund';
    var anyChose = creditRows.some(function (sel) { return choseCredit(sel) && !isFinal(sel); });
    if (creditRows.length < xBackRows.length) return anyChose ? 'split' : 'splitFinal';
    if (!creditRows.some(isFinal)) return 'credit';
    return anyChose ? 'creditSome' : 'creditFinal';
  }

  /**
   * Renders a full-screen success modal after an exchange or return is
   * submitted successfully. Overlay + centered card + close button.
   * `kind` is 'exchange', 'return' or 'mixed' (controls title string).
   * 2026-10-13: opts.summary (a counts line above the body) and opts.body
   * (replaces the body copy) are how the mixed success states each leg.
   */
  function renderSuccessModal(kind, opts) {
    var titleKey = kind === 'mixed' ? 'successModalTitleMixed'
      : kind === 'return' ? 'successModalTitleReturn' : 'successModalTitleExchange';
    // 2026-10-13 — mixed flow (fix round 2): opts.returnOutcome is
    // returnBackOutcome over every returned line. It replaces the old
    // storeCredit (every line credit) / bonus (ANY line chose credit) pair,
    // which promised a cash refund for a final-sale row beside a chosen-
    // credit one, and the 5% bonus on final-sale credit. 'refund' (and no
    // return rows) falls through to the exchange copy below.
    var bodyKey;
    var ro = opts && opts.returnOutcome;
    if (ro === 'creditBonus') {
      bodyKey = 'successModalBodyCreditBonus';
    } else if (ro === 'credit') {
      bodyKey = 'successModalBodyCredit';
    } else if (ro === 'creditSome') {
      bodyKey = 'successModalBodyCreditSomeBonus';
    } else if (ro === 'splitBonus') {
      // Credit rows (all chose it, with the bonus) and refund rows.
      bodyKey = 'successModalBodyMixedBonus';
    } else if (ro === 'split') {
      bodyKey = 'successModalBodySplit';
    } else if (opts && opts.exchangeDifference === 'credit') {
      // Phase 2.5 — an exchange settling money back. Only reached when the
      // return-leg copy above doesn't already own the message, so the two
      // credit stories can never contradict each other in one modal.
      bodyKey = 'successModalBodyExchangeCredit';
    } else if (opts && opts.exchangeDifference === 'creditFinal') {
      // 2026-10-13: a final-sale original's difference is credit by policy —
      // no 5% bonus to claim (the 'credit' copy above claimed one).
      bodyKey = 'successModalBodyExchangeCreditFinal';
    } else if (opts && opts.exchangeDifference === 'creditSome') {
      // Fix round 2: all credit — chosen (bonus) and final sale side by side.
      bodyKey = 'successModalBodyExchangeCreditSome';
    } else if (opts && opts.exchangeDifference === 'split') {
      // Fix round 1: some rows' difference is credit, the rest a refund.
      bodyKey = 'successModalBodyExchangeSplit';
    } else if (opts && opts.exchangeDifference === 'splitFinal') {
      // Fix round 2: the same split, but no row chose that credit — the
      // credit part is final sale only.
      bodyKey = 'successModalBodyExchangeSplitFinal';
    } else if (opts && opts.exchangeDifference === 'refund') {
      bodyKey = 'successModalBodyExchangeRefund';
    } else {
      bodyKey = 'successModalBody';
    }
    // Close any prior modal to avoid stacking
    var existing = document.querySelector('.mojito-returns-modal');
    if (existing) existing.remove();

    var backdrop = el('div', {
      className: 'mojito-returns-modal',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'mojito-returns-modal-title'
    });

    var card = el('div', { className: 'mojito-returns-modal__card' });

    var checkmark = el('div', { className: 'mojito-returns-modal__check' }, '\u2713');
    var title = el('h3', {
      className: 'mojito-returns-modal__title',
      id: 'mojito-returns-modal-title'
    }, t(titleKey));
    var body = el('p', { className: 'mojito-returns-modal__body' }, (opts && opts.body) || t(bodyKey));

    function dismissAndReset() {
      backdrop.remove();
      // Wipe session state and send the customer back to the lookup screen.
      state.order = null;
      state.authToken = null;
      state.eligible = true;
      state.eligibilityReason = null;
      state.selectedItems = [];
      state.itemUnits = {};
      state.messages = [];
      state.chatLoading = false;
      state.activeAction = null;
      state.streamingNode = null;
      state.lastRecommendations = null;
      state.lastExchangeContext = null;
      state.originalItemPrice = null;
      state.originalItemDiscountRate = 0;
      state.originalItemProductId = null;
      state.activeReason = null;
      state.showReturnConfirm = false;
      state.customerHistory = null;
      state.returnIntentCount = 0;
      state.hasPitchedExchange = false;
      state.priceOpen = false;
      state.currentProposedProductId = null;
      state.exchangeQueue = null;
      state.exchangeSelections = null;
      state.returnSelections = null;
      state.currentExchangeItemId = null;
      state.stockShortages = null;
      state.stockShortMessage = null;
      // Product facts belong to THIS order — the next lookup must refetch,
      // or question mode would answer from the previous order's products.
      state.productInfoContext = null;
      state.productInfoLoading = false;
      // 2026-10-13 — mixed flow state; reasonDetails was never reset here.
      // stepSeq stays monotonic.
      state.itemPlan = {};
      state.planDraft = null;
      state.stepChanging = false;
      state.reasonDetails = {};
      state.pendingRecommendationsRender = false;
      state.singleLineId = null;
      // Fix round 1: anything still in flight for this session goes stale
      // (see resetChatState).
      state.chatGen++;
      state.stepSeq++;
      state.screen = 'lookup';
      var app = document.getElementById('mojito-returns-app');
      if (app) renderLookupScreen(app);
    }

    var closeBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-modal__close',
      onClick: dismissAndReset
    }, t('successModalClose'));

    card.appendChild(checkmark);
    card.appendChild(title);
    if (opts && opts.summary) {
      card.appendChild(el('p', { className: 'mojito-returns-modal__body mojito-returns-modal__body--summary' }, opts.summary));
    }
    card.appendChild(body);
    card.appendChild(closeBtn);
    backdrop.appendChild(card);

    // Click outside card to dismiss
    backdrop.addEventListener('click', function (e) {
      if (e.target === backdrop) dismissAndReset();
    });
    // Escape key to dismiss
    document.addEventListener('keydown', function onKey(e) {
      if (e.key === 'Escape' && document.body.contains(backdrop)) {
        document.removeEventListener('keydown', onKey);
        dismissAndReset();
      }
    });

    document.body.appendChild(backdrop);
    // Focus the close button for keyboard users
    setTimeout(function () { closeBtn.focus(); }, 50);
  }

  function renderReturnConfirmButton() {
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;

    // In queue mode the button scopes to the current item (mark for return,
    // advance to next). In single-item mode it opens the global return modal.
    // When the item(s) in play are final-sale, the button says store credit
    // so the customer is never promised a refund.
    var label;
    if (isInItemQueueMode()) {
      var curQueueId = state.currentExchangeItemId
        || (state.exchangeQueue && state.exchangeQueue[0]);
      label = curQueueId && isFinalSaleItem(curQueueId)
        ? t('confirmStoreCredit')
        : t('returnThisItem');
    } else {
      var allCredit = state.selectedItems.length > 0 && state.selectedItems.every(isFinalSaleItem);
      label = allCredit ? t('confirmStoreCredit') : t('confirmReturn');
    }

    var btn = el('button', {
      type: 'button',
      className: 'mojito-returns-chat__confirm-btn mojito-returns-chat__confirm-btn--return',
      onClick: function (e) {
        // 2026-10-13 — mixed flow (fix round 2): outside a walk-through this
        // button starts something a running turn would refuse (the append
        // path's handleAction, or the modal's "Exchange Instead", which sent
        // nothing and left this button disabled). Refuse first, before the
        // button disables itself, so it stays usable once the reply ends.
        if (!isInItemQueueMode() && (turnInFlight() || state.stepChanging)) return;
        // Prevent the button from being re-clicked in the millisecond it
        // takes the modal to open.
        btn.disabled = true;
        if (isInItemQueueMode()) {
          var ok = markCurrentItemAsReturn();
          if (!ok) {
            // Fallback: go to review modal so the customer always sees progress
            btn.disabled = false;
            openItemReviewModal();
          }
        } else if (appendPending()) {
          // Fix round 1: an earlier single-path button pressed while a newly
          // ticked line waits — that line's Return, not the modal (which
          // would file it under the earlier line's reason).
          handleAction('return');
        } else {
          // Fix round 2: the modal hands the button back when it closes
          // without Confirm (×, Escape, the backdrop, Exchange Instead).
          openReturnConfirmModal(btn);
        }
      }
    }, label);

    var wrapper = el('div', { className: 'mojito-returns-chat__confirm-wrapper' }, [btn]);
    messagesEl.appendChild(wrapper);
    scrollToBottom();
  }

  /**
   * Return confirmation modal. Replaces the inline createReturn() summary so
   * the customer sees the full order with items highlighted vs. excluded
   * before committing — matches REDO's "review everything" pattern.
   *
   * Footer:
   *   Exchange Instead  — closes modal, switches flow to exchange
   *   Confirm Return    — fires the real /api/create-return call
   *
   * 2026-10-13 — mixed flow (fix round 2): opener, when given, is the chat
   * button that opened the modal; it is enabled again whenever the modal
   * closes without Confirm.
   */
  function openReturnConfirmModal(opener) {
    if (!state.order || !state.order.line_items) {
      if (opener) opener.disabled = false;   // fix round 2: no modal, button back
      addMessage('assistant', 'Something went wrong. Please try again.');
      return;
    }
    if (state.selectedItems.length === 0) {
      if (opener) opener.disabled = false;
      addMessage('assistant', 'Please select at least one item to return.');
      return;
    }

    var app = document.getElementById('mojito-returns-app');
    // Working set — mutated as the customer toggles items in the modal.
    // Committed to state.selectedItems only when they hit Confirm / Exchange Instead.
    // Closing without commit preserves the original selection.
    // 2026-10-13 — mixed flow (fix round 1): every ticked line, pre-selected,
    // as before — a line already decided as EXCHANGE included. This modal is
    // reached only outside a walk-through (the single path, the typed and
    // AI-label fallbacks), where the customer has just asked to RETURN; Confirm
    // converts the line through commitSelection. Leaving those lines out
    // opened an empty modal on a single-path exchange. Only a line that
    // cannot be returned is left out (it is not listed below either).
    var workingSelected = state.selectedItems.filter(function (id) {
      var li = orderLineItem(id);
      return !!li && li.eligible !== false;
    }).map(function (id) { return String(id); });
    // Fix round 2: what was ticked when the modal opened, and the ONE line
    // the chip on file (activeReason) was given for — the single path's
    // pinned line, else the first of the working set. A line included here
    // was filed under that chip; it now gets its own prior reason or 'other'.
    var tickedAtOpen = workingSelected.slice();
    var reasonLineId = state.singleLineId != null ? String(state.singleLineId) : (workingSelected[0] || null);
    // Fix round 2: a line the customer EXCLUDED here that still has a
    // decision once the commit is done — an exchange (Confirm only resets the
    // returns), or any decision on Exchange Instead — keeps it AND stays
    // ticked: decisions stay a subset of the ticked lines. (Excluding a
    // single-path exchange unticked it while it stayed in the request.)
    function keepDecidedTicked() {
      tickedAtOpen.forEach(function (id) {
        if (workingSelected.indexOf(id) === -1 && decisionOf(id) && !isItemSelected(id)) {
          state.selectedItems.push(id);
        }
      });
    }

    var backdrop = el('div', {
      className: 'mojito-returns-rcm',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'mojito-returns-rcm-title'
    });
    var card = el('div', { className: 'mojito-returns-rcm__card' });
    backdrop.appendChild(card);

    // Fix round 2: the opener disabled itself on the click; every way out
    // but Confirm hands it back, so the customer never loses the control.
    var confirmed = false;
    function close() {
      backdrop.remove();
      document.removeEventListener('keydown', onKey);
      if (opener && !confirmed) opener.disabled = false;
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    backdrop.addEventListener('click', function (e) { if (e.target === backdrop) close(); });

    // Close button
    card.appendChild(el('button', {
      type: 'button',
      className: 'mojito-returns-rcm__close',
      'aria-label': t('pdmClose'),
      onClick: close
    }, '\u00D7'));

    // Header — with the store-credit policy note whenever a final-sale
    // item could end up in this return (any final-sale line on the order).
    var headerChildren = [
      el('h3', { className: 'mojito-returns-rcm__title', id: 'mojito-returns-rcm-title' }, 'Confirm your return'),
      el('p', { className: 'mojito-returns-rcm__subtitle' },
        'Click any item to include or exclude it from the return, then choose an action.')
    ];
    var hasFinalSaleLine = state.order.line_items.some(function (li) {
      return li.final_sale && li.eligible !== false;
    });
    if (hasFinalSaleLine) {
      headerChildren.push(el('p', { className: 'mojito-returns-rcm__subtitle' }, t('finalSaleNote')));
    }
    card.appendChild(el('div', { className: 'mojito-returns-rcm__header' }, headerChildren));

    // Items list — each item is clickable to toggle include/exclude right here
    var itemsList = el('div', { className: 'mojito-returns-rcm__items' });
    // Keep per-row references so we can update them on toggle without
    // re-rendering the whole list (faster, preserves scroll position).
    var rowRefs = [];

    function updateFooterState() {
      var count = workingSelected.length;
      confirmBtn.disabled = count === 0;
      exchangeBtn.disabled = count === 0;
      // All-final-sale selections are store credit, not a refund — say so
      // on the button itself. Both states localized so the label doesn't
      // flip language as the selection changes on /es/.
      var allCredit = count > 0 && workingSelected.every(isFinalSaleItem);
      var label = allCredit ? t('confirmStoreCredit') : t('confirmReturn');
      confirmBtn.textContent = count > 1 ? label + ' (' + count + ')' : label;
    }

    function toggleItem(itemId) {
      var idStr = String(itemId);
      var idx = workingSelected.indexOf(idStr);
      if (idx === -1) {
        workingSelected.push(idStr);
      } else {
        workingSelected.splice(idx, 1);
      }
      // Update just this row's visual state + badge
      var ref = rowRefs.find(function (r) { return r.id === idStr; });
      if (ref) {
        var nowSelected = workingSelected.indexOf(idStr) !== -1;
        ref.row.className = 'mojito-returns-rcm__item mojito-returns-rcm__item--clickable'
          + (nowSelected ? ' is-selected' : ' is-excluded');
        ref.row.setAttribute('aria-pressed', nowSelected ? 'true' : 'false');
        ref.badge.className = 'mojito-returns-rcm__item-badge' + (nowSelected ? ' is-active' : '');
        ref.badge.textContent = nowSelected
          ? (ref.credit ? t('storeCreditBadge') : t('returningBadge'))
          : t('notIncludedBadge');
        if (ref.qtyEcho) ref.qtyEcho.hidden = !nowSelected;
      }
      updateFooterState();
    }

    state.order.line_items.forEach(function (item) {
      // 2026-10-13: never list a line that cannot be returned.
      if (item.eligible === false) return;
      var idStr = String(item.id);
      var isSelected = workingSelected.indexOf(idStr) !== -1;

      // Whole row is a button — click anywhere to toggle
      var row = el('button', {
        type: 'button',
        className: 'mojito-returns-rcm__item mojito-returns-rcm__item--clickable'
          + (isSelected ? ' is-selected' : ' is-excluded'),
        'aria-pressed': isSelected ? 'true' : 'false',
        'aria-label': (isSelected ? 'Exclude ' : 'Include ') + item.title,
        onClick: function () { toggleItem(item.id); }
      });

      var thumb;
      if (item.image) {
        var src = item.image.indexOf('?') !== -1 ? item.image + '&width=160' : item.image + '?width=160';
        thumb = el('img', {
          className: 'mojito-returns-rcm__item-thumb',
          src: src,
          alt: item.title,
          loading: 'lazy'
        });
      } else {
        thumb = el('div', { className: 'mojito-returns-rcm__item-thumb mojito-returns-rcm__item-thumb--empty' });
      }

      var infoChildren = [
        el('div', { className: 'mojito-returns-rcm__item-title' }, item.title)
      ];
      if (item.variant_title) {
        infoChildren.push(el('div', { className: 'mojito-returns-rcm__item-variant' }, item.variant_title));
      }
      // Phase 2.7 — this row printed the LIST price and no count, so a
      // discounted qty-2 line read differently here than on the order card
      // one screen earlier. Same nodes as everywhere else now: paid per unit,
      // list struck through, count, line total.
      infoChildren.push(el('div', { className: 'mojito-returns-rcm__item-price' },
        priceLineNodes(item, orderedUnits(item))));
      // Read-only echo of the order-card count — this modal picks WHICH items,
      // not how many, and the number stays editable one screen on. Hidden
      // while the row is excluded: "2 of 2 selected" under a NOT INCLUDED
      // badge contradicts itself.
      var qtyEcho = null;
      if (returnableUnits(item) > 1) {
        qtyEcho = el('div', { className: 'mojito-returns-rcm__item-variant' },
          t('qtySelectedOf')
            .replace('{n}', String(chosenUnits(item.id)))
            .replace('{max}', String(returnableUnits(item))));
        qtyEcho.hidden = !isSelected;
        infoChildren.push(qtyEcho);
      }
      var info = el('div', { className: 'mojito-returns-rcm__item-info' }, infoChildren);

      var isCredit = isFinalSaleItem(item.id);
      var badge = el('div', {
        className: 'mojito-returns-rcm__item-badge' + (isSelected ? ' is-active' : '')
      }, isSelected ? (isCredit ? t('storeCreditBadge') : t('returningBadge')) : t('notIncludedBadge'));

      row.appendChild(thumb);
      row.appendChild(info);
      row.appendChild(badge);
      itemsList.appendChild(row);
      rowRefs.push({ id: idStr, row: row, badge: badge, credit: isCredit, qtyEcho: qtyEcho });
    });
    card.appendChild(itemsList);

    // Footer actions — disabled when workingSelected is empty
    var footer = el('div', { className: 'mojito-returns-rcm__footer' });

    var exchangeBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-rcm__btn mojito-returns-rcm__btn--ghost',
      onClick: function () {
        // 2026-10-13 — mixed flow (fix round 2): handleChatSend and
        // handleAction refuse while a reply streams or a kit loads. Refuse
        // here first, before anything is committed or the modal closes —
        // it used to close, flip the flow to exchange and send nothing.
        if (turnInFlight() || state.stepChanging) return;
        // Commit working set before switching flow so the exchange
        // path sees the items the customer currently has checked.
        state.selectedItems = workingSelected.map(function (id) {
          // IDs may be numeric originally; keep as strings in state
          return id;
        });
        keepDecidedTicked();   // fix round 2: excluded decided lines stay ticked
        // 2026-10-13: the order card's ticks follow the committed set.
        updateActionStates();
        close();
        state.activeAction = 'exchange';
        state.returnIntentCount = 0;
        // Fix round 2: a line included here beside a decision already on
        // file takes the Exchange card's append path — its own step, reason
        // and kit. Through handleChatSend it got a plain AI turn, and only
        // the decided line's old cards to pick from.
        // Fix round 3: so do two or more lines with nothing decided yet —
        // the plan bubble, one decision per line (handleAction's own rule).
        // Through handleChatSend only the pinned line was ever discussed and
        // filed; the line included here was dropped without a word.
        if (appendPending() || pendingIds().length > 1) {
          handleAction('exchange');
          return;
        }
        // Fix round 2: the single path's line — re-pinned when the customer
        // excluded the pinned one here.
        pinSingleLine(getExchangeContextItem());
        handleChatSend(app, 'Actually, I\'d like to exchange instead — show me my options.');
      }
    }, 'Exchange Instead');

    var confirmBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-rcm__btn mojito-returns-rcm__btn--primary',
      onClick: function () {
        // Route through the final review modal (has the email-confirm
        // gate) instead of firing the API directly. The review modal
        // reads state.returnSelections, so we populate it here in the
        // same shape the queue flow uses.
        state.selectedItems = workingSelected.slice();
        // 2026-10-13: each line keeps its OWN reason when it already had one
        // (a drained walk-through; fix round 1: or an exchange this converts)
        // — read before the reset below wipes it. A return's reason wins.
        var priorReasons = {};
        (state.exchangeSelections || []).concat(state.returnSelections || []).forEach(function (s) {
          if (s && s.reason) priorReasons[selItemId(s)] = s.reason;
        });
        // Fresh replacement: the item-picker represents the EXACT set the
        // customer wants to return ("these and only these"). Reset first,
        // then commitSelection per item — which also removes any stale
        // exchangeSelections for the same items (mutual exclusivity).
        state.returnSelections = [];
        workingSelected.forEach(function (itemId) {
          var origItem = (state.order && Array.isArray(state.order.line_items))
            ? state.order.line_items.find(function (li) { return String(li.id) === String(itemId); })
            : null;
          // Fix round 2: the chip on file belongs to ONE line (reasonLineId);
          // any other line included here never inherits it.
          var k = String(itemId);
          commitSelection(itemId, 'return', {
            originalItemId: k,
            reason: priorReasons[k] || (k === reasonLineId ? state.activeReason : null) || 'other',
            storeCredit: isFinalSaleItem(itemId),
            originalItem: origItem ? {
              id: origItem.id,
              title: origItem.title,
              variant_title: origItem.variant_title || '',
              price: origItem.price,
              image: origItem.image
            } : { id: itemId, title: 'Item' }
          });
        });
        keepDecidedTicked();   // fix round 2: an excluded exchange stays, ticked
        updateActionStates();
        confirmed = true;
        close();
        openItemReviewModal();
      }
    }, 'Confirm Return');

    footer.appendChild(exchangeBtn);
    footer.appendChild(confirmBtn);
    card.appendChild(footer);

    document.body.appendChild(backdrop);
    updateFooterState();
    setTimeout(function () { confirmBtn.focus(); }, 50);
  }

  /**
   * Combined review modal for the multi-item queue. Shows two sections:
   *   - Exchanges: original → replacement pairs (from state.exchangeSelections)
   *   - Returns: items being returned (from state.returnSelections)
   * The customer can scan both lists and hit Submit to fire the right API
   * calls (create-exchange with array + create-return with line items).
   */
  /**
   * Post a chat bubble with a "Reopen Review" button after the customer
   * dismisses the final review modal without submitting (backdrop click,
   * Escape, × button, or Cancel). Selections stay in state, so clicking
   * the button just re-runs openItemReviewModal() with everything intact.
   * Guards against duplicates if they dismiss multiple times.
   */
  function showReopenReviewPrompt() {
    var exchanges = state.exchangeSelections || [];
    var returns = state.returnSelections || [];
    // Nothing to come back to — don't show the prompt.
    if (exchanges.length === 0 && returns.length === 0) return;

    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;

    // Remove any existing reopen prompt so repeated dismisses don't stack.
    var existing = messagesEl.querySelectorAll('.mojito-returns-chat__reopen-review');
    existing.forEach(function (n) { n.remove(); });

    var counts = [];
    if (exchanges.length) counts.push(exchanges.length + ' exchange' + (exchanges.length > 1 ? 's' : ''));
    if (returns.length) counts.push(returns.length + ' return' + (returns.length > 1 ? 's' : ''));
    var summary = counts.join(' + ');
    var textForTranscript = 'Review closed — your ' + summary + ' are saved. Click "Reopen Review" to submit.';
    state.messages.push({ role: 'assistant', text: textForTranscript });

    var wrap = el('div', {
      className: 'mojito-returns-chat__message mojito-returns-chat__message--assistant mojito-returns-chat__reopen-review is-entering'
    });
    // 2026-10-13: localized — the "Add another item" and untick flows lean
    // on this prompt. The transcript line above stays English.
    // Fix round 2: final-sale returns count as store credit, as in the plan
    // summary and the review badge (mixedCountsLine) — not "to return".
    var shown = mixedCountsLine(exchanges, returns);
    wrap.appendChild(el('div', {
      className: 'mojito-returns-chat__reopen-review-text'
    }, t('reopenSaved').replace('{summary}', function () { return shown; })));
    wrap.appendChild(el('button', {
      type: 'button',
      className: 'mojito-returns-chat__reopen-review-btn',
      onClick: function () {
        wrap.remove();
        openItemReviewModal();
      }
    }, t('reopenBtn')));
    // Phase 2.9: the lines the last submit flagged short get their own way
    // back in — the sold-out advice used to end here, in a chat where the
    // next pick could not be routed to the right line.
    stockShortIds().forEach(function (id) {
      var sel = exchanges.filter(function (x) { return selItemId(x) === id; })[0];
      if (!sel) return;
      var item = orderLineItem(id) || sel.originalItem || {};
      wrap.appendChild(el('button', {
        type: 'button',
        className: 'mojito-returns-chat__reopen-review-btn mojito-returns-chat__reopen-review-btn--change',
        onClick: function () {
          wrap.remove();
          requeueExchangeItem(id);
        }
      }, t('reopenChange').replace('{item}', item.title || 'item')));
    });
    messagesEl.appendChild(wrap);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () { wrap.classList.remove('is-entering'); });
    });

    scrollToBottom();
  }

  // 2026-10-13 — mixed flow: opts is passed only by the in-place "Return
  // instead" re-render — {creditChoice, email, focusId} carry the other rows'
  // credit toggles, the edited email and the focus across it. Every other
  // caller passes nothing, so a reopen or a failed submit still starts with
  // every credit toggle off (the 28JUL invariant).
  function openItemReviewModal(opts) {
    var app = document.getElementById('mojito-returns-app');
    var exchanges = state.exchangeSelections || [];
    var returns = state.returnSelections || [];
    if (exchanges.length === 0 && returns.length === 0) {
      addMessage('assistant', 'Nothing to submit — you haven\'t made any selections yet.');
      return;
    }

    // Dismiss any stale reopen prompt — we're about to show the modal again.
    var prevPrompts = document.querySelectorAll('.mojito-returns-chat__reopen-review');
    prevPrompts.forEach(function (n) { n.remove(); });

    var backdrop = el('div', {
      className: 'mojito-returns-rcm',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'mojito-returns-xrm-title'
    });
    var card = el('div', { className: 'mojito-returns-rcm__card' });
    backdrop.appendChild(card);

    // close(silent) — silent=true when nothing needs recovering: a
    // successful submit, or a "Change" that continues the walk-through in
    // chat. All other dismiss paths pass false so the customer gets a
    // "Reopen Review" prompt in chat — their selections stay in state.
    // 2026-10-13: while a submit is in flight, ×, the backdrop, Escape and
    // Cancel cannot close the modal — closing it then allowed "Reopen
    // Review" to fire a second create-request. The success close(true)
    // still closes.
    var busy = false;
    function close(silent) {
      if (busy && !silent) return;
      backdrop.remove();
      document.removeEventListener('keydown', onKey);
      if (!silent) showReopenReviewPrompt();
    }
    function onKey(e) { if (e.key === 'Escape') close(false); }
    document.addEventListener('keydown', onKey);
    backdrop.addEventListener('click', function (e) { if (e.target === backdrop) close(false); });

    card.appendChild(el('button', {
      type: 'button',
      className: 'mojito-returns-rcm__close',
      'aria-label': t('pdmClose'),
      onClick: function () { close(false); }
    }, '\u00D7'));

    // Title + subtitle reflect what's actually in the review (exchanges,
    // returns, or a mix) so the customer sees a summary they recognize.
    // 2026-10-13: localized (they were hard-coded English).
    var titleText;
    if (exchanges.length > 0 && returns.length > 0) {
      titleText = t('reviewTitleMixed').replace('{n}', String(exchanges.length + returns.length));
    } else if (exchanges.length > 0) {
      titleText = t('reviewTitleExchange');
    } else {
      titleText = t('reviewTitleReturn');
    }
    var subtitleText;
    if (exchanges.length > 0 && returns.length > 0) {
      subtitleText = t('reviewSubMixed');
    } else if (exchanges.length > 0) {
      subtitleText = t('reviewSubExchange');
    } else {
      subtitleText = t('reviewSubReturn');
    }
    // Whether a return row resolves to store credit (final sale). Prefer the
    // committed flag; fall back to the live order data for entries committed
    // before this build shipped (e.g. a restored session).
    function isCreditSel(sel) {
      if (sel.storeCredit) return true;
      var id = sel.originalItemId || (sel.originalItem && sel.originalItem.id);
      return isFinalSaleItem(id);
    }

    // Per-item store-credit choices (5% bonus), keyed by line item id and
    // kept LOCAL to this modal — never written onto state.returnSelections,
    // so a failed submit or a reopened review can never leave a refund row
    // silently converted to credit. The choices ride the submit call.
    var creditChoice = {};
    var creditOfferBtns = [];
    function selKey(sel) {
      return String(sel.originalItemId || (sel.originalItem && sel.originalItem.id) || '');
    }
    // What this line would actually refund: the per-unit price the customer
    // paid, times the units they chose to send back (Phase 2.6). A qty-2 row
    // refunding one unit's worth was the old behavior and it under-promised.
    function refundCentsFor(sel, unitsOverride) {
      var li = orderLineItem(sel);
      if (!li) return 0;
      var units = unitsOverride != null ? unitsOverride : selQuantity(sel);
      return paidUnitCents(li) * units;
    }
    var fmtMoney = fmtCents;
    // Phase 2.6 — units picker for a multi-unit line, rendered ON the row so
    // there's never a question which line it belongs to. It writes the count
    // straight onto the selection; submit reads it back through selQuantity,
    // which re-clamps against what's still returnable. onChange re-renders
    // that row's money in the same tick, so the price, the refund promise and
    // the 5% offer can never disagree with the count above them.
    var qtyControls = [];
    // Phase 2.9: exchange rows by line id, so a stock rejection can point at
    // the row it is about; and the per-row "Change" buttons, frozen with
    // everything else while a submit is in flight.
    var xRows = {};
    var changeBtns = [];
    // 2026-10-13: each return row's "Exchange instead", by line id — the
    // in-place "Return instead" re-render puts focus on the row that moved.
    var rowSwitchBtns = {};
    // Phase 2.7: the picker itself moved to buildUnitsPicker so this modal and
    // the order card can never drift apart. Everything specific to the review
    // step — writing onto the selection, the freeze list — stays here.
    function buildQtyPicker(sel, max, onChange) {
      return buildUnitsPicker({
        prefix: 'mojito-returns-rcm',
        labelText: t('qtyLabel'),
        max: max,
        controls: qtyControls,
        onChange: onChange,
        get: function () { return selQuantity(sel); },
        set: function (n) {
          sel.quantity = n;
          sel.quantity = selQuantity(sel);
          // Phase 2.7: mirror the edit back onto the order-card store so ONE
          // number exists. Without this, a customer who cut 2 down to 1 here,
          // closed the modal and then picked a different replacement got a
          // fresh selection re-stamped from the stale order-card count — their
          // reduction silently reverted upward, on a live return.
          // Guarded: a selection with no resolvable id would otherwise write
          // an "undefined" key that nothing ever reads back.
          var mirrorId = sel.originalItemId || (sel.originalItem && sel.originalItem.id);
          if (mirrorId != null) setChosenUnits(mirrorId, sel.quantity);
        }
      });
    }
    // Rows the bonus offer applies to: cash-refund rows with a known price.
    // Final-sale credit is face value and never earns the bonus.
    var offerRows = returns.filter(function (sel) {
      return !isCreditSel(sel) && refundCentsFor(sel) > 0;
    });
    // Phase 2.5: exchange rows that settle money BACK to the customer get the
    // same choice. Same creditChoice map, same line-item key, so one review
    // can mix both legs and submitAllDecisions needs no second channel.
    // Final-sale originals are excluded — their difference is store credit by
    // policy, so there is nothing to choose (stated, not offered).
    var xOfferRows = exchanges.filter(function (sel) {
      return !isCreditSel(sel) && exchangeDifferenceCents(sel) < -1;
    });
    // 2026-10-13: only the in-place "Return instead" re-render passes a
    // seed — the other rows' toggles, filtered to rows that still qualify.
    if (opts && opts.creditChoice) {
      offerRows.concat(xOfferRows).forEach(function (s) {
        var k = selKey(s);
        if (opts.creditChoice[k]) creditChoice[k] = true;
      });
    }

    var reviewHeader = [
      el('h3', { className: 'mojito-returns-rcm__title', id: 'mojito-returns-xrm-title' }, titleText),
      el('p', { className: 'mojito-returns-rcm__subtitle' }, subtitleText)
    ];
    if (returns.some(isCreditSel)) {
      reviewHeader.push(el('p', { className: 'mojito-returns-rcm__subtitle' }, t('finalSaleNote')));
    }
    if (offerRows.length + xOfferRows.length > 0) {
      reviewHeader.push(el('p', { className: 'mojito-returns-rcm__subtitle' }, t('creditOfferHint')));
    }
    card.appendChild(el('div', { className: 'mojito-returns-rcm__header' }, reviewHeader));

    var itemsList = el('div', { className: 'mojito-returns-rcm__items' });

    // Exchange section — "original → new" pair per row
    if (exchanges.length > 0) {
      if (returns.length > 0) {
        itemsList.appendChild(el('div', { className: 'mojito-returns-rcm__section-label' },
          t('reviewSectionExchanging').replace('{n}', String(exchanges.length))));
      }
      exchanges.forEach(function (sel) {
        // Phase 2.6: the LIVE line, for quantity + paid price (see the return
        // rows below for why the cached copy isn't enough).
        var orig = orderLineItem(sel) || sel.originalItem || {};
        var rec = sel.recommendation || {};
        // Phase 2.7 — the count is now chosen on the order card, BEFORE a
        // replacement exists, so "both of them" can outrun what's on the
        // shelf. Cap at the replacement's own stock when the recommendation
        // carries a real number; absent or zero means "unknown", and an
        // unknown must never silently cut the customer down to one unit.
        var recStock = Number(rec.quantity);
        var maxUnits = returnableUnits(orig);
        // What the customer asked for, before stock gets a say — read now so
        // the note below can tell them their count was cut rather than
        // leaving a quietly-changed price line to imply it.
        var wantedUnits = selQuantity(sel);
        var stockCapped = false;
        if (isFinite(recStock) && recStock > 0 && recStock < maxUnits) {
          maxUnits = recStock;
          stockCapped = true;
        }
        // selQuantity only clamps against what's RETURNABLE, so a stock cap
        // has to be written onto the selection itself — otherwise the money
        // below is computed from a count the picker will not let them reach.
        if (Number(sel.quantity) > maxUnits) sel.quantity = maxUnits;
        var row = el('div', { className: 'mojito-returns-rcm__xpair' });
        var xRowId = selItemId(sel);
        if (xRowId) xRows[xRowId] = row;
        var origPriceEl = el('div', { className: 'mojito-returns-rcm__item-price' });

        function buildSide(title, variantTitle, priceNode, imageUrl, className) {
          var thumb;
          if (imageUrl) {
            var src = imageUrl.indexOf('?') !== -1 ? imageUrl + '&width=160' : imageUrl + '?width=160';
            thumb = el('img', {
              className: 'mojito-returns-rcm__item-thumb',
              src: src, alt: title, loading: 'lazy'
            });
          } else {
            thumb = el('div', { className: 'mojito-returns-rcm__item-thumb mojito-returns-rcm__item-thumb--empty' });
          }
          var infoChildren = [el('div', { className: 'mojito-returns-rcm__item-title' }, title)];
          if (variantTitle) infoChildren.push(el('div', { className: 'mojito-returns-rcm__item-variant' }, variantTitle));
          if (priceNode) infoChildren.push(priceNode);
          var info = el('div', { className: 'mojito-returns-rcm__item-info' }, infoChildren);
          return el('div', { className: 'mojito-returns-rcm__xside ' + className }, [thumb, info]);
        }

        row.appendChild(buildSide(orig.title || 'Item', orig.variant_title, origPriceEl, orig.image, 'is-original'));
        row.appendChild(el('div', { className: 'mojito-returns-rcm__xarrow', 'aria-hidden': 'true' }, '\u2192'));
        row.appendChild(buildSide(rec.title || 'Replacement', null,
          rec.price ? el('div', { className: 'mojito-returns-rcm__item-price' }, '$' + rec.price) : null,
          rec.image, 'is-replacement'));

        // Phase 2.5: money-back exchanges say WHERE the money goes, and let
        // the customer change it. Before this the portal said "credit" and
        // whoever processed the exchange in Shopify admin picked the actual
        // method by hand \u2014 two different promises from one sentence.
        // Phase 2.6: the amount tracks the units picker below it.
        var xKey = selKey(sel);
        var xFinal = isCreditSel(sel);
        var xNote = el('div', { className: 'mojito-returns-rcm__xdiff-note' });
        var xLabel = null, xAmount = null, xBtn = null;
        if (!xFinal) {
          xLabel = el('span', { className: 'mojito-returns-rcm__credit-offer-label' }, t('creditSwitchOff'));
          xAmount = el('span', { className: 'mojito-returns-rcm__credit-offer-amount' });
          xBtn = el('button', {
            type: 'button',
            className: 'mojito-returns-rcm__credit-offer',
            'aria-pressed': 'false',
            onClick: function () {
              var on = !creditChoice[xKey];
              if (on) { creditChoice[xKey] = true; } else { delete creditChoice[xKey]; }
              xBtn.classList.toggle('is-on', on);
              xBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
              xLabel.textContent = on ? t('creditSwitchOn') : t('creditSwitchOff');
              syncXRow();
            }
          }, [xLabel, xAmount]);
          // 2026-10-13: a choice carried across the in-place re-render.
          if (creditChoice[xKey]) {
            xBtn.classList.add('is-on');
            xBtn.setAttribute('aria-pressed', 'true');
            xLabel.textContent = t('creditSwitchOn');
          }
          creditOfferBtns.push(xBtn);
        }

        // Phase 2.7: the stock-cap note is created HERE, above syncXRow, so
        // syncXRow can rewrite it. Built after the picker it explains, it was
        // frozen at first render and went on asserting "exchanging 2, not 3"
        // after the customer had stepped the picker down to 1.
        var qtyNote = stockCapped
          ? el('div', { className: 'mojito-returns-rcm__qty-note' })
          : null;

        // Single recompute for price, settlement sentence and offer amount \u2014
        // the count above them can never disagree with the money below.
        function syncXRow() {
          var units = selQuantity(sel);
          while (origPriceEl.firstChild) origPriceEl.removeChild(origPriceEl.firstChild);
          priceLineNodes(orig, units).forEach(function (n) { origPriceEl.appendChild(n); });

          // Phase 2.7: recompute the cap sentence from the LIVE count, so the
          // only words on this row that state a number stay true as it moves.
          if (qtyNote) {
            qtyNote.textContent = (units < wantedUnits ? t('qtyStockCut') : t('qtyStockCap'))
              .replace('{max}', String(maxUnits))
              .replace('{n}', String(units))
              .replace('{want}', String(wantedUnits));
          }

          var backCents = -exchangeDifferenceCents(sel, units);
          var owed = backCents > 1;
          // Phase 2.7: the OTHER direction \u2014 the customer owes money \u2014 used to
          // render nothing at all here, which was survivable while the count
          // was fixed at 1. It no longer is: the inline confirm card quotes an
          // amount for the order-card count, and the stock cap can cut that
          // count after the card is gone from the transcript. This row is the
          // last screen before submit, so it has to carry the corrected figure.
          // Phase 2.5's silence was about not naming the FORM of a refund; an
          // amount the customer pays has no such ambiguity.
          var payCents = -backCents;
          if (!owed && payCents > 1) {
            xNote.style.display = '';
            xNote.textContent = t('xDiffOwedLine').replace('{amount}', fmtMoney(payCents));
            if (xBtn) xBtn.style.display = 'none';
            return;
          }
          xNote.style.display = owed ? '' : 'none';
          if (xBtn) xBtn.style.display = owed ? '' : 'none';
          if (!owed) {
            // 2026-09-11 — same-item price match: a pricier color/size of
            // the same product is billed at the paid price, so the two price
            // columns disagree on purpose. Say why, in one line.
            if (isSameProductRec(orig, rec) &&
                Math.round(parseFloat(rec.price) * 100) > paidUnitCents(orig)) {
              xNote.style.display = '';
              xNote.textContent = t('xDiffMatchedLine');
            }
            return;
          }
          if (xFinal) {
            xNote.textContent = t('xDiffFinalSaleLine').replace('{amount}', fmtMoney(backCents));
            return;
          }
          var bonusCents = Math.round(backCents * 0.05);
          var on = !!creditChoice[xKey];
          xNote.textContent = (on ? t('xDiffCreditLine') : t('xDiffRefundLine'))
            .replace('{amount}', fmtMoney(on ? backCents + bonusCents : backCents));
          xAmount.textContent = fmtMoney(backCents + bonusCents)
            + ' (' + fmtMoney(backCents) + ' + ' + fmtMoney(bonusCents) + ' ' + t('bonusWord') + ')';
        }
        syncXRow();
        if (maxUnits > 1) row.appendChild(buildQtyPicker(sel, maxUnits, syncXRow));
        // Shown even when the cap leaves nothing to pick (stock of 1 against
        // a qty-2 line): "why can't I send both back" needs an answer.
        if (qtyNote) row.appendChild(qtyNote);
        row.appendChild(xNote);
        if (xBtn) row.appendChild(xBtn);
        // Phase 2.9 — the way OUT of a dead replacement. A sold-out rejection
        // used to say "choose a different size or color" with no control that
        // could do it; Cancel led to a chat where the next pick landed on the
        // wrong line. This re-queues THIS line and continues the walk-through
        // for it alone; the other decisions stay put.
        if (xRowId) {
          var changeBtn = el('button', {
            type: 'button',
            className: 'mojito-returns-rcm__xchange',
            'aria-label': t('xChangeAria').replace('{item}', orig.title || 'item'),
            onClick: function () {
              close(true);
              requeueExchangeItem(xRowId);
            }
          }, t('xChangeBtn'));
          changeBtns.push(changeBtn);
          // 2026-10-13 — mixed flow: two taps from the review to turn this
          // exchange into a return, through the single writer. The reason
          // and the OTHER rows' credit choices survive the in-place
          // re-render; the units come from the order-card count, not the
          // stock-capped exchange count above. setBusy freezes it.
          var xFs = isFinalSaleItem(xRowId);
          var toReturnBtn = el('button', {
            type: 'button',
            className: 'mojito-returns-rcm__xchange mojito-returns-rcm__xswitch',
            'aria-label': t('reviewReturnInsteadAria').replace('{item}', function () { return orig.title || 'item'; }),
            onClick: function () {
              var carry = {};
              Object.keys(creditChoice).forEach(function (k) {
                if (k !== xRowId && creditChoice[k]) carry[k] = true;
              });
              var keepEmail = emailInput.value;
              commitSelection(sel.originalItemId, 'return', {
                originalItemId: sel.originalItemId,
                originalItem: sel.originalItem || orderLineItem(xRowId) || null,
                reason: sel.reason || 'other',
                storeCredit: xFs,
                quantity: chosenUnits(xRowId)
              });
              state.itemPlan[xRowId] = 'return';
              // Same transcript line a walk-through return decision writes.
              state.messages.push({
                role: 'assistant',
                text: (xFs ? '✓ Will return for store credit: ' : '✓ Will return: ') + (orig.title || 'item')
              });
              trackEvent('item_intent_changed', { from: 'exchange', to: 'return', via: 'review', hasReason: !!sel.reason });
              close(true);
              openItemReviewModal({ creditChoice: carry, email: keepEmail, focusId: xRowId });
            }
          }, t(xFs ? 'reviewCreditInstead' : 'reviewReturnInstead'));
          changeBtns.push(toReturnBtn);
          row.appendChild(el('div', { className: 'mojito-returns-rcm__row-actions' }, [changeBtn, toReturnBtn]));
        }

        itemsList.appendChild(row);
      });
    }

    // Return section — single-item rows with "Returning" badge
    if (returns.length > 0) {
      if (exchanges.length > 0) {
        itemsList.appendChild(el('div', { className: 'mojito-returns-rcm__section-label' },
          t('reviewSectionReturning').replace('{n}', String(returns.length))));
      }
      returns.forEach(function (sel) {
        // Phase 2.6: read the LIVE line so quantity, returnable count and the
        // paid price are all present even for a selection committed before
        // those fields existed.
        var item = orderLineItem(sel) || sel.originalItem || {};
        var maxUnits = returnableUnits(item);
        var row = el('div', { className: 'mojito-returns-rcm__item is-selected' });
        var thumb;
        if (item.image) {
          var imgSrc = item.image.indexOf('?') !== -1 ? item.image + '&width=160' : item.image + '?width=160';
          thumb = el('img', { className: 'mojito-returns-rcm__item-thumb', src: imgSrc, alt: item.title, loading: 'lazy' });
        } else {
          thumb = el('div', { className: 'mojito-returns-rcm__item-thumb mojito-returns-rcm__item-thumb--empty' });
        }
        var priceEl = el('div', { className: 'mojito-returns-rcm__item-price' });
        var infoChildren = [el('div', { className: 'mojito-returns-rcm__item-title' }, item.title || 'Item')];
        if (item.variant_title) infoChildren.push(el('div', { className: 'mojito-returns-rcm__item-variant' }, item.variant_title));
        infoChildren.push(priceEl);
        var info = el('div', { className: 'mojito-returns-rcm__item-info' }, infoChildren);
        var badge = el('div', { className: 'mojito-returns-rcm__item-badge is-active' },
          isCreditSel(sel) ? t('storeCreditBadge') : t('returningBadge'));
        row.appendChild(thumb);
        row.appendChild(info);
        row.appendChild(badge);

        // Store-credit offer, ON THIS ITEM's row so there's never a question
        // which line the 5% bonus applies to. Toggles: off = cash refund
        // (default), on = customer-chosen store credit + bonus. Only shown
        // for rows that would otherwise refund to the original payment
        // method — final-sale rows are already credit at face value.
        var key = selKey(sel);
        var isCredit = isCreditSel(sel);
        var offerLabel = null, offerAmount = null, offerBtn = null;
        if (!isCredit && paidUnitCents(item) > 0) {
          offerLabel = el('span', { className: 'mojito-returns-rcm__credit-offer-label' },
            t('creditSwitchOff'));
          offerAmount = el('span', { className: 'mojito-returns-rcm__credit-offer-amount' });
          offerBtn = el('button', {
            type: 'button',
            className: 'mojito-returns-rcm__credit-offer',
            'aria-pressed': 'false',
            onClick: function () {
              var on = !creditChoice[key];
              if (on) { creditChoice[key] = true; } else { delete creditChoice[key]; }
              offerBtn.classList.toggle('is-on', on);
              offerBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
              offerLabel.textContent = on ? t('creditSwitchOn') : t('creditSwitchOff');
              badge.textContent = on ? t('storeCreditBonusBadge') : t('returningBadge');
            }
          }, [offerLabel, offerAmount]);
          // 2026-10-13: a choice carried across the in-place re-render.
          if (creditChoice[key]) {
            offerBtn.classList.add('is-on');
            offerBtn.setAttribute('aria-pressed', 'true');
            offerLabel.textContent = t('creditSwitchOn');
            badge.textContent = t('storeCreditBonusBadge');
          }
          row.classList.add('mojito-returns-rcm__item--offer');
          creditOfferBtns.push(offerBtn);
        }

        // One place recomputes everything this row promises, so the count and
        // the money can never drift apart.
        function syncRow() {
          var units = selQuantity(sel);
          while (priceEl.firstChild) priceEl.removeChild(priceEl.firstChild);
          priceLineNodes(item, units).forEach(function (n) { priceEl.appendChild(n); });
          if (offerAmount) {
            var refundCents = refundCentsFor(sel, units);
            var bonusCents = Math.round(refundCents * 0.05);
            offerAmount.textContent = fmtMoney(refundCents + bonusCents)
              + ' (' + fmtMoney(refundCents) + ' + ' + fmtMoney(bonusCents) + ' ' + t('bonusWord') + ')';
          }
        }
        syncRow();
        if (maxUnits > 1) {
          row.classList.add('mojito-returns-rcm__item--qty');   // enables wrap
          row.appendChild(buildQtyPicker(sel, maxUnits, syncRow));
        }
        if (offerBtn) row.appendChild(offerBtn);

        // 2026-10-13 — mixed flow: return rows had no control at all. This
        // re-queues the line as an exchange step that reuses the reason on
        // file; the queue drains straight back into this review.
        var rKey = selKey(sel);
        var toExBtn = el('button', {
          type: 'button',
          className: 'mojito-returns-rcm__xchange mojito-returns-rcm__rswitch',
          'aria-label': t('reviewExchangeInsteadAria').replace('{item}', function () { return item.title || 'item'; }),
          onClick: function () {
            trackEvent('item_intent_changed', { from: 'return', to: 'exchange', via: 'review', hasReason: !!sel.reason });
            close(true);
            requeueExchangeItem(sel.originalItemId, { knownReason: sel.reason, fromReturn: true });
          }
        }, t('reviewExchangeInstead'));
        changeBtns.push(toExBtn);
        rowSwitchBtns[rKey] = toExBtn;
        row.classList.add('mojito-returns-rcm__item--actions');
        row.appendChild(el('div', { className: 'mojito-returns-rcm__row-actions' }, [toExBtn]));

        itemsList.appendChild(row);
      });
    }

    // 2026-10-13 — mixed flow: catch the one-at-a-time customer at the exact
    // point the second request used to be born. Closing keeps every
    // selection (and posts the reopen prompt); the next card press on the
    // newly ticked line takes handleAction's append path.
    var others = eligibleLines().filter(function (li) { return !decisionOf(li.id); });
    if (others.length) {
      var addBtn = el('button', {
        type: 'button',
        className: 'mojito-returns-rcm__xchange',
        onClick: function () {
          trackEvent('review_add_another', { others: others.length });
          close(false);
          addMessage('assistant', t('addAnotherHint'));
          revealOrderCard(others[0].id);
        }
      }, t('reviewAddAnotherBtn'));
      changeBtns.push(addBtn);
      itemsList.appendChild(el('div', { className: 'mojito-returns-rcm__add-another' }, [
        el('span', { className: 'mojito-returns-rcm__add-another-text' }, t('reviewAddAnotherText')),
        addBtn
      ]));
    }

    card.appendChild(itemsList);

    // === Email confirmation row ===
    // Customers need a valid contact email to receive RMA updates. Show
    // on-file email as display + pencil → input swap. If no email on file
    // (or it's missing from the lookup), show input pre-expanded in error
    // state so the customer is forced to provide one before submit.
    var orderEmailRaw = '';
    if (state.order) {
      orderEmailRaw = state.order.email
        || (state.order.customer && state.order.customer.email)
        || '';
    }
    // 2026-10-13: an email edited before an in-place re-render is kept.
    var orderEmail = (opts && typeof opts.email === 'string' && opts.email.trim())
      ? opts.email.trim().toLowerCase()
      : (typeof orderEmailRaw === 'string' ? orderEmailRaw.trim().toLowerCase() : '');

    var emailRow = el('div', { className: 'mojito-returns-rcm__email-row' });
    var emailLabel = el('div', { className: 'mojito-returns-rcm__email-label' }, 'Send updates to');
    var emailText = el('span', { className: 'mojito-returns-rcm__email-text' }, orderEmail);
    var editBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-rcm__email-edit-btn',
      'aria-label': 'Edit email'
    }, '\u270E Edit');
    var emailDisplay = el('div', { className: 'mojito-returns-rcm__email-display' }, [emailText, editBtn]);
    var emailInput = el('input', {
      type: 'email',
      className: 'mojito-returns-rcm__email-input',
      value: orderEmail,
      placeholder: 'you@example.com',
      'aria-label': 'Contact email',
      autocomplete: 'email',
      inputmode: 'email',
      maxlength: '200'
    });
    var emailError = el('div', { className: 'mojito-returns-rcm__email-error' }, 'Email required to receive status updates.');

    emailRow.appendChild(emailLabel);
    emailRow.appendChild(emailDisplay);
    emailRow.appendChild(emailInput);
    emailRow.appendChild(emailError);

    function emailIsValid(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
    }
    function showEmailInput() {
      emailDisplay.style.display = 'none';
      emailInput.style.display = '';
      setTimeout(function () { try { emailInput.focus(); emailInput.select(); } catch (e) {} }, 10);
    }
    function showEmailDisplay() {
      var v = (emailInput.value || '').trim().toLowerCase();
      emailText.textContent = v;
      emailDisplay.style.display = '';
      emailInput.style.display = 'none';
      emailInput.classList.remove('is-invalid');
      emailError.style.display = 'none';
    }
    function flagEmailInvalid(msg) {
      emailDisplay.style.display = 'none';
      emailInput.style.display = '';
      emailInput.classList.add('is-invalid');
      emailError.textContent = msg;
      emailError.style.display = '';
      setTimeout(function () { try { emailInput.focus(); } catch (e) {} }, 10);
    }

    // Initial visibility: display mode if we have a valid on-file email,
    // otherwise input with red error state.
    if (orderEmail && emailIsValid(orderEmail)) {
      emailInput.style.display = 'none';
      emailError.style.display = 'none';
    } else {
      emailDisplay.style.display = 'none';
      emailInput.classList.add('is-invalid');
      emailError.style.display = '';
    }

    editBtn.addEventListener('click', function () { showEmailInput(); });
    emailInput.addEventListener('blur', function () {
      var v = (emailInput.value || '').trim();
      if (v && emailIsValid(v)) {
        showEmailDisplay();
      } else {
        emailInput.classList.add('is-invalid');
        emailError.textContent = v
          ? 'Please enter a valid email address.'
          : 'Email required to receive status updates.';
        emailError.style.display = '';
      }
    });
    emailInput.addEventListener('input', function () {
      // Clear inline error while the user is actively correcting
      if (emailInput.classList.contains('is-invalid')) {
        emailInput.classList.remove('is-invalid');
        emailError.style.display = 'none';
      }
    });
    emailInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); emailInput.blur(); }
    });

    card.appendChild(emailRow);

    // Phase 2.8 — where a rejected submit is explained. Sits directly above
    // the button that was pressed; the chat transcript also gets the message
    // but it is behind the backdrop while this modal is up.
    var submitNotice = el('div', { className: 'mojito-returns-rcm__submit-notice', role: 'alert' });
    submitNotice.hidden = true;
    card.appendChild(submitNotice);

    var footer = el('div', { className: 'mojito-returns-rcm__footer' });
    var cancelBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-rcm__btn mojito-returns-rcm__btn--ghost',
      onClick: function () { close(false); }
    }, t('reviewCancel'));
    var submitLabel = exchanges.length > 0 && returns.length > 0
      ? t('reviewSubmitAll')
      : exchanges.length > 0 ? t('reviewSubmitExchanges') : t('reviewSubmitReturns');

    // Email gate + submit. setBusy freezes everything that could change the
    // submission mid-flight, including the per-item credit toggles.
    // 2026-10-13: it also arms close()'s busy guard (busy, above).
    function setBusy(b) {
      busy = b;
      submitBtn.disabled = b;
      cancelBtn.disabled = b;
      creditOfferBtns.forEach(function (x) { x.disabled = b; });
      changeBtns.forEach(function (x) { x.disabled = b; });
      // Phase 2.6: the units picker changes what gets submitted, so it freezes
      // with everything else. Re-enabling restores each button's own limit
      // state rather than blanket-enabling a maxed-out +.
      qtyControls.forEach(function (x) {
        if (b) { x.disabled = true; } else { x.disabled = x.dataset.atLimit === '1'; }
      });
    }
    function chosenCreditIds() {
      return Object.keys(creditChoice).filter(function (k) { return creditChoice[k]; });
    }
    // Phase 2.9 — flag the exchange rows a stock rejection named, so the
    // notice and the row it is about are visibly the same thing. An empty
    // list clears every flag (a fresh attempt starts clean).
    function markShortRows(ids) {
      Object.keys(xRows).forEach(function (k) { xRows[k].classList.remove('is-short'); });
      var first = null;
      (ids || []).forEach(function (id) {
        var r = xRows[String(id)];
        if (!r) return;
        r.classList.add('is-short');
        if (!first) first = r;
      });
      if (first && typeof first.scrollIntoView === 'function') {
        try { first.scrollIntoView({ block: 'nearest' }); } catch (e) { /* older engines */ }
      }
    }
    function gatedSubmit(clickedBtn, restoreLabel, submitOpts) {
      var currentEmail = (emailInput.value || '').trim().toLowerCase();
      if (!emailIsValid(currentEmail)) {
        flagEmailInvalid(currentEmail
          ? 'Please enter a valid email address.'
          : 'Email required to receive status updates.');
        return;
      }
      setBusy(true);
      clickedBtn.textContent = t('reviewProcessing');
      submitNotice.hidden = true;
      markShortRows([]);
      submitAllDecisions(function (success, info) {
        if (success) {
          close(true);
          return;
        }
        setBusy(false);
        clickedBtn.textContent = restoreLabel;
        if (info && info.message) {
          submitNotice.textContent = info.message;
          submitNotice.hidden = false;
        }
        // Phase 2.9: point at the row(s) the sentence above is about.
        if (info && info.shortIds) markShortRows(info.shortIds);
      }, currentEmail, submitOpts);
    }
    var submitBtn = el('button', {
      type: 'button',
      className: 'mojito-returns-rcm__btn mojito-returns-rcm__btn--primary',
      onClick: function () {
        // Gate submit behind a valid contact email. Read from the input
        // directly — it's the source of truth whether display mode or
        // edit mode is currently active. The per-item store-credit picks
        // ride along as ids so nothing about the selections has to mutate.
        gatedSubmit(submitBtn, submitLabel, { creditChoiceIds: chosenCreditIds() });
      }
    }, submitLabel);
    footer.appendChild(cancelBtn);
    footer.appendChild(submitBtn);
    card.appendChild(footer);

    document.body.appendChild(backdrop);
    // Phase 2.9: a review reopened after a stock rejection still shows which
    // rows are short — derived live, so a row whose shared replacement has
    // since been freed up is no longer red — and repeats the sentence that
    // says why: red borders with no words was the Phase 2.8 defect again.
    var reopenShort = stockShortIds();
    markShortRows(reopenShort);
    if (reopenShort.length && state.stockShortMessage) {
      submitNotice.textContent = state.stockShortMessage;
      submitNotice.hidden = false;
    }
    // 2026-10-13: after "Return instead", focus lands on the row that moved
    // (its "Exchange instead"), not on Submit.
    var f = (opts && opts.focusId != null) ? rowSwitchBtns[String(opts.focusId)] : null;
    setTimeout(function () { (f || submitBtn).focus(); }, 50);
  }

  /**
   * Phase 2.8 — localized copy for the worker's insufficient_stock rejection.
   * The worker only knows the variant's option string ("WHITE / 2XL"); the
   * selection the customer made carries the full replacement title, so name
   * that. Falls back to the worker's own sentence if the shape is unfamiliar.
   */
  function insufficientStockMessage(result, exchanges) {
    var s = Array.isArray(result.shortages) && result.shortages[0];
    if (!s) return result.message || t('submitGenericError');
    var sel = (exchanges || []).find(function (x) {
      return String(x.recommendation && x.recommendation.variant_id) === String(s.newVariantId);
    });
    var title = (sel && sel.recommendation && sel.recommendation.title) || s.variantTitle || '';
    var avail = Number(s.available) > 0 ? Number(s.available) : 0;
    var rows = Number(s.rows) > 1 ? Number(s.rows) : 1;
    // Sold out → pick something else. One row over → lower its picker.
    // Several rows sharing the variant → change one of them; every picker
    // may already read 1, so the "lower" copy would point at nothing.
    var key = avail <= 0 ? 'errStockSoldOut' : rows > 1 ? 'errStockShared' : 'errStockShort';
    return t(key)
      .replace('{item}', title)
      .replace('{available}', String(avail))
      .replace('{requested}', String(s.requested))
      .replace('{rows}', String(rows));
  }

  /**
   * Submit all queue decisions in one go via the unified /api/create-request
   * endpoint. One call → one returnCreate mutation in Shopify → ONE return
   * request in the dashboard containing both refund-only items and any
   * exchange items (new variants to ship). On failure we keep the queue
   * state intact so the customer can retry.
   */
  function submitAllDecisions(onComplete, customerEmail, opts) {
    var app = document.getElementById('mojito-returns-app');
    var config = getConfig(app);
    var sessionId = getSessionId();
    var exchanges = state.exchangeSelections || [];
    var returns = state.returnSelections || [];
    // creditChoiceIds: the line items the customer switched to store credit
    // (5% bonus) at the review step — a PER-ITEM choice, so a review can mix
    // cash-refund rows and credit rows. Kept off the selection entries so a
    // failed submit or reopened review never carries a stale conversion.
    var creditChoiceIds = {};
    if (opts && Array.isArray(opts.creditChoiceIds)) {
      opts.creditChoiceIds.forEach(function (id) { creditChoiceIds[String(id)] = true; });
    }
    function choseCredit(sel) {
      // Same key derivation as the review modal's selKey — a mismatch would
      // silently drop a choice the customer was shown.
      var id = sel.originalItemId || (sel.originalItem && sel.originalItem.id);
      return !!id && !!creditChoiceIds[String(id)];
    }

    // Phase 2.8: `info` rides along on failure so the review modal can show
    // the reason where the customer is looking. Every failure message was
    // going to the chat — BEHIND the modal's backdrop — and the modal just
    // silently unfroze.
    function done(success, info) {
      if (typeof onComplete === 'function') onComplete(success, info);
    }

    if (exchanges.length === 0 && returns.length === 0) {
      done(false);
      return;
    }

    // Reason schema v2: catch-all reasons get the customer's own words
    // appended ('other-reason: sleeves too stiff'). The worker records the
    // raw string as the Shopify return note and maps unknown tokens to
    // OTHER, so the detail rides along with zero server-side coupling.
    function reasonForSubmit(sel) {
      // 2026-10-13: every row files its OWN reason. The activeReason fallback
      // is gone — it handed every reason-less queue row whatever the LAST
      // item's chip had been (item A filed with item B's reason).
      var r = sel.reason || 'other';
      if (r === 'other-reason' || r === 'other' || r === 'fit-shape') {
        var detail = state.reasonDetails &&
          state.reasonDetails[String(sel.originalItemId)];
        if (detail) r = r + ': ' + detail;
      }
      return r;
    }

    var payload = {
      sessionId: sessionId,
      orderId: state.order.id,
      chatTranscript: state.messages,
      // v2 = directional fit chips (too-small / too-large). Lets analysis
      // separate this cohort from pre-2026-08 rows whose SIZE_TOO_LARGE was
      // invented by the old doesnt-fit/wrong-size mapping.
      reasonSchema: 2,
      customerEmail: (typeof customerEmail === 'string' ? customerEmail.trim().toLowerCase() : ''),
      returns: returns.map(function (sel) {
        return {
          originalItemId: sel.originalItemId,
          // Phase 2.6: the units the customer picked at review (default 1,
          // re-clamped here against what's still returnable). This was
          // hardcoded to 1, so a customer returning both of a qty-2 line got
          // a return recorded for one of them.
          quantity: selQuantity(sel),
          reason: reasonForSubmit(sel),
          // Informational — the worker re-derives final-sale from product
          // tags and enforces store-credit marking server-side regardless.
          storeCredit: !!(sel.storeCredit || choseCredit(sel) || isFinalSaleItem(sel.originalItemId)),
          // Customer switched THIS item to store credit (5% bonus) at
          // review. The worker only honors it for non-final-sale rows.
          creditChoice: choseCredit(sel)
        };
      }),
      exchanges: exchanges.map(function (sel) {
        return {
          originalItemId: sel.originalItemId,
          newVariantId: sel.recommendation.variant_id,
          // Phase 2.6 — same story as the return leg: exchanging 2 units used
          // to submit 1, leaving the customer a unit short in Shopify.
          quantity: selQuantity(sel),
          reason: reasonForSubmit(sel),
          // Phase 2.5: the customer switched THIS exchange's price difference
          // to store credit (5% bonus) at review. Restrict-only — the worker
          // re-derives whether a difference is even owed, and final-sale
          // originals are forced to credit regardless of this flag.
          creditChoice: choseCredit(sel)
        };
      })
    };

    function clearQueueState() {
      state.exchangeQueue = null;
      state.exchangeSelections = null;
      state.returnSelections = null;
      state.currentExchangeItemId = null;
      state.stockShortages = null;
      state.stockShortMessage = null;
      // 2026-10-13 — mixed flow: after a success, a partial success,
      // already_returned or unauthorized, the ticks go too, so the half that
      // went through cannot be picked up and run again. A plan bubble left
      // open (an untick posted the reopen prompt under it) is dropped.
      state.itemPlan = {};
      state.stepChanging = false;
      state.selectedItems = [];
      state.singleLineId = null;
      if (state.planDraft) {
        if (state.planDraft.node && state.planDraft.node.parentNode) {
          state.planDraft.node.parentNode.removeChild(state.planDraft.node);
        }
        state.planDraft = null;
      }
      updateActionStates();
    }

    apiFetch(config.apiEndpoint + '/api/create-request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }, 30000)
      .then(function (res) { return res.json(); })
      .then(function (result) {
        result = result || {};
        // Phase 2.9: the worker's stock gate runs before any write, so ANY
        // answer that is not insufficient_stock proves the last rejection's
        // flags are stale (the customer fixed the row; this failure is
        // something else). Only the branch below re-sets them.
        state.stockShortages = null;
        state.stockShortMessage = null;
        var pf = result.partialFailure || {};
        var isPartial = !!(result.partial || pf.returnRequestErrors || pf.exchangeErrors);

        // FULL success — the only leg, or both legs, went through.
        if (result.success && !isPartial) {
          // 2026-10-13 — mixed flow: an exchange + a return used to be titled
          // "Return initiated" with one body for both legs. It now gets its
          // own title, a counts line and one sentence per money leg.
          var isMixed = exchanges.length > 0 && returns.length > 0;
          // Phase 2.5: if any exchange settles money back, say where it goes.
          var xBackRows = exchanges.filter(function (sel) {
            return exchangeDifferenceCents(sel) < -1;
          });
          if (isMixed) {
            renderSuccessModal('mixed', {
              summary: mixedCountsLine(exchanges, returns),
              body: mixedSuccessBody(returns, xBackRows, choseCredit)
            });
          } else {
            var modalKind = (exchanges.length > 0 && returns.length === 0) ? 'exchange' : 'return';
            // 2026-10-13 — mixed flow (fix round 2): the return body is decided
            // per row, as the mixed path's return leg is (returnBackOutcome):
            // a final-sale row is credit with no bonus, a chosen row credit
            // with one, the rest refunds — never one flag for all of them.
            var rOutcome = modalKind === 'return' ? returnBackOutcome(returns, choseCredit) : null;
            // 'credit' (with the 5% bonus) only when every row CHOSE credit; a
            // final-sale original is credit by policy with no bonus to claim
            // (2026-10-13: it used to be told it had one) — 'creditFinal'.
            // Fix round 1: decided over EVERY row ('split' when credit and
            // refund rows mix) — see exchangeBackOutcome.
            var xDifference = exchangeBackOutcome(xBackRows, choseCredit);
            renderSuccessModal(modalKind, {
              returnOutcome: rOutcome,
              exchangeDifference: xDifference
            });
          }
          trackEvent('exchange_completed', {
            orderName: state.order.name,
            exchangeCount: exchanges.length,
            returnCount: returns.length,
            mixed: isMixed
          });
          clearQueueState();
          done(true);
          return;
        }

        // PARTIAL success — one leg succeeded, the other failed. Be honest:
        // never show the success celebration. Tell the customer exactly what
        // didn't go through and that we'll finish it, then close out so they
        // aren't looped into re-submitting the half that already succeeded.
        if (result.success && isPartial) {
          var partialMsg = t('partialSuccess');
          if (Array.isArray(result.partialMessages) && result.partialMessages.length) {
            partialMsg += ' (' + result.partialMessages.join(' ') + ')';
          }
          addMessage('assistant', partialMsg);
          clearQueueState();
          done(true);
          return;
        }

        // ALREADY RETURNED — calm and specific, never "try again". Clear the
        // selections so the no-longer-returnable item can't be re-submitted.
        if (result.code === 'already_returned') {
          addMessage('assistant', t('errAlreadyReturned'));
          clearQueueState();
          done(true);
          return;
        }

        // SESSION EXPIRED — the order-bound token is no longer valid. Clear it
        // and the queue so the customer can't loop on a dead token; they're
        // told to look up the order again (which mints a fresh one).
        if (result.code === 'unauthorized') {
          addMessage('assistant', result.message || t('sessionExpired'));
          state.authToken = null;
          clearQueueState();
          done(true);
          return;
        }

        // INSUFFICIENT STOCK — the worker re-checked the replacement's
        // inventory at submit and it can't cover the count. Nothing was
        // filed. Keep the queue: the fix is one picker step away, inside the
        // modal that is still open.
        if (result.code === 'insufficient_stock') {
          var stockMsg = insufficientStockMessage(result, exchanges);
          // Phase 2.9: remember the shortages and the sentence. Which lines
          // are short is derived from them (stockShortIds) — the review
          // modal highlights those, the reopen prompt offers a "Change" for
          // each, and a pick made after the queue drained routes to them.
          state.stockShortages = Array.isArray(result.shortages) ? result.shortages : [];
          state.stockShortMessage = stockMsg;
          var shortIds = stockShortIds();
          if (!shortIds.length) {
            // Variant ids failed to line up with the selections (unexpected
            // payload shape): flag what the worker named, this once.
            state.stockShortages.forEach(function (sh) {
              var shId = sh && sh.originalItemId != null ? String(sh.originalItemId) : '';
              if (shId && shortIds.indexOf(shId) === -1) shortIds.push(shId);
            });
          }
          addMessage('assistant', stockMsg);
          done(false, { code: result.code, message: stockMsg, shortIds: shortIds });
          return;
        }

        // Other failure — prefer the worker's human message; keep the queue
        // intact so a genuinely transient issue can be retried.
        var otherMsg = result.message || t('submitGenericError');
        addMessage('assistant', otherMsg);
        done(false, { code: result.code || 'error', message: otherMsg });
      })
      .catch(function () {
        // apiFetch only rejects on network error / timeout — the one case
        // where "please try again" is the correct guidance. No evidence the
        // last shortage still stands, so its flags go too.
        state.stockShortages = null;
        state.stockShortMessage = null;
        addMessage('assistant', t('submitNetworkError'));
        done(false, { code: 'network', message: t('submitNetworkError') });
      });
  }

  function submitReturn(onComplete) {
    var app = document.getElementById('mojito-returns-app');
    var config = getConfig(app);
    var sessionId = getSessionId();

    // Inline return button may or may not exist anymore (modal is primary UI now)
    var returnBtn = document.querySelector('.mojito-returns-chat__confirm-btn--return');
    if (returnBtn) { returnBtn.style.display = ''; returnBtn.disabled = true; returnBtn.textContent = 'Processing return...'; }

    var lineItems = state.selectedItems.map(function (itemId) {
      return { id: itemId, quantity: 1 };
    });

    function done(success) {
      if (typeof onComplete === 'function') onComplete(success);
    }

    apiFetch(config.apiEndpoint + '/api/create-return', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: sessionId,
        orderId: state.order.id,
        lineItems: lineItems,
        reason: state.activeReason || 'other',
        chatTranscript: state.messages
      })
    }, 30000)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.success) {
          renderSuccessModal('return');
          trackEvent('return_completed', { orderName: state.order.name, itemCount: lineItems.length });
          if (returnBtn) returnBtn.remove();
          done(true);
        } else if (data.code === 'unauthorized') {
          // Expired/invalid session token — don't loop on a dead token.
          addMessage('assistant', data.message || t('sessionExpired'));
          state.authToken = null;
          if (returnBtn) returnBtn.remove();
          done(true);
        } else {
          addMessage('assistant', data.message || 'Unable to create return. Please contact customer.service@mojitocollection.com.');
          if (returnBtn) { returnBtn.disabled = false; returnBtn.textContent = 'Confirm Return'; }
          done(false);
        }
      })
      .catch(function () {
        addMessage('assistant', 'Something went wrong. Please try again or contact customer.service@mojitocollection.com.');
        if (returnBtn) { returnBtn.disabled = false; returnBtn.textContent = 'Confirm Return'; }
        done(false);
      });
  }

  // 2026-10-13: opts.noScroll — the welcome message must not scroll the
  // order card away on mobile.
  function addMessage(role, text, opts) {
    state.messages.push({ role: role, text: text });

    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;

    var msgClass = 'mojito-returns-chat__message mojito-returns-chat__message--' + role + ' is-entering';
    var msgNode;

    if (role === 'assistant') {
      msgNode = el('div', { className: msgClass, htmlContent: formatMessage(text) });
    } else {
      msgNode = el('div', { className: msgClass }, text);
    }

    messagesEl.appendChild(msgNode);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        msgNode.classList.remove('is-entering');
      });
    });

    if (!(opts && opts.noScroll)) scrollToBottom();
  }

  /**
   * Render a rich exchange/return decision as a chat bubble instead of plain
   * text. Produces the same visual card style as the review modal rows so
   * customers see thumbnails + variant + price at-a-glance as they cycle
   * through items. Text-form equivalent is still pushed to state.messages so
   * the transcript + AI context stay complete.
   *
   *   kind = 'exchange' → { original, recommendation }  (shows old → new pair)
   *   kind = 'return'   → { original }                  (shows item + RETURNING badge)
   */
  function addDecisionCard(kind, payload) {
    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;
    payload = payload || {};
    var orig = payload.original || {};

    // Keep transcript + AI context in sync with what the customer sees.
    if (kind === 'exchange') {
      var rec = payload.recommendation || {};
      state.messages.push({
        role: 'assistant',
        text: '\u2713 Will exchange: ' + (orig.title || 'item') + ' \u2192 ' + (rec.title || 'replacement')
      });
    } else {
      state.messages.push({
        role: 'assistant',
        text: payload.storeCredit
          ? '\u2713 Will return for store credit: ' + (orig.title || 'this item')
          : '\u2713 Will return: ' + (orig.title || 'this item')
      });
    }

    function thumbFor(imageUrl, altText) {
      if (!imageUrl) return el('div', { className: 'mojito-returns-rcm__item-thumb mojito-returns-rcm__item-thumb--empty' });
      var src = imageUrl.indexOf('?') !== -1 ? imageUrl + '&width=160' : imageUrl + '?width=160';
      return el('img', { className: 'mojito-returns-rcm__item-thumb', src: src, alt: altText || '', loading: 'lazy' });
    }

    function sideFor(title, variantTitle, price, imageUrl, sideClass) {
      var infoChildren = [el('div', { className: 'mojito-returns-rcm__item-title' }, title || 'Item')];
      if (variantTitle) infoChildren.push(el('div', { className: 'mojito-returns-rcm__item-variant' }, variantTitle));
      if (price) infoChildren.push(el('div', { className: 'mojito-returns-rcm__item-price' }, '$' + price));
      return el('div', { className: 'mojito-returns-rcm__xside ' + (sideClass || '') }, [
        thumbFor(imageUrl, title),
        el('div', { className: 'mojito-returns-rcm__item-info' }, infoChildren)
      ]);
    }

    var card;
    if (kind === 'exchange') {
      var rec2 = payload.recommendation || {};
      card = el('div', { className: 'mojito-returns-rcm__xpair mojito-returns-chat__decision-card' }, [
        sideFor(orig.title, orig.variant_title, orig.price, orig.image, 'is-original'),
        el('div', { className: 'mojito-returns-rcm__xarrow', 'aria-hidden': 'true' }, '\u2192'),
        sideFor(rec2.title, null, rec2.price, rec2.image, 'is-replacement')
      ]);
    } else {
      var infoChildren = [el('div', { className: 'mojito-returns-rcm__item-title' }, orig.title || 'Item')];
      if (orig.variant_title) infoChildren.push(el('div', { className: 'mojito-returns-rcm__item-variant' }, orig.variant_title));
      if (orig.price) infoChildren.push(el('div', { className: 'mojito-returns-rcm__item-price' }, '$' + orig.price));
      card = el('div', { className: 'mojito-returns-rcm__item is-selected mojito-returns-chat__decision-card' }, [
        thumbFor(orig.image, orig.title),
        el('div', { className: 'mojito-returns-rcm__item-info' }, infoChildren),
        el('div', { className: 'mojito-returns-rcm__item-badge is-active' },
          payload.storeCredit ? t('storeCreditBadge') : t('returningBadge'))
      ]);
    }

    var wrap = el('div', {
      className: 'mojito-returns-chat__message mojito-returns-chat__message--assistant mojito-returns-chat__message--decision is-entering'
    }, [card]);
    messagesEl.appendChild(wrap);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        wrap.classList.remove('is-entering');
      });
    });

    scrollToBottom();
  }

  /**
   * Multi-item queue prompt — replaces the plain "Item N of M: …" text with
   * a structured bubble: "Item N of M" label, thumbnail+title+variant row,
   * then the reason question below. Same assistant-bubble visual style as
   * addMessage(), just with richer inner DOM. Transcript still gets the
   * text equivalent so AI context + session history stay complete.
   * 2026-10-13 — mixed flow: the verb, the intent pill and the question are
   * per item (intent = the line's plan) and localized; they were hard-coded
   * English. questionKey overrides the question (a re-queued line asks a
   * different one). opts.allowSwitch adds the step's "Return this one
   * instead" / "Exchange this one instead" link, live until a reason chip is
   * tapped. The bubble is stamped with its step; it is history and stays.
   */
  function addItemPromptMessage(item, stepNum, totalCount, intent, questionKey, opts) {
    opts = opts || {};
    var seq = state.stepSeq;
    var fs = !!item.final_sale;
    var qKey = questionKey || (intent === 'exchange' ? 'stepQExchange' : (fs ? 'stepQCredit' : 'stepQReturn'));
    var word = intent === 'exchange' ? 'EXCHANGE' : (fs ? 'STORE CREDIT' : 'RETURN');
    var variantSuffix = item.variant_title ? ' (' + item.variant_title + ')' : '';
    // The transcript line stays ENGLISH whatever the storefront language —
    // the worker's ITEM-BY-ITEM PLAN rule keys on "Item N of M (...):".
    var textForTranscript = 'Item ' + stepNum + ' of ' + totalCount + ' (' + word + '): '
      + (item.title || 'Item') + variantSuffix + '. ' + STRINGS.en[qKey];
    state.messages.push({ role: 'assistant', text: textForTranscript });

    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;

    var pillKey = intent === 'exchange' ? 'exchange' : (fs ? 'credit' : 'return');
    var labelEl = el('div', { className: 'mojito-returns-chat__item-prompt-head' }, [
      el('span', { className: 'mojito-returns-chat__item-prompt-label' },
        t('itemOfTotal').replace('{n}', String(stepNum)).replace('{m}', String(totalCount))),
      el('span', {
        className: 'mojito-returns-chat__item-prompt-intent mojito-returns-chat__item-prompt-intent--' + pillKey
      }, t(pillKey === 'exchange' ? 'exchangeAction' : pillKey === 'credit' ? 'intentCredit' : 'returnAction'))
    ]);

    var thumbEl;
    if (item.image) {
      var imgUrl = item.image.indexOf('?') !== -1
        ? item.image + '&width=160'
        : item.image + '?width=160';
      thumbEl = el('img', {
        className: 'mojito-returns-chat__item-prompt-thumb',
        src: imgUrl,
        alt: item.title || '',
        loading: 'lazy'
      });
    } else {
      thumbEl = el('div', {
        className: 'mojito-returns-chat__item-prompt-thumb mojito-returns-chat__item-prompt-thumb--empty'
      });
    }

    var infoChildren = [
      el('div', { className: 'mojito-returns-chat__item-prompt-title' }, item.title || 'Item')
    ];
    if (item.variant_title) {
      infoChildren.push(el('div', {
        className: 'mojito-returns-chat__item-prompt-variant'
      }, item.variant_title));
    }
    var infoEl = el('div', { className: 'mojito-returns-chat__item-prompt-info' }, infoChildren);

    var rowEl = el('div', { className: 'mojito-returns-chat__item-prompt-row' }, [thumbEl, infoEl]);

    var questionEl = el('div', {
      className: 'mojito-returns-chat__item-prompt-question'
    }, t(qKey));

    var children = [labelEl, rowEl, questionEl];
    if (opts.allowSwitch) {
      // Reads the line's plan at CLICK time — a switch already made in place
      // has flipped the label (switchCurrentItemIntent rewrites it).
      var switchBtn = el('button', {
        type: 'button',
        className: 'mojito-returns-chat__item-prompt-switch',
        onClick: function () {
          var other = (intentOf(item.id) || 'exchange') === 'exchange' ? 'return' : 'exchange';
          switchCurrentItemIntent(other, { seq: seq, itemId: item.id }, 'prompt');
        }
      }, t(intent === 'exchange' ? (fs ? 'switchToCreditLink' : 'switchToReturnLink') : 'switchToExchangeLink'));
      children.push(stamp(switchBtn, seq));
    }

    var wrap = el('div', {
      className: 'mojito-returns-chat__message mojito-returns-chat__message--assistant mojito-returns-chat__message--item-prompt is-entering'
    }, children);
    stamp(wrap, seq);
    messagesEl.appendChild(wrap);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        wrap.classList.remove('is-entering');
      });
    });

    scrollToBottom();
  }

  /* ========== Typing Indicator ========== */

  function showTypingIndicator() {
    state.chatLoading = true;
    // 2026-10-13 (fix round 1): who owns the dots — see kitDrop.
    state.typingGen++;

    var messagesEl = document.querySelector('.mojito-returns-chat__messages');
    if (!messagesEl) return;

    var typing = el('div', { className: 'mojito-returns-chat__typing' }, [
      el('span', { className: 'mojito-returns-chat__dot' }),
      el('span', { className: 'mojito-returns-chat__dot' }),
      el('span', { className: 'mojito-returns-chat__dot' })
    ]);
    messagesEl.appendChild(typing);
    scrollToBottom();
  }

  function hideTypingIndicator() {
    state.chatLoading = false;
    var typing = document.querySelector('.mojito-returns-chat__typing');
    if (typing) typing.remove();
  }

  /* ========== Freeform Chat Input ========== */

  /**
   * Detects whether the customer's message expresses a firm return/refund intent.
   * Returns:
   *   'immediate' — explicit refund-only phrase; switch flow right now
   *   'counted'   — general return/refund mention; count it (requires AI to have
   *                 pitched an exchange first so we give the exchange path a fair shot)
   *   null        — no return intent detected
   */
  /**
   * AI button labels that should open the return confirmation modal directly
   * instead of being sent as a chat turn. Case-insensitive match on any of:
   *   "Start Return", "Confirm Return", "Return Items", "Return Instead",
   *   "Proceed With Return", "Yes, Return", "Submit Return"
   */
  function isReturnConfirmLabel(label) {
    if (!label) return false;
    var normalized = String(label).trim().toLowerCase();
    return normalized === 'start return'
      || normalized === 'confirm return'
      || normalized === 'return items'
      || normalized === 'return this item'
      || normalized === 'return this one'
      || normalized === 'return instead'
      || normalized === 'proceed with return'
      || normalized === 'yes, return'
      || normalized === 'yes return'
      || normalized === 'yes, process return'
      || normalized === 'submit return';
  }

  /**
   * AI button labels that mean "give me store credit". These ride the
   * return flow (final-sale reimbursement IS store credit) but only when
   * the items in play are actually final-sale — for regular items the flow
   * has no store-credit outcome, so the label falls through to the AI
   * round-trip instead of silently submitting a cash-refund return.
   * Accent-folded so 'Crédito'/'Credito' both match.
   */
  function isStoreCreditLabel(label) {
    if (!label) return false;
    var normalized = String(label).trim().toLowerCase();
    if (typeof normalized.normalize === 'function') {
      normalized = normalized.normalize('NFD').replace(/[̀-ͯ]/g, '');
    }
    return normalized === 'store credit'
      || normalized === 'get store credit'
      || normalized === 'yes, store credit'
      || normalized === 'yes store credit'
      || normalized === 'confirm store credit'
      || normalized === 'credito de tienda'
      || normalized === 'obtener credito'
      || normalized === 'confirmar credito'
      || normalized === 'si, credito'
      || normalized === 'si credito';
  }

  /**
   * True when the store-credit label click can be honored structurally:
   * queue mode → the CURRENT item is final-sale; single mode → at least one
   * item is selected and every selected item is final-sale.
   */
  function storeCreditContextApplies() {
    if (isInItemQueueMode()) {
      var curId = state.currentExchangeItemId
        || (state.exchangeQueue && state.exchangeQueue[0]);
      return !!curId && isFinalSaleItem(curId);
    }
    return state.selectedItems.length > 0 && state.selectedItems.every(isFinalSaleItem);
  }

  /**
   * Detect whether a freeform chat message is asking for filtered variants:
   *   'size'  — "show me other sizes", "try a different size"
   *   'color' — "other colors", "different color", "another color"
   *   'style' — "different style", "other styles", "short sleeve", "long sleeve"
   *   null    — not a filter request; use default recommendation flow.
   * This routes the fetch to the right endpoint + variant filter instead of
   * blindly running smart-recommendations every freeform turn.
   */
  function detectExchangeFilterIntent(text) {
    if (!text) return null;
    var lower = text.toLowerCase();
    // Style / sleeve / product-type requests (cross-product)
    if (/\b(other|different|more|another|new)\s+styles?\b/.test(lower) ||
        /\bdifferent\s+product/.test(lower) ||
        /\b(short|long)\s+sleeves?\b/.test(lower) ||
        /\bsleeveless\b/.test(lower) ||
        // Fabric-scoped style asks ("show linen styles", "see cotton options")
        /\b(linen|cotton|blend|silk)\s+(styles?|options?|shirts?|ones?)\b/.test(lower)) {
      return 'style';
    }
    // Color requests (same product, different color)
    if (/\b(other|different|more|another|new)\s+colou?rs?\b/.test(lower) ||
        /\bin\s+(another|different)\s+colou?r\b/.test(lower)) {
      return 'color';
    }
    // Size requests (same product, different size)
    if (/\b(other|different|more|another)\s+sizes?\b/.test(lower) ||
        /\btry\s+(a|another)\s+size\b/.test(lower)) {
      return 'size';
    }
    // Generic "show me more" / "any other" / "what else" — drop strict filters
    // (preferredSize, maxPrice) so the AI sees the broader pool.
    if (/\b(any\s+other(s|\s+ones?)?|anything\s+else|what\s+else|show\s+me\s+more|see\s+more|more\s+options?)\b/.test(lower)) {
      return 'broader';
    }
    return null;
  }

  /* ========== Named-style detection ========== */

  // Connective tissue + attribute words. MUST stay in sync with the worker's
  // STYLE_STOPWORDS (index.js) — the worker scores the match, this list only
  // decides whether the turn is worth a catalog search at all. Catalog
  // vocabulary is deliberately absent: "two/four", "long/short", "big/tall"
  // and every fabric word are load-bearing parts of real product titles.
  var STYLE_STOPWORDS_FE = {};
  ('i im id ive me my mine we us you your it its this that these those there here the a an and or '
    + 'of in on at to for with without from by as so is are was were be been am do does did doing '
    + 'have has had can could would will shall should may want wants wanted like liked love need '
    + 'needs needed prefer prefers get got take send show see look looking give go went try trying '
    + 'swap switch change exchange exchanged return returned order ordered buy bought please thanks '
    + 'thank hi hello hey ok okay yes yeah yep no nope not but if then than instead rather actually '
    + 'maybe really just still also too very much another other others different same similar '
    + 'something anything everything else any some all more most new size sizes color colors colour '
    + 'colours style styles option options version available availability stock instock help about '
    + 'what which who how why when where '
    // Spanish/Portuguese function words — es-US is ~15.6% of web orders and
    // product titles are English, so "quiero la <English title> en Salmon" is
    // the normal shape. The worker AND-s every surviving token into its
    // catalog search, and one unindexed word ("quiero") returns zero rows.
    // Garment nouns (camisa, guayabera, lino) stay OUT — they identify product.
    + 'quiero quisiera queria busco buscando necesito necesita tengo tienen tiene hay puedo puede '
    + 'podria dame quero preciso gostaria tem ter la el los las lo un una unos unas uma um de del '
    + 'en con sin por para que y o e este esta esto estos estas ese esa eso mismo misma otro otra '
    + 'otros otras mas pero si no cambiar cambio devolver devolucion talla tallas colores estilo '
    + 'estilos gracias hola porfavor').split(' ').forEach(function (w) { if (w) STYLE_STOPWORDS_FE[w] = true; });

  // A word that only shows up when someone is describing an actual garment.
  // Without this gate, any three-word complaint ("fabric feels scratchy")
  // would burn a catalog search every turn.
  var STYLE_NOUNS_FE = {};
  ('guayabera guayaberas guayamisa chacabana camisa shirt shirts blouse blouses dress dresses '
    + 'pants trouser trousers shorts polo polos blazer suit set sets sweater jacket tee top tops '
    + 'skirt jumpsuit romper linen cotton blend silk rayon poplin seersseucker pocket pockets '
    + 'pleat pleats pleated embroidery embroidered sleeve sleeves collar cuff cuffs trim')
    .split(' ').forEach(function (w) { STYLE_NOUNS_FE[w] = true; });

  /**
   * Is this message naming a specific product? Returns the identifying words
   * as a search string, or null.
   *
   * Permissive on purpose — the WORKER scores the result against the real
   * catalog and reports no-match honestly, and a no-match falls straight
   * through to the normal recommendation flow. A false positive here costs
   * one extra ~600ms lookup; a false negative is the live bug this fixes
   * (customer named a style, the assistant answered "not in stock" about a
   * shirt with units on the shelf).
   */
  function detectNamedStyleIntent(text, minNouns) {
    if (!text) return null;
    var toks = String(text).toLowerCase().replace(/[‘’']/g, '').split(/[^a-z0-9]+/);
    var all = [];
    for (var i = 0; i < toks.length; i++) { if (toks[i]) all.push(toks[i]); }
    // A pasted paragraph is a story, not a product name.
    if (all.length > 24) return null;
    var content = all.filter(function (t) { return !STYLE_STOPWORDS_FE[t]; });
    if (content.length < 3) return null;
    var nouns = content.filter(function (t) { return STYLE_NOUNS_FE[t]; });
    if (nouns.length < (minNouns || 2)) return null;
    return content.slice(0, 16).join(' ');
  }

  /**
   * Parse Shopify-style variant titles ("XX-Large / Banana") into parts.
   * Returns { size, color } — assumes Shopify's conventional Size-first order.
   */
  function parseVariantTitleParts(variantTitle) {
    if (!variantTitle) return { size: null, color: null };
    var parts = variantTitle.split('/').map(function (s) { return s.trim(); });
    // Titles on this store are "COLOR / SIZE" (options order Color, Size) —
    // never assume position: detect the size part by pattern and treat the
    // other part as the color. Positional parsing swapped the two, which
    // killed same-color prioritization in every variant filter below.
    var sizePattern = /^(XXS|XS|S|M|L|XL|XXL|XXXL|[2-8]XL?|X-?Small|XX-?Small|Small|Medium|Large|X-?Large|XX-?Large|\d{1,2}[WL]?)$/i;
    var size = null;
    var color = null;
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i]) continue;
      if (size === null && sizePattern.test(parts[i])) {
        size = parts[i];
      } else if (color === null) {
        color = parts[i];
      }
    }
    return { size: size, color: color };
  }

  /**
   * Build the AVAILABLE INVENTORY block fed to the AI from an in-stock variant
   * list. When currentColor is given, same-color options are ranked first
   * (callers that already proximity-sorted pass null to keep their order).
   * The list is capped so the worker's 3000-char exchangeContext limit never
   * silently truncates it — before this cap, tail-of-list colorways (RED sits
   * near the end of a 75-variant grid) simply vanished from the AI's world.
   * Returns { text, list } — callers must build cards from the SAME sorted
   * list so the cards match the AI's prose.
   */
  var MAX_CTX_VARIANTS = 40;
  // Char budget for the variant lines alone: headers embed the customer's
  // free-typed complaint (≤500 chars) and the "(+N more...)" note adds ~150,
  // and the worker hard-caps the whole exchangeContext at 3000 — 2100 + 150
  // + 500 + headers always fits, so the worker cap never eats the note that
  // keeps the AI honest about unlisted options.
  var MAX_CTX_CHARS = 2100;
  function buildInventoryCtx(inStock, currentColor) {
    var sorted = inStock;
    if (currentColor) {
      sorted = inStock.slice().sort(function (a, b) {
        return (a.color === currentColor ? 0 : 1) - (b.color === currentColor ? 0 : 1);
      });
    }
    var lines = [];
    var chars = 0;
    for (var i = 0; i < sorted.length && lines.length < MAX_CTX_VARIANTS; i++) {
      var v = sorted[i];
      var line = '- ' + v.title + ' — $' + v.price + ' — ' + v.quantity + ' in stock [id:' + v.id + ']';
      if (chars + line.length + 1 > MAX_CTX_CHARS) break;
      lines.push(line);
      chars += line.length + 1;
    }
    var text = lines.join('\n');
    if (sorted.length > lines.length) {
      text += '\n(+' + (sorted.length - lines.length) + ' more in-stock options exist but are not listed — if nothing above fits, offer to show more instead of saying nothing else is available)';
    }
    return { text: text, list: sorted };
  }

  /**
   * Canonical size key for cross-format comparison. "X-Large", "XL", "x large"
   * all normalize to "xl" so we can sort + match regardless of source format.
   */
  function canonicalSize(s) {
    if (!s) return '';
    var t = String(s).toLowerCase().replace(/[-_\s]/g, '');
    // XXL and 2XL are the SAME physical size under different labels (this
    // store's variants say "2XL"; customers type "2x", "xxl", "2xl"...) —
    // everything canonicalizes onto the NXL scale so they all match.
    var map = {
      'xxs': 'xxs',
      'xs': 'xs', 'xsmall': 'xs', 'extrasmall': 'xs',
      's': 's', 'small': 's',
      'm': 'm', 'medium': 'm',
      'l': 'l', 'large': 'l',
      'xl': 'xl', 'xlarge': 'xl', 'extralarge': 'xl', '1x': 'xl',
      'xxl': '2xl', 'xxlarge': '2xl', '2xl': '2xl', '2x': '2xl',
      'xxxl': '3xl', 'xxxlarge': '3xl', '3xl': '3xl', '3x': '3xl',
      '4xl': '4xl', '4x': '4xl',
      '5xl': '5xl', '5x': '5xl',
      '6xl': '6xl', '6x': '6xl',
      '7xl': '7xl', '7x': '7xl',
      '8xl': '8xl', '8x': '8xl'
    };
    return map[t] || t;
  }

  // Sizes from smallest → largest (canonical keys only — canonicalSize maps
  // xxl/xxxl onto 2xl/3xl before lookup). Sizes outside this list (numeric,
  // etc.) return index -1 and are treated as "unknown relative position."
  var SIZE_ORDER = ['xxs', 'xs', 's', 'm', 'l', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl'];

  /**
   * Detect whether the customer's message is a size-specific request:
   *   { specific: 'X-Large' } — they named a size ("XL", "Large", "Medium")
   *   { direction: 'smaller' | 'larger' } — they gave a direction hint
   *   null — no size signal
   * Checked BEFORE the generic exchange-filter intent, because size is more
   * specific and determines proximity-sort behavior.
   */
  function detectSizeDirectionIntent(text) {
    if (!text) return null;
    var lower = text.toLowerCase();

    // Direction words first — they override specific sizes ("size down please")
    if (/\b(smaller|size\s+down|go\s+down\s+a\s+size|a\s+size\s+(smaller|down|less)|one\s+size\s+down|tighter\s+fit)\b/.test(lower)) {
      return { direction: 'smaller' };
    }
    if (/\b(larger|bigger|size\s+up|go\s+up\s+a\s+size|a\s+size\s+(larger|up|bigger)|one\s+size\s+up|roomier|looser\s+fit)\b/.test(lower)) {
      return { direction: 'larger' };
    }

    // Specific named sizes — word-boundary + plausible context
    var nameMatch = lower.match(/\b(xxs|xs|xxxl|xxl|xl|small|medium|large|x-?large|xx-?large|xxx-?large|extra[\s-]?large|[2-8]xl)\b/);
    if (nameMatch) return { specific: nameMatch[1] };

    // Bare "2x".."8x" — "is there a 3x", "show me 3x in similar styles" is how
    // customers actually ask. Requiring a size word immediately BEFORE the
    // number (the old rule) meant both of those phrasings parsed as NO size
    // request at all, so a customer asking for 3X was answered with 2XL.
    // Bare positions can still mean "twice", so reject only those repetition
    // contexts. Runs AFTER the named-size match so "2x of the medium" → medium.
    var bareNx = lower.match(/\b([2-8])\s?x\b/);
    if (bareNx) {
      var at = bareNx.index;
      var before = lower.slice(Math.max(0, at - 26), at);
      var after = lower.slice(at + bareNx[0].length, at + bareNx[0].length + 20);
      var isRepetition = /\b(wore|worn|wear|wearing|wash|washed|washes|used|ordered|bought|returned|exchanged|tried)\s+(it\s+|them\s+|this\s+)?$/.test(before)
        || /^\s*(times?|a\s+day|a\s+week|a\s+month|a\s+year|per\b|daily|weekly|monthly|already)/.test(after);
      if (!isRepetition) return { specific: bareNx[1] + 'x' };
    }

    // Bare single-letter sizes need a size context word nearby to avoid
    // false positives ("I'll" matching "l", "I am" matching "m").
    if (/\b(in|size|want|need|try|get|have)\s+(s|m|l)\b/.test(lower)) {
      var m = lower.match(/\b(in|size|want|need|try|get|have)\s+(s|m|l)\b/);
      return { specific: m[2] };
    }

    return null;
  }

  /**
   * Filter variants based on a size request returned by detectSizeDirectionIntent.
   * Excludes the customer's current variant always. Sorts results by proximity
   * to the current size (closest adjacent first) so the AI recommends Large
   * before Small when the customer asked for "smaller" starting from XL.
   * Same-color matches are ranked above different-color matches.
   */
  function filterVariantsBySizeRequest(variants, currentItem, sizeRequest) {
    var currentVariantId = currentItem && currentItem.variant_id ? String(currentItem.variant_id) : '';
    var parts = parseVariantTitleParts(currentItem && currentItem.variant_title);
    var currentSize = parts.size;
    var currentColor = parts.color;
    var currentIdx = SIZE_ORDER.indexOf(canonicalSize(currentSize));
    var base = function (v) { return v.inStock && String(v.id) !== currentVariantId; };
    var sameColorRank = function (v) { return v.color && v.color === currentColor ? 0 : 1; };
    var matching;

    if (sizeRequest.specific) {
      var targetKey = canonicalSize(sizeRequest.specific);
      matching = variants.filter(function (v) {
        return base(v) && canonicalSize(v.size) === targetKey;
      });
      matching.sort(function (a, b) { return sameColorRank(a) - sameColorRank(b); });
    } else if (sizeRequest.direction === 'smaller') {
      matching = variants.filter(function (v) {
        var idx = SIZE_ORDER.indexOf(canonicalSize(v.size));
        return base(v) && idx !== -1 && currentIdx !== -1 && idx < currentIdx;
      });
      // Closest-smaller first (higher index ≈ closer to current when we're going down)
      matching.sort(function (a, b) {
        var ai = SIZE_ORDER.indexOf(canonicalSize(a.size));
        var bi = SIZE_ORDER.indexOf(canonicalSize(b.size));
        if (ai !== bi) return bi - ai;
        return sameColorRank(a) - sameColorRank(b);
      });
    } else if (sizeRequest.direction === 'larger') {
      matching = variants.filter(function (v) {
        var idx = SIZE_ORDER.indexOf(canonicalSize(v.size));
        return base(v) && idx !== -1 && currentIdx !== -1 && idx > currentIdx;
      });
      matching.sort(function (a, b) {
        var ai = SIZE_ORDER.indexOf(canonicalSize(a.size));
        var bi = SIZE_ORDER.indexOf(canonicalSize(b.size));
        if (ai !== bi) return ai - bi;
        return sameColorRank(a) - sameColorRank(b);
      });
    } else {
      matching = variants.filter(base);
    }
    return matching;
  }

  /**
   * Format the size request for AI context so it understands WHAT was asked
   * and WHICH variant is the right first recommendation.
   */
  function formatSizeRequestForAI(sizeRequest, currentSize) {
    if (!sizeRequest) return '';
    if (sizeRequest.specific) {
      return 'CUSTOMER SIZE REQUEST: ' + sizeRequest.specific + ' (specific size; recommend only variants in that size).';
    }
    if (sizeRequest.direction === 'smaller') {
      return 'CUSTOMER SIZE REQUEST: one size smaller than ' + (currentSize || 'their current size')
        + '. The FIRST item in AVAILABLE INVENTORY is the closest smaller size — recommend it first, then offer others only if they decline.';
    }
    if (sizeRequest.direction === 'larger') {
      return 'CUSTOMER SIZE REQUEST: one size larger than ' + (currentSize || 'their current size')
        + '. The FIRST item in AVAILABLE INVENTORY is the closest larger size — recommend it first, then offer others only if they decline.';
    }
    return '';
  }

  /**
   * Rule-201 negatives for the reason-chip ctx (2XL incident, 27AUG2026): the
   * prompt only lets the AI say a size is unavailable when a context line
   * starts "NO <SIZE> IN STOCK", and no code produced those lines before —
   * the model could not honestly say "2XL is sold out" even though the full
   * grid proving it was already fetched. Emits one line per size in the
   * complaint's direction with zero sellable stock in ANY color of the style.
   */
  function buildDirectionalStockNotes(variants, currentItem, direction) {
    var parts = parseVariantTitleParts(currentItem && currentItem.variant_title);
    var curIdx = SIZE_ORDER.indexOf(canonicalSize(parts.size));
    if (curIdx === -1) return '';
    var byKey = {};
    (variants || []).forEach(function (v) {
      var key = canonicalSize(v.size);
      var idx = SIZE_ORDER.indexOf(key);
      if (idx === -1) return;
      if (!byKey[key]) byKey[key] = { label: String(v.size || key).toUpperCase(), idx: idx, anyInStock: false };
      if (v.inStock) byKey[key].anyInStock = true;
    });
    var notes = Object.keys(byKey)
      .map(function (k) { return byKey[k]; })
      .filter(function (e) {
        var inDirection = direction === 'larger' ? e.idx > curIdx : e.idx < curIdx;
        return inDirection && !e.anyInStock;
      })
      .sort(function (a, b) { return a.idx - b.idx; })
      .map(function (e) { return 'NO ' + e.label + ' IN STOCK in any color of this style.'; });
    return notes.length ? notes.join('\n') + '\n' : '';
  }

  /**
   * Cross-style size search shared by the typed-message router and the
   * too-small / too-large reason chips (2XL incident: the chips carry a
   * DIRECTION but no typed size token, so they previously could never reach
   * this search — the AI was left to fabricate a same-style bigger size).
   * Asks the worker for same-category styles stocking `sizeLabel`, builds the
   * grounded ctx, and sends the chat turn itself — callers just `return` it.
   */
  // 2026-10-13 (fix round 1): tok — the caller's kitToken, so a search that
  // lands after its step (or session) was left writes and sends nothing.
  function runCrossStyleSizeSearch(app, config, selectedItem, userText, sizeLabel, notePrefix, tok) {
    var refParts = parseVariantTitleParts(selectedItem && selectedItem.variant_title);
    return apiFetch(config.apiEndpoint + '/api/smart-recommendations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        productId: selectedItem.product_id,
        reason: state.activeReason || 'wrong-size',
        requestedSize: sizeLabel,
        preferredColor: refParts.color || '',
        // Fit beats budget: once a size is the target, don't let the ±10%
        // price band silently drop the styles that carry it. The cards
        // still show the real "+$X more" difference.
        maxPrice: 9999
      })
    }, 20000)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (tok && !kitLive(tok)) { kitDrop(tok); return; }
        var recs = (data && data.recommendations) || [];
        var ctx = (notePrefix || '')
          + 'CUSTOMER CURRENT VARIANT: ' + ((selectedItem && selectedItem.variant_title) || 'unknown') + '\n';
        ctx += 'CUSTOMER REQUESTED SIZE: ' + sizeLabel
          + ' — this is the size they need. Never offer a different size as a substitute for it.\n';
        if (data && data.success && recs.length > 0) {
          state.lastRecommendations = recs;
          state.originalItemPrice = parseFloat(selectedItem.price);
          state.originalItemDiscountRate = itemDiscountRate(selectedItem);
          state.originalItemProductId = selectedItem.product_id || null;
          state.pendingRecommendationsRender = true;
          if (state.originalItemDiscountRate > 0) {
            ctx += 'CUSTOMER PAID: $' + selectedItem.discounted_price + ' per unit after their original-order discount.';
            ctx += ' The same discount carries over to any exchange item, so NEVER quote price differences from the catalog prices below — the cards already show the correct amounts.\n';
          }
          ctx += 'OTHER STYLES WITH ' + sizeLabel + ' IN STOCK RIGHT NOW (every line below IS ' + sizeLabel + ' and buyable):\n';
          ctx += recs.map(function (r, i) {
            var qty = (typeof r.quantity === 'number' && r.quantity > 0) ? r.quantity + ' in stock' : 'in stock';
            return (i + 1) + '. ' + r.title + ' — ' + r.variant_title + ' — $' + r.price
              + ' — ' + String(r.fabric || '').toUpperCase() + ' — ' + qty + ' [id:' + r.variant_id + ']';
          }).join('\n');
          ctx += '\nLead with these, say the size ' + sizeLabel + ' out loud, and emit a [[CARD:id]] for every item you name.';
        } else {
          // Honest empty result — and clear the previous cards so a
          // [[CARDS]] marker can't replay the OLD size underneath a
          // reply about this one.
          state.lastRecommendations = null;
          state.pendingRecommendationsRender = false;
          // The positive facts go on their OWN lines: the worker's stock-claim
          // validator skips any line starting "NO ... IN STOCK" wholesale, so
          // sizes riding that line would be disowned and honest mentions of
          // them false-corrected.
          ctx += 'NO ' + sizeLabel + ' IN STOCK among the '
            + ((data && data.products_searched) || 0) + ' same-category styles just searched.';
          var others = (data && data.other_sizes_in_stock) || [];
          if (others.length > 0) {
            ctx += '\nSizes that ARE in stock across those styles: ' + others.join(', ') + '.';
          }
          ctx += '\nSay plainly that you could not find ' + sizeLabel + ' in this category right now.'
            + ' Do NOT claim the store carries none at all — this search covered one category only.'
            + ' Offer to have the team check the full catalog at customer.service@mojitocollection.com and end with [[BTN:Email Support]].';
        }
        hideTypingIndicator();
        sendChatMessage(app, userText, null, ctx);
      })
      .catch(function () {
        if (tok && !kitLive(tok)) { kitDrop(tok); return; }
        hideTypingIndicator();
        // Never let a failed lookup become a confident denial.
        sendChatMessage(app, userText, null,
          'SIZE SEARCH FAILED for ' + sizeLabel + '. State NO availability for that size — say you could not check it just now, and offer [[BTN:Email Support]].');
      });
  }

  /**
   * Filter a product's variants based on an intent derived from the customer's
   * complaint/request:
   *   'size'  — keep current color, swap size
   *   'color' — keep current size, swap color
   *   null    — any other in-stock variant
   * Always excludes the current variant. Falls back to a looser filter if the
   * strict match returns empty (so we never return zero cards when options exist).
   */
  function filterVariantsByIntent(variants, currentItem, intent) {
    var currentVariantId = currentItem && currentItem.variant_id ? String(currentItem.variant_id) : '';
    var parts = parseVariantTitleParts(currentItem && currentItem.variant_title);
    var currentSize = parts.size;
    var currentColor = parts.color;
    var base = function (v) { return v.inStock && String(v.id) !== currentVariantId; };

    if (intent === 'size' && currentColor) {
      var sameColor = variants.filter(function (v) {
        return base(v) && v.color && v.color === currentColor && v.size !== currentSize;
      });
      if (sameColor.length > 0) return sameColor;
      return variants.filter(function (v) { return base(v) && v.size !== currentSize; });
    }
    if (intent === 'color' && currentSize) {
      var sameSize = variants.filter(function (v) {
        return base(v) && v.size && v.size === currentSize && v.color !== currentColor;
      });
      if (sameSize.length > 0) return sameSize;
      return variants.filter(function (v) { return base(v) && v.color !== currentColor; });
    }
    return variants.filter(base);
  }

  /**
   * Detect when the customer signals they're open to paying more than the
   * default ±10% price window — phrases like "can pay the difference",
   * "show me pricier options", "more expensive is fine", etc. When detected
   * we flip state.priceOpen so subsequent rec fetches drop the maxPrice cap.
   * Sticky once set — customers rarely un-say "I can pay more".
   */
  function detectPriceOpenIntent(text) {
    if (!text) return false;
    var lower = text.toLowerCase();
    var patterns = [
      'pay the difference', 'pay more', 'can pay more', "don't mind paying",
      'do not mind paying', "doesn't matter the price", 'price is fine',
      'price is ok', 'price is okay', 'pricier', 'more expensive',
      'higher price', 'spend more', 'willing to spend', 'budget is flexible',
      'cost more is fine', 'extra cost is fine', 'any price', 'price no issue',
      'price no object', 'money is not an issue', 'money is not the issue'
    ];
    for (var i = 0; i < patterns.length; i++) {
      if (lower.indexOf(patterns[i]) !== -1) return true;
    }
    return false;
  }

  /**
   * Detect whether a freeform chat message is a fit / material / style
   * complaint that would make sense to resolve via exchange. Used to
   * pre-fetch inventory during RETURN flow when the customer's message
   * signals they might pivot to exchange — otherwise the AI has no
   * AVAILABLE INVENTORY and either stalls ("let me check") or invents
   * options. This is intentionally broader than detectSizeDirectionIntent
   * and detectExchangeFilterIntent so it catches plain complaints like
   * "too tight" / "the material is scratchy" / "wrong fabric".
   */
  /**
   * Explicit exchange-request detector — used ONLY to EXIT question mode.
   * Deliberately narrow: bare size vocabulary ("does it run small?") must
   * stay in Q&A, but a stated wish to exchange/swap must leave it so the
   * inventory machinery re-arms (without this, the AI gets asked to show
   * products it was never given). Action cards remain the canonical entry.
   */
  function detectExplicitExchangeIntent(text) {
    if (!text) return false;
    var lower = text.toLowerCase();
    if (/\b(can|could|may)\s+i\s+(make\s+(an?\s+)?)?(exchange|swap)\b/.test(lower)) return true;
    if (/\b(want|like|need|going)\s+to\s+(exchange|swap)\b/.test(lower)) return true;
    if (/\b(make|start|do)\s+(an?\s+)?(exchange|swap)\b/.test(lower)) return true;
    if (/\bexchange\s+(it|this|one|for|style|styles)\b/.test(lower)) return true;
    if (/^\s*(start\s+)?exchange\b/.test(lower)) return true;
    if (/\bswap\s+(it|this|for)\b/.test(lower)) return true;
    if (/\bneed\s+a\s+different\s+(one|size|colou?r|style|fit)\b/.test(lower)) return true;
    return false;
  }

  function detectExchangeWorthyComplaint(text) {
    if (!text) return false;
    var lower = text.toLowerCase();
    // Fit complaints
    if (/\b(too\s+)?(tight|loose|snug|small|big|large|short|long|boxy|baggy|oversiz(ed)?)\b/.test(lower)) return true;
    if (/\b(doesn'?t\s+fit|does\s+not\s+fit|poor\s+fit|bad\s+fit|wrong\s+fit|off\s+fit|ill[-\s]?fitting)\b/.test(lower)) return true;
    if (/\b(runs?\s+(small|big|tight|loose|large))\b/.test(lower)) return true;
    // Material / fabric / feel complaints
    if (/\b(scratchy|itchy|stiff|thin|sheer|see[-\s]?through|cheap\s+feel|rough\s+(feel|texture)|uncomfortable)\b/.test(lower)) return true;
    if (/\b(different\s+(fabric|material)|wrong\s+(fabric|material)|not\s+(linen|cotton|silk|polyester))\b/.test(lower)) return true;
    // Look / appearance complaints
    if (/\b(looks?\s+(different|weird|wrong|cheap|off)|not\s+what\s+(i\s+)?expected|photo\s+(was\s+)?(off|different))\b/.test(lower)) return true;
    return false;
  }

  /**
   * 2026-10-13 — mixed flow (fix round 1): the Spanish "refund only, no
   * exchange" escape. It used to be ten bare substrings, and "cambio" also
   * means CHANGE — so ordinary exchange talk committed the item as a return:
   *   "no quiero cambio de color, solo una talla más grande"
   *   "no quiero cambiarla por otro color"
   *   "¿es mejor devolverlo o cambiarlo?"
   *   "no quiero mi dinero de vuelta, quiero otra talla"
   * None of those may match. So the WHOLE message must be one of the phrases
   * below (accents folded, trailing punctuation and a trailing "gracias" /
   * "por favor" ignored), and a question never counts.
   */
  var ES_REFUND_ONLY = new RegExp('^(?:'
    + 'solo (?:quiero )?(?:el |un )?reembolso'
    + '|prefiero (?:el |un )?reembolso'
    + '|quiero (?:mi dinero|un reembolso|el reembolso)(?: de vuelta)?'
    + '|no quiero (?:un |el )?cambio'
    + '|no quiero cambiarl[oa]'
    + '|(?:prefiero|mejor) devolverl[oa]'
    + '|(?:prefiero|mejor) (?:lo|la) devuelvo'
    + '|prefiero devolver'
    + '|solo devolver(?:lo|la)?'
    + ')$');

  function isSpanishRefundOnly(text) {
    var raw = String(text || '');
    if (/[?\u00BF]/.test(raw)) return false;
    var s = raw.toLowerCase();
    try {
      s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (e) { /* no String#normalize — accented input simply won't match */ }
    s = s.replace(/\s+/g, ' ')
      .replace(/^[\s\u00A1]+/, '')
      .replace(/[\s.!,;:\u00A1]+$/, '')
      .replace(/[\s,]+(?:gracias|por favor)$/, '')
      .replace(/[\s.!,;:]+$/, '');
    return ES_REFUND_ONLY.test(s);
  }

  function detectReturnIntent(text) {
    if (!text) return null;
    var lower = text.toLowerCase();
    // Explicit refund-only phrases → switch immediately, no waiting.
    var explicit = [
      'refund only', 'just refund', 'only refund', 'just a refund',
      "don't want an exchange", 'do not want an exchange',
      "don't want exchange", 'no exchange',
      'just want my money back', 'want my money back',
      'give me my money back', 'money back please',
      'just the refund', 'cash refund',
      'no thanks just return', 'return this instead', 'skip the exchange'
    ];
    for (var i = 0; i < explicit.length; i++) {
      if (lower.indexOf(explicit[i]) !== -1) return 'immediate';
    }
    // 2026-10-13: Spanish customers who type had no way out of an exchange
    // step. A WHOLE message only, 'immediate' only — never Spanish 'counted'
    // matching ("¿me dan reembolso de la diferencia?" must not misfire).
    if (isSpanishRefundOnly(text)) return 'immediate';
    // General return/refund mention → count, but only if AI has pitched an
    // exchange first. Pre-pitch mentions are exploratory and shouldn't count.
    if (!state.hasPitchedExchange) return null;
    if (RETURN_MENTION_RE.test(text)) return 'counted';
    return null;
  }

  // detectReturnIntent's 'counted' phrases (appendIntent uses its own copy
  // without the bare "refund", APPEND_RETURN_RE). Broader coverage:
  //   return it / return this / return these / return that
  //   just return / want (to) return / i'll return / let me return
  //   process (the|my) return / do (the|my) return / confirm return
  var RETURN_MENTION_RE = /\b(refund|return\s+(it|this|these|them|that|one)|just\s+return|want\s+(to\s+)?return|i'?ll\s+return|let\s+me\s+return|process\s+(the\s+|my\s+)?return|do\s+(the\s+|my\s+)?return|confirm\s+return|get\s+a\s+refund)\b/i;

  // Fix round 2: "I don't want a refund" / "I don't want to exchange it" name
  // the opposite of what they ask — neither may route the waiting line.
  var NEGATED_RETURN_RE = /\b(?:don'?t|don\u2019t|do\s+not|not|never)\s+(?:want|need|like)\s+(?:to\s+)?(?:an?\s+|the\s+|my\s+)?(?:refund|return)|\bno\s+refund/i;
  var NEGATED_EXCHANGE_RE = /\b(?:don'?t|don\u2019t|do\s+not|not|never)\s+(?:want|need|like)\s+(?:to\s+)?(?:an?\s+|the\s+)?(?:exchange|swap)/i;

  // Fix round 3: in the append state a QUESTION never routes the waiting
  // line — "How long does a refund take?", "Does the Cuban run small?" are
  // answered by the plain turn, whose order context already says which line
  // waits. "Can I also return / exchange …" is the one question shape that
  // IS the request.
  var APPEND_QUESTION_RE = /^\s*(does|do|is|are|how|what|which|will|would|did|can|could|should|when|where|why)\b|\?\s*$/i;
  var APPEND_CAN_I_RE = /^\s*(can|could|may)\s+i\s+(also\s+|just\s+)?(return|exchange|swap|get\s+a\s+refund)\b/i;
  // RETURN_MENTION_RE without its bare "refund": a refund has to be asked for
  // ("when will my refund arrive" is not a return request).
  var APPEND_RETURN_RE = /\b(return\s+(it|this|these|them|that|one)|just\s+return|(want|like|need)\s+(to\s+)?return|i'?ll\s+return|let\s+me\s+return|process\s+(the\s+|my\s+)?return|do\s+(the\s+|my\s+)?return|confirm\s+return|(get|want|like|need)\s+a\s+refund|refund\s+(it|this|that|them|one))\b/i;

  /**
   * 2026-10-13 — mixed flow (fix round 2): what a typed message in the
   * append state (decisions on file, a newly ticked line waiting) asks for
   * that line — 'return', 'exchange', or null for a plain AI turn (which
   * buildOrderContext tells the line is waiting). Only 'immediate' used to
   * route, so "I want to return the Cuban shirt too" became a turn that
   * could not act — and the AI, not told, could answer as if it were added.
   *   1. a refund-only phrase ('immediate')            → return
   *   2. "can I (also) return / exchange …"             → that one
   *   3. any other question                             → plain turn
   *   4. explicit exchange words XOR return words       → that one
   *      (both named: ask — never guess)
   *   5. a size DIRECTION or an explicit filter ask      → exchange
   * Fix round 3: routing a question, a bare "refund", or a bare size or
   * complaint word ("long", "small" — maybe about the decided line) started
   * a step the customer never asked for and swallowed their question.
   */
  function appendIntent(text) {
    if (detectReturnIntent(text) === 'immediate') return 'return';
    var canI = text.match(APPEND_CAN_I_RE);
    if (canI) return /^(exchange|swap)$/i.test(canI[3]) ? 'exchange' : 'return';
    if (APPEND_QUESTION_RE.test(text)) return null;
    var exWords = detectExplicitExchangeIntent(text) && !NEGATED_EXCHANGE_RE.test(text);
    var rtWords = APPEND_RETURN_RE.test(text) && !NEGATED_RETURN_RE.test(text);
    if (exWords || rtWords) return exWords === rtWords ? null : (exWords ? 'exchange' : 'return');
    var size = detectSizeDirectionIntent(text);
    if ((size && size.direction) || detectExchangeFilterIntent(text)) return 'exchange';
    return null;
  }

  /**
   * Apply return intent signal. Returns true if the flow was switched to
   * 'return' (caller should short-circuit any exchange-specific context fetch).
   *
   * Threshold logic:
   *   - activeAction === 'return' and user signals return → 1 hit is enough.
   *     They already chose Return; they're just pushing through the AI's
   *     exchange pitch. Don't make them say it twice.
   *   - activeAction === 'exchange' and user signals return → 2 hits required,
   *     so a casual "I might return" doesn't accidentally flip the flow.
   *
   * On switch we open the return modal directly instead of doing another AI
   * round-trip — faster and unambiguous.
   */
  function applyReturnIntent(app, signal, userText) {
    // Queue mode: scope the return decision to the CURRENT item, don't
    // open the global modal. Customer can still change other items later.
    function triggerInQueue() {
      // 2026-10-13 — mixed flow: on an exchange step with no reason yet,
      // "return this instead" flips the step to the return chips — it no
      // longer files the line with reason 'other'. After a reason it commits
      // as before.
      if (walkthroughActive() && state.currentExchangeItemId != null && !state.activeReason
          && (intentOf(state.currentExchangeItemId) || 'exchange') === 'exchange') {
        switchCurrentItemIntent('return', { seq: state.stepSeq, itemId: state.currentExchangeItemId }, 'typed');
        return true;
      }
      return markCurrentItemAsReturn({ via: 'typed' });
    }
    function triggerGlobal() {
      // Fix round 1: a newly ticked line waiting after earlier decisions
      // takes the Return card's append path — never the modal, which would
      // re-decide the line on file or file the new one under its reason.
      if (appendPending()) { handleAction('return'); return; }
      state.activeAction = 'return';
      state.returnIntentCount = 2;
      addMessage('assistant', 'Got it — let\'s process the return.');
      openReturnConfirmModal();
    }
    if (signal === 'immediate') {
      if (isInItemQueueMode()) return triggerInQueue();
      triggerGlobal();
      return true;
    }
    if (signal === 'counted') {
      state.returnIntentCount = (state.returnIntentCount || 0) + 1;
      // 2026-10-13: the CURRENT item's plan sets the bar (the counter itself
      // is reset per walk-through step).
      var threshold = currentIntent() === 'return' ? 1 : 2;
      if (state.returnIntentCount >= threshold) {
        if (isInItemQueueMode()) return triggerInQueue();
        triggerGlobal();
        return true;
      }
    }
    return false;
  }

  function handleChatSend(app, overrideText) {
    // 2026-10-13: no typed turn may start in the gap while a step change
    // waits for the previous step's reply. Fix round 1: nor while a reply
    // is still streaming (turnInFlight; chatLoading is false by then). The
    // typed text stays in the input, so nothing is lost.
    if (turnInFlight() || state.stepChanging) return;

    var text;
    if (overrideText) {
      // AI button click or programmatic send — don't read the input field
      text = String(overrideText).trim();
    } else {
      var input = document.querySelector('.mojito-returns-chat__input');
      if (!input) return;
      text = input.value.trim();
      if (!text) return;
      input.value = '';
    }

    if (!text) return;
    addMessage('user', text);

    // 2026-10-13 — mixed flow: while the plan bubble is open, typed text gets
    // a plain AI turn — no kit (selectedItems[0] means nothing yet) and no
    // return-intent routing, so a message cannot hijack the plan. The
    // stream end moves the bubble back to the bottom.
    if (state.planDraft) {
      sendChatMessage(app, text, null);
      return;
    }

    // 2026-10-13 — mixed flow (fix round 1): decisions exist and a newly
    // ticked line waits for Exchange or Return. A typed refund ask is that
    // line's Return (the append walk-through, which asks its own reason);
    // anything else gets a plain AI turn — no kit and no return-intent
    // routing, which would re-decide the line already on file or file the
    // new one under its reason. The old pitch kit is dropped, as when the
    // plan opens: it belongs to the decided line.
    // Fix round 2: a return ask ('counted' phrases too) or an exchange ask
    // the code can recognise takes that card's append path (appendIntent);
    // the plain turn's order context now says which line is still waiting.
    if (appendPending()) {
      var appendRoute = appendIntent(text);
      if (appendRoute) {
        handleAction(appendRoute);
        return;
      }
      state.lastExchangeContext = null;
      state.lastRecommendations = null;
      state.currentProposedProductId = null;
      state.pendingRecommendationsRender = false;
      state.showReturnConfirm = false;
      sendChatMessage(app, text, null);
      return;
    }

    // Reason-detail capture (schema v2): when the active reason is a
    // catch-all ('other-reason' / 'fit-shape'), the FIRST thing the customer
    // types afterwards is almost always the actual reason ("sleeves too
    // stiff", "need it for an event next week"). Keep it, keyed to the item
    // it belongs to — submitAllDecisions appends it to the submitted reason
    // string, which the worker records verbatim as the Shopify return note.
    // Typed messages only (overrideText = AI button clicks, not the customer
    // explaining themselves).
    if (!overrideText &&
        (state.activeReason === 'other-reason' || state.activeReason === 'fit-shape')) {
      var detailItem = getExchangeContextItem();
      if (detailItem && detailItem.id) {
        state.reasonDetails = state.reasonDetails || {};
        var detailKey = String(detailItem.id);
        if (!state.reasonDetails[detailKey]) {
          state.reasonDetails[detailKey] = text.replace(/\s+/g, ' ').substring(0, 120);
        }
      }
    }

    // Intent detection: if user firmly wants a return, switch flow before
    // burning tokens on exchange-context fetching.
    var intent = detectReturnIntent(text);
    if (intent && applyReturnIntent(app, intent, text)) {
      return;
    }

    // Sticky flag: once the customer says they're open to paying more,
    // all subsequent rec fetches drop the ±10% price cap.
    if (detectPriceOpenIntent(text)) {
      state.priceOpen = true;
    }

    // Fetch inventory context whenever an exchange is being pitched OR the
    // customer's message signals an exchange-worthy pivot. Three triggers:
    //   1. activeAction === 'exchange'            — customer chose exchange
    //   2. hasPitchedExchange                     — AI already proposed one
    //   3. message carries size/filter/complaint  — pivot-from-return signal
    //
    // Trigger (3) covers the case where a RETURN-flow customer expresses a
    // complaint that should convert to an exchange ("its little tight, need
    // one size smaller", "too scratchy", "doesn't fit"). Without it, the AI
    // receives no AVAILABLE INVENTORY and either stalls or hallucinates.
    var config = getConfig(app);
    var hasExchangeSignal = !!detectSizeDirectionIntent(text)
      || !!detectExchangeFilterIntent(text)
      || detectExchangeWorthyComplaint(text);
    // Question mode: free-typed questions ("does it run small?") must NOT
    // trigger the exchange kit — same rule as the topic chips, or the
    // original ask-a-question-get-a-sales-pitch bug returns on the typed
    // path. BUT a genuine pivot must EXIT question mode, or the AI is left
    // conversing about an exchange it has no inventory for (live incident:
    // it leaked the "Inventory not yet loaded" placeholder to a customer).
    // Exit on: an explicit exchange request ("can I make an exchange...",
    // "need a different one"), or a complaint STATED as a fact ("it's too
    // tight") — question-form complaints ("does it run small?") stay Q&A.
    var inQuestionMode = state.activeAction === 'question';
    if (inQuestionMode) {
      var isInterrogative = /^\s*(does|do|is|are|how|what|which|will|would|did)\b/i.test(text) || /\?\s*$/.test(text);
      if (detectExplicitExchangeIntent(text)
          || (!isInterrogative && detectExchangeWorthyComplaint(text))) {
        state.activeAction = 'exchange';
        inQuestionMode = false;
        // Fix round 2: a single path starts here — pin its line.
        pinSingleLine(getExchangeContextItem());
      }
    }
    // 2026-10-13: per item — a walk-through return step does not fetch the
    // kit on every message just because the walk-through began as Exchange.
    // Fix round 1: nor because its one exchange offer has been made — a
    // "no thanks" or a question there re-armed the kit, and the stream-end
    // fallback re-rendered up to 4 cards under the AI's no-cards reply. Only
    // an explicit exchange signal (size / filter / complaint) fetches again.
    var onReturnStep = walkthroughActive() && state.currentExchangeItemId != null
      && (intentOf(state.currentExchangeItemId) || 'exchange') === 'return';
    var pitchRearms = state.hasPitchedExchange && !onReturnStep;
    if (!inQuestionMode && (currentIntent() === 'exchange' || pitchRearms || hasExchangeSignal)) {
      // Queue-aware: multi-item exchanges operate on the current item
      var selectedItem = getExchangeContextItem();
      // Fix round 2: whatever branch below runs, it fetches for this line —
      // pin it (a single path started by typing never pinned).
      pinSingleLine(selectedItem);
      if (selectedItem && selectedItem.product_id) {
        var preferredSize = parseVariantSize(selectedItem.variant_title);
        // Open budget: use 9999 as "no cap" when the customer has signaled
        // willingness to spend more. Worker treats any high number as unlimited.
        var maxPrice = state.priceOpen
          ? 9999
          : Math.ceil(parseFloat(selectedItem.price) * 1.10 * 100) / 100;

        // Intent-aware routing priority:
        //   1. Specific size / size-direction ("I need L", "smaller") — most precise
        //   2. Generic filter ("other colors", "other sizes", "other styles")
        //   3. Broader ("any other ones", "show me more") — drop strict filters
        //   4. Default smart-rec → fallback chain
        var sizeRequest = detectSizeDirectionIntent(text);
        var filterIntent = detectExchangeFilterIntent(text);
        // "similar styles", "other styles", "anything else" — the customer is
        // asking to look BEYOND the style they ordered.
        var wantsOtherStyles = /\b(other|others|different|similar|another|anything\s+else|something\s+else|else|styles?)\b/i.test(text);
        // The size they NAMED, in the store's own spelling ("3x" → "3XL").
        // This is the one thing we must never silently substitute.
        var requestedSizeLabel = (sizeRequest && sizeRequest.specific)
          ? canonicalSize(sizeRequest.specific).toUpperCase()
          : null;
        if (requestedSizeLabel) state.lastRequestedSize = requestedSizeLabel;

        // 'broader' request — temporarily drop size AND price ceiling so the
        // AI can see the whole candidate pool. Don't mutate state.priceOpen
        // (sticky flag) — just relax this turn's fetch.
        if (filterIntent === 'broader') {
          preferredSize = null;
          maxPrice = 9999;
        }

        // For color/size filters, fetch the PROPOSED product's inventory
        // (the one the AI most recently recommended via [[CARD:id]]) rather
        // than the customer's original order item. "See other colors" after
        // AI proposed Linen should show Linen colors, not Cotton Blend.
        var proposedRef = null;
        if (state.currentProposedProductId && state.lastRecommendations) {
          proposedRef = state.lastRecommendations.find(function (r) {
            return String(r.product_id) === String(state.currentProposedProductId);
          });
        }
        var targetProductId = proposedRef ? proposedRef.product_id : selectedItem.product_id;
        var targetRefItem = proposedRef || selectedItem;

        showTypingIndicator();
        // 2026-10-13 (fix round 1): every fetch below is bound to the step
        // and session this message was typed in. Each .then/.catch tests
        // kitLive(tok) first; a stale one only lets go of its typing dots
        // (kitDrop) — it writes no recommendation state and sends nothing.
        var tok = kitToken();

        // Helper: fetch same-product inventory, optionally filtered by intent
        function fetchSameProductInventory(productId, intent, refItem) {
          return apiFetch(config.apiEndpoint + '/api/check-inventory', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ productId: productId })
          }, 15000)
            .then(function (res) { return res.json(); })
            .then(function (data) {
              var ctx = '';
              // Stale: no state writes here; the caller drops the send.
              if (!kitLive(tok)) return ctx;
              if (data.success && data.variants) {
                var inv = buildInventoryCtx(
                  filterVariantsByIntent(data.variants, refItem, intent || null),
                  parseVariantTitleParts(refItem && refItem.variant_title).color
                );
                var inStock = inv.list;
                var refTitle = (refItem && refItem.variant_title) || (refItem && refItem.title) || '';
                ctx = 'CUSTOMER CURRENT VARIANT: ' + refTitle + ' (complaint: ' + text + ')\n';
                ctx += 'SAME PRODUCT INVENTORY (' + (data.product ? data.product.title : (refItem && refItem.title) || '') + '):\n';
                ctx += inv.text;
                if (inStock.length === 0) ctx += 'No other variants currently in stock.';
                if (inStock.length > 0 && data.product) {
                  state.lastRecommendations = inStock.slice(0, MAX_CTX_VARIANTS).map(function (v) {
                    return {
                      product_id: data.product.id,
                      variant_id: v.id,
                      title: data.product.title + ' — ' + v.title,
                      handle: data.product.handle || '',
                      price: v.price,
                      image: v.image || (refItem && refItem.image) || (data.product.images && data.product.images[0] && data.product.images[0].src) || null,
                      fabric: null
                    };
                  });
                  state.originalItemPrice = parseFloat(selectedItem.price);
                  state.originalItemDiscountRate = itemDiscountRate(selectedItem);
                  state.originalItemProductId = selectedItem.product_id || null;
                  state.pendingRecommendationsRender = true;
                }
              }
              return ctx;
            });
        }

        // Cross-style search for a size the customer NAMED. The worker filters
        // every candidate by THAT size instead of the ordered one, across the
        // whole same-category shelf — the only way Big & Tall styles (whose
        // size ladders START at 3XL) can ever surface for a 3X request.
        // Sends the chat turn itself, so callers just `return` it.
        var sizeSearchRan = false;
        function crossStyleSizeSearch(sizeLabel, notePrefix) {
          sizeSearchRan = true;
          // Shared with the too-small/too-large reason chips — see
          // runCrossStyleSizeSearch (2XL-incident extraction, 27AUG2026).
          return runCrossStyleSizeSearch(app, config, selectedItem, text, sizeLabel, notePrefix, tok);
        }

        // When the named-style lookup comes back empty, hand the turn back to
        // the branch that WOULD have run without it — carrying the verdict as
        // a note. A customer who named both a style and a size ("is there a 3x
        // in the wide pleated") must still get the cross-style size search;
        // dropping them into the generic flow would silently undo the 28JUL
        // fix and re-answer a 3X request with their ordered size.
        function styleMissFallback(note) {
          if (requestedSizeLabel && !sizeSearchRan) return crossStyleSizeSearch(requestedSizeLabel, note);
          return defaultRecommendationFlow(note);
        }

        // Resolve a style the customer named in prose against the real catalog.
        // Sends the chat turn itself, so callers just `return` it.
        function namedStyleSearch(query) {
          return apiFetch(config.apiEndpoint + '/api/smart-recommendations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              productId: selectedItem.product_id,
              reason: state.activeReason || 'different-style',
              styleQuery: query,
              // The size they named beats the size they ordered; either way
              // it only marks which card to feature, never filters the style
              // out. Fit beats budget here for the same reason it does in the
              // size search — they asked for THIS shirt.
              requestedSize: requestedSizeLabel || preferredSize || '',
              excludeVariantId: selectedItem.variant_id || '',
              maxPrice: 9999
            })
          }, 20000)
            .then(function (res) { return res.json(); })
            .then(function (data) {
              if (!kitLive(tok)) { kitDrop(tok); return; }
              var ctx = 'CUSTOMER NAMED A STYLE: "' + text + '"\n';

              if (data && data.success && data.matched && data.ambiguous) {
                // Several real titles fit. Picking one for them is the same
                // silent substitution this whole path exists to prevent.
                state.lastRecommendations = null;
                state.pendingRecommendationsRender = false;
                ctx += 'SEVERAL STYLES MATCH THAT NAME — do NOT pick one for them, ask which they mean:\n';
                ctx += (data.candidates || []).map(function (c, i) {
                  return (i + 1) + '. ' + c.title;
                }).join('\n');
                ctx += '\nName them back and ask which one. Do not claim anything about stock yet.';
                hideTypingIndicator();
                sendChatMessage(app, text, null, ctx);
                return;
              }

              if (data && data.success && data.matched && data.recommendations && data.recommendations.length > 0) {
                var m = data.match || {};
                var wantColor = data.requested_color || '';
                var wantSize = data.requested_size || '';
                state.lastRecommendations = data.recommendations;
                state.originalItemPrice = parseFloat(selectedItem.price);
                state.originalItemDiscountRate = itemDiscountRate(selectedItem);
                state.originalItemProductId = selectedItem.product_id || null;
                state.pendingRecommendationsRender = true;

                // Below a near-perfect coverage this is the closest title, not
                // provably the one they meant — say so rather than asserting.
                if (typeof m.coverage === 'number' && m.coverage < 0.85) {
                  ctx += 'CLOSEST CATALOG MATCH: ' + m.title
                    + ' — confirm this is the style they meant before treating it as settled.\n';
                } else {
                  ctx += 'NAMED STYLE FOUND: ' + m.title + ' — this IS the style they named, it exists and is buyable.\n';
                }
                if (wantColor) ctx += 'THEY ASKED FOR COLOR: ' + wantColor + '\n';
                if (wantSize) ctx += 'THEY ASKED FOR SIZE: ' + wantSize + '\n';

                if (state.originalItemDiscountRate > 0) {
                  ctx += 'CUSTOMER PAID: $' + selectedItem.discounted_price + ' per unit after their original-order discount.';
                  ctx += ' The same discount carries over to any exchange item, so NEVER quote price differences from the catalog prices below — the cards already show the correct amounts.\n';
                }

                ctx += 'IN STOCK RIGHT NOW ON THIS STYLE (every line below IS buyable):\n';
                ctx += data.recommendations.map(function (r, i) {
                  var qty = (typeof r.quantity === 'number' && r.quantity > 0) ? r.quantity + ' in stock' : 'in stock';
                  return (i + 1) + '. ' + r.title + ' — ' + r.variant_title + ' — $' + r.price
                    + ' — ' + String(r.fabric || '').toUpperCase() + ' — ' + qty + ' [id:' + r.variant_id + ']';
                }).join('\n');

                // Say the miss out loud when they named a combination that
                // isn't there — the honest half of finding the style.
                var exact = data.recommendations.filter(function (r) {
                  return (!wantColor || r.color_match) && (!wantSize || r.size_match);
                });
                if (exact.length === 0 && (wantColor || wantSize)) {
                  ctx += '\nTHE EXACT COMBINATION THEY NAMED ('
                    + [wantColor, wantSize].filter(Boolean).join(' / ')
                    + ') IS NOT IN THE LIST ABOVE — say that plainly, then offer what IS listed.';
                  if (data.sizes_in_stock && data.sizes_in_stock.length) {
                    ctx += ' Sizes this style has in stock: ' + data.sizes_in_stock.join(', ') + '.';
                  }
                }
                ctx += '\nLead with this style by name, say the color and size out loud, and emit a [[CARD:id]] for every variant you name.';
                hideTypingIndicator();
                sendChatMessage(app, text, null, ctx);
                return;
              }

              // Found the style, but nothing on it is buyable. This is the ONE
              // case where "sold out" is a true statement about a named style —
              // and it has to be said separately, or it inherits the "couldn't
              // find that name" copy below and tells the customer the opposite
              // of what happened.
              if (data && data.success && data.matched && !data.ambiguous && data.match) {
                state.lastRecommendations = null;
                state.pendingRecommendationsRender = false;
                return styleMissFallback(ctx
                  + 'NAMED STYLE FOUND BUT SOLD OUT: ' + data.match.title
                  + ' exists and you DID check it — every colorway is currently out of stock.'
                  + ' Say that specifically about this style (not about the store), then offer the alternatives below.\n\n');
              }

              // Not found / too generic / search failed → the normal flow still
              // runs, but the customer hears about their actual request first.
              // NEVER a stock claim: a failed NAME lookup is not a stock check.
              var note;
              if (data && data.search_failed) {
                note = ctx + 'STYLE LOOKUP FAILED — you could not check that name just now. Say so, do NOT say it is unavailable, and offer [[BTN:Email Support]].\n\n';
              } else if (data && data.reason === 'too_generic') {
                // They described a CATEGORY ("a long sleeve linen shirt"), not
                // one product. "I couldn't find that name" would be wrong —
                // many styles fit, which is what the list below is for.
                note = ctx + 'THAT DESCRIBES A CATEGORY, NOT ONE STYLE — several products fit it.'
                  + ' Do NOT say you could not find it and do NOT claim anything is unavailable;'
                  + ' show the matching alternatives below and let them narrow it down.\n\n';
              } else {
                note = ctx + 'NAMED STYLE NOT FOUND: the catalog search could not identify a product by that name ('
                  + ((data && data.products_searched) || 0) + ' titles checked).'
                  + ' Say you could not find a style by that name and ask them to confirm the exact name or paste the product link.'
                  + ' You may NOT say it is out of stock, sold out, discontinued, or unavailable — you failed to find the NAME, you did not check its stock.'
                  + ' After acknowledging that, you may offer the alternatives below.\n\n';
              }
              return styleMissFallback(note);
            })
            .catch(function () {
              if (!kitLive(tok)) { kitDrop(tok); return; }
              // A network failure must not become a confident denial either.
              return styleMissFallback(
                'CUSTOMER NAMED A STYLE: "' + text + '"\n'
                + 'STYLE LOOKUP FAILED — say you could not look that name up just now and do NOT claim it is unavailable.\n\n');
            });
        }

        // The customer typed a product NAME. Nothing else in this router reads
        // one: every other branch is anchored on the product they ORDERED, and
        // its candidate pool (3 collections × 20 + 20 same-type) structurally
        // cannot contain a style they picked out of the wider catalog. Live
        // incident: "i want the Cotton Blend Guayabera Long Sleeve Two Pocket
        // Wide Pleated in Salmon" → "not in stock right now" while Salmon/Small
        // had 2 units. Runs FIRST because a named style is the most specific
        // thing a customer can ask for — "the Wide Pleated in 2XL" must search
        // that STYLE for 2XL, not their own style; a no-match falls through to
        // the normal flow with an honest note rather than a dead end.
        // The noun bar rises when they also named a SIZE: "a 2xl linen shirt"
        // is a size request wearing two catalog words, and routing it here
        // would cost it the cross-style size search. A real product title
        // carries 3+ ("cotton blend guayabera long sleeve two pocket wide
        // pleated" has five).
        var styleAsk = detectNamedStyleIntent(text, requestedSizeLabel ? 3 : 2);
        if (styleAsk) {
          namedStyleSearch(styleAsk);
          return;
        }

        // Size-direction / specific-size → same-product inventory, filtered by
        // proximity to the requested size, with explicit AI guidance on which
        // variant to recommend first.
        if (sizeRequest) {
          // They named a size AND asked for other/similar styles — skip the
          // same-product lookup entirely and search the category for it.
          // filterIntent guards the overlap: "another COLOR in 2XL" also
          // contains "another", but it is a same-product question — answering
          // it with a different style would be a non-sequitur.
          if (requestedSizeLabel && wantsOtherStyles
            && filterIntent !== 'color' && filterIntent !== 'size') {
            crossStyleSizeSearch(requestedSizeLabel);
            return;
          }
          apiFetch(config.apiEndpoint + '/api/check-inventory', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ productId: targetProductId })
          }, 15000)
            .then(function (res) { return res.json(); })
            .then(function (data) {
              if (!kitLive(tok)) { kitDrop(tok); return; }
              var ctx = '';
              if (data.success && data.variants) {
                // Already proximity-sorted (same-color ranked first within each
                // size) — pass null so buildInventoryCtx caps without resorting.
                var inv = buildInventoryCtx(
                  filterVariantsBySizeRequest(data.variants, targetRefItem, sizeRequest),
                  null
                );
                var inStock = inv.list;
                var refParts = parseVariantTitleParts(targetRefItem && targetRefItem.variant_title);
                var sizeHint = formatSizeRequestForAI(sizeRequest, refParts.size);
                ctx = 'CUSTOMER CURRENT VARIANT: ' + ((targetRefItem && targetRefItem.variant_title) || 'unknown') + '\n';
                if (sizeHint) ctx += sizeHint + '\n';
                ctx += 'AVAILABLE INVENTORY (sorted by proximity to request, best match first):\n';
                ctx += inv.text;
                // Their own style doesn't have the size they named — that is
                // the moment to search other styles for it, NOT to pivot them
                // to a size that won't fit.
                if (inStock.length === 0 && requestedSizeLabel) {
                  return crossStyleSizeSearch(requestedSizeLabel);
                }
                if (inStock.length === 0) {
                  ctx += 'No matching variants in stock.';
                }
                if (inStock.length > 0 && data.product) {
                  state.lastRecommendations = inStock.slice(0, MAX_CTX_VARIANTS).map(function (v) {
                    return {
                      product_id: data.product.id,
                      variant_id: v.id,
                      title: data.product.title + ' — ' + v.title,
                      handle: data.product.handle || '',
                      price: v.price,
                      image: (v.image) || (targetRefItem && targetRefItem.image) || (data.product.images && data.product.images[0] && data.product.images[0].src) || null,
                      fabric: null
                    };
                  });
                  state.originalItemPrice = parseFloat(selectedItem.price);
                  state.originalItemDiscountRate = itemDiscountRate(selectedItem);
                  state.originalItemProductId = selectedItem.product_id || null;
                  state.pendingRecommendationsRender = true;
                }
              } else if (requestedSizeLabel) {
                // Same-product lookup came back unusable — still owe the
                // customer a real answer about the size they named.
                return crossStyleSizeSearch(requestedSizeLabel);
              }
              hideTypingIndicator();
              sendChatMessage(app, text, null, ctx || null);
            })
            .catch(function () {
              if (!kitLive(tok)) { kitDrop(tok); return; }
              // A named size still deserves a real search. Passing null ctx
              // here would resend state.lastExchangeContext — the stale list
              // in the OLD size, which is exactly how the same wrong cards
              // got replayed turn after turn.
              if (requestedSizeLabel && !sizeSearchRan) {
                return crossStyleSizeSearch(requestedSizeLabel);
              }
              hideTypingIndicator();
              sendChatMessage(app, text, null);
            });
          return;
        }

        // Color / size intents → skip smart-rec, go straight to same-product inventory
        if (filterIntent === 'color' || filterIntent === 'size') {
          fetchSameProductInventory(targetProductId, filterIntent, targetRefItem)
            .then(function (ctx) {
              if (!kitLive(tok)) { kitDrop(tok); return; }
              hideTypingIndicator();
              sendChatMessage(app, text, null, ctx || null);
            })
            .catch(function () {
              if (!kitLive(tok)) { kitDrop(tok); return; }
              hideTypingIndicator();
              sendChatMessage(app, text, null);
            });
          return;
        }

        // Style / default → smart-rec first, fall back to same-product.
        // `notePrefix` carries a named-style search verdict when one ran and
        // came back empty, so the customer hears "I couldn't find that name"
        // BEFORE the alternatives instead of a bare pivot.
        function defaultRecommendationFlow(notePrefix) {
          return apiFetch(config.apiEndpoint + '/api/smart-recommendations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              productId: selectedItem.product_id,
              reason: state.activeReason || 'different-style',
              preferredSize: preferredSize,
              maxPrice: maxPrice
            })
          }, 20000)
            .then(function (res) { return res.json(); })
            .then(function (data) {
              if (!kitLive(tok)) { kitDrop(tok); return; }
              if (data.success && data.recommendations && data.recommendations.length > 0) {
                hideTypingIndicator();
                state.lastRecommendations = data.recommendations;
                state.originalItemPrice = parseFloat(selectedItem.price);
                state.originalItemDiscountRate = itemDiscountRate(selectedItem);
                state.originalItemProductId = selectedItem.product_id || null;
                state.pendingRecommendationsRender = true;
                var ctx = notePrefix || '';
                ctx += 'ORIGINAL ITEM: ' + (data.original ? data.original.title : selectedItem.title);
                ctx += ' — $' + (data.original ? data.original.price : selectedItem.price);
                // Phase 2.4: keep AI prose consistent with the discount-scaled
                // card math (see the reason-flow ctx for the same rule).
                if (state.originalItemDiscountRate > 0) {
                  ctx += '\nCUSTOMER PAID: $' + selectedItem.discounted_price + ' per unit after their original-order discount.';
                  ctx += ' The same discount carries over to any exchange item, so NEVER quote price differences from the catalog prices above — the cards already show the correct amounts.';
                }
                ctx += '\nCustomer message: ' + text;
                ctx += '\n\nAVAILABLE ALTERNATIVES (in stock):\n';
                ctx += data.recommendations.map(function (r, i) {
                  var sizeNote = r.preferred_size_in_stock ? '✓ ' + preferredSize + ' in stock' : 'Sizes: ' + r.available_sizes.join(', ');
                  return (i + 1) + '. ' + r.title + ' — $' + r.price + ' — ' + r.fabric.toUpperCase() + ' — ' + sizeNote + ' [id:' + r.variant_id + ']';
                }).join('\n');
                sendChatMessage(app, text, null, ctx);
              } else {
                // No smart-rec results — fall back to same-product inventory
                return fetchSameProductInventory(selectedItem.product_id, null, selectedItem).then(function (ctx) {
                  if (!kitLive(tok)) { kitDrop(tok); return; }
                  hideTypingIndicator();
                  sendChatMessage(app, text, null, (notePrefix || '') + (ctx || '') || null);
                });
              }
            })
            .catch(function () {
              if (!kitLive(tok)) { kitDrop(tok); return; }
              return fetchSameProductInventory(selectedItem.product_id, null, selectedItem).then(function (ctx) {
                if (!kitLive(tok)) { kitDrop(tok); return; }
                hideTypingIndicator();
                sendChatMessage(app, text, null, (notePrefix || '') + (ctx || '') || null);
              }).catch(function () {
                if (!kitLive(tok)) { kitDrop(tok); return; }
                hideTypingIndicator();
                sendChatMessage(app, text, null, notePrefix || null);
              });
            });
        }

        defaultRecommendationFlow(null);
        return;
      }
    }

    sendChatMessage(app, text, null);
  }

  /* ========== Auto-scroll ========== */

  function scrollToBottom() {
    var body = document.querySelector('.mojito-returns-chat__body');
    if (body) {
      setTimeout(function () {
        body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
      }, 50);
    }
  }

  /* ========== Init ========== */

  function init() {
    var app = document.getElementById('mojito-returns-app');
    if (!app) return;
    trackEvent('portal_opened');
    renderLookupScreen(app);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();